import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { 
  Loader2, 
  ChevronRight,
  Building2,
  Link2,
  Bell,
  Users2,
  RefreshCw,
  Database,
  CheckCircle,
  XCircle,
  Instagram,
  Facebook,
  Check,
  Copy,
  Code,
  Globe,
  Zap,
  ShieldCheck,
  Pencil,
  Trash2,
  Plus,
  Calendar
} from 'lucide-react';
import { Select } from '../components/ui/Select';
import '../context/AuthContext.css';  // Loading animasyonunu import edelim
import BrandDetails from './settings/BrandDetails';
import ApiConnections from './settings/ApiConnections';
import Notifications from './settings/Notifications';
import UserPermissions from './settings/UserPermissions';
import MonthlyTargets from './settings/MonthlyTargets';
import DataSyncLogs from './DataSyncLogs';
import DataHealth from './DataHealth';
import RealtimeModal from '../components/RealtimeModal';
import { Modal, Form, Spin, Checkbox, TimePicker, Switch, Select as AntSelect } from 'antd';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
import { useSyncStore } from '../stores/syncStore';
import { Navigate } from 'react-router-dom';

// Settings sections configuration
const settingsSections = [
  {
    id: 'brand-details',
    name: 'Brand Details',
    description: 'Manage your brand information and preferences',
    icon: Building2,
    component: BrandDetails
  },
  {
    id: 'api-connections',
    name: 'Platform Connections',
    description: 'Connect and manage your advertising platform integrations',
    icon: Link2,
    component: ApiConnections
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Configure your notification preferences and alerts',
    icon: Bell,
    component: Notifications
  },
  {
    id: 'user-permissions',
    name: 'User Permissions',
    description: 'Manage user roles and access permissions',
    icon: Users2,
    component: UserPermissions
  },
  {
    id: 'monthly-targets',
    name: 'Monthly Targets',
    description: 'Set monthly budgets, revenue goals, and conversion targets per channel',
    icon: Calendar,
    component: MonthlyTargets
  },
  {
    id: 'data-health',
    name: 'Data Health',
    description: 'Monitor data completeness and backfill missing data',
    icon: ShieldCheck,
    component: DataHealth
  },
  {
    id: 'data-sync-logs',
    name: 'Data Sync Logs',
    description: 'View history of data synchronization with connected platforms',
    icon: Database,
    component: DataSyncLogs
  },
  {
    id: 'data-cleanup',
    name: 'Data Cleanup',
    description: 'Clean up data for specific dates across all platforms',
    icon: RefreshCw,
    component: null // Custom component will be handled inline
  }
];

/**
 * FINANCE / GBP sayfa kısıtı olan MARKA rolleridir ve doğrudan
 * `UserBrandAccess.role`da durur — kullanıcının diğer markalarını etkilemez.
 * (Eskiden `User.accountType`ta durup tüm hesabı kilitliyordu, 27 Ağu 2026'da
 * marka bazına alındı.) Backend karşılığı: routes/brands.js → HESAP_TIPI_ROLLERI.
 */
const rolSecimDegeri = (u) => u?.role;

export default function Settings() {
  // =========================================================
  // Data Cleanup UI feature flags (temporary)
  // =========================================================
  const SHOW_META_CREATIVE_TOOLS = false; // temporarily disabled per request
  const SHOW_GA4_ECOM_MONTHLY_BACKFILL = true; // enabled

  // Brands that support the "MARKETER" role
  const MARKETER_BRAND_IDS = [
    '44e35dfe-0186-495d-a0cc-db5e7e459ab7',
    'dfcf54dd-b00d-434a-be1e-27b6484848eb',
    '7fc8e68d-f2ba-4101-95f7-acece386ab8f',
    'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
  ];

  const [connections, setConnections] = useState({});
  // Multi-account: list of accounts per platform (e.g. { GOOGLE_ADS: [...], META_ADS: [...] })
  const MULTI_ACCOUNT_PLATFORMS = ['GOOGLE_ADS', 'META_ADS'];
  const MULTI_ACCOUNT_BRAND_IDS = ['3ddb6c9f-19f6-4fa7-bf40-20f7f38ebaa6', '062be473-3b2b-4bad-a2e3-a31995e02d57', '44e35dfe-0186-495d-a0cc-db5e7e459ab7', '675cf508-b160-4767-b75e-43be853e3e59'];
  // Markaya yalnız bazı platformlarda çoklu hesap (listede yoksa MULTI_ACCOUNT_PLATFORMS'un hepsi)
  const MULTI_ACCOUNT_BRAND_PLATFORMS = { '675cf508-b160-4767-b75e-43be853e3e59': ['META_ADS'] }; // temizmama, 4 Kas 2026
  const isMultiAccountFor = (brandId, platform) =>
    MULTI_ACCOUNT_PLATFORMS.includes(platform) && MULTI_ACCOUNT_BRAND_IDS.includes(brandId)
    && (!MULTI_ACCOUNT_BRAND_PLATFORMS[brandId] || MULTI_ACCOUNT_BRAND_PLATFORMS[brandId].includes(platform));
  const [multiAccounts, setMultiAccounts] = useState({});
  const [multiAccountsLoading, setMultiAccountsLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [currency, setCurrency] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduleForm] = Form.useForm();
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dataSyncLogs, setDataSyncLogs] = useState([]);
  const [dataSyncLogsLoading, setDataSyncLogsLoading] = useState(true);
  
  // User Permissions State
  const [brandUsers, setBrandUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const [inviteUserForm, setInviteUserForm] = useState({ email: '', role: 'VIEWER' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userToModify, setUserToModify] = useState(null); // For both editing and removing
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);

  const { user: currentUser, selectedBrand } = useAuth();

  // Disconnect modal state'leri
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [platformToDisconnect, setPlatformToDisconnect] = useState(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  // Criteo account picker: a consent can cover several Criteo advertisers, so the brand
  // must be told which one it is instead of us binding whichever came back first.
  const [criteoPickerOpen, setCriteoPickerOpen] = useState(false);
  const [criteoAdvertisers, setCriteoAdvertisers] = useState([]);
  const [criteoChosenAdvertiser, setCriteoChosenAdvertiser] = useState(null);
  const [criteoPickerLoading, setCriteoPickerLoading] = useState(false);
  const [criteoSaving, setCriteoSaving] = useState(false);
  
  // Data Cleanup state'leri
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [cleanupDate, setCleanupDate] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [cleanupStartDate, setCleanupStartDate] = useState('');
  const [cleanupEndDate, setCleanupEndDate] = useState('');
  const [metaCreativeCleanupLoading, setMetaCreativeCleanupLoading] = useState(false);
  // NOTE: YouTube enrich (temp) UI removed from Settings > Data Cleanup
  const [metaCreativeCleanupAllLoading, setMetaCreativeCleanupAllLoading] = useState(false);
  const [gadsAssetsYesterdayLoading, setGadsAssetsYesterdayLoading] = useState(false);
  const [cleanupSelectedTables, setCleanupSelectedTables] = useState(['google_ads_*', 'ga_*', 'meta_*', 'adjust_*', 'google_mc_*', 'search_console_*', 'shopify_*', 'ikas_*', 'woo_*', 'criteo_*', 'rtb_house_*', 'pinterest_*', 'tiktok_*']);
  
  // Notification settings state
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notif, setNotif] = useState({ daily: true, weekly: true, monthly: true, anomaly: true, revenueAlert: true });
  
  // Delete All Data state'leri  
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [deleteAllResults, setDeleteAllResults] = useState(null);
  
  // Yesterday Cleanup state'leri
  const [yesterdayCleanupLoading, setYesterdayCleanupLoading] = useState(false);
  const [yesterdayCleanupResults, setYesterdayCleanupResults] = useState(null);
  
  // Custom Date Fetch state'leri
  const [customFetchDate, setCustomFetchDate] = useState('');
  const [customFetchStartDate, setCustomFetchStartDate] = useState('');
  const [customFetchEndDate, setCustomFetchEndDate] = useState('');
  const [customFetchLoading, setCustomFetchLoading] = useState(false);
  const [customSelectedTables, setCustomSelectedTables] = useState(['google_ads_*', 'ga_*', 'meta_*', 'adjust_*', 'google_mc_*', 'search_console_*', 'criteo_*', 'rtb_house_*', 'pinterest_*', 'tiktok_*']);
  // Multi-account selection for Custom Data Fetch
  const [selectedFetchAccounts, setSelectedFetchAccounts] = useState({});
  // { GOOGLE_ADS: ['all'], META_ADS: ['conn-id-1'] }
  const [metaCreativeLoading, setMetaCreativeLoading] = useState(false);
  const [metaCreativeYesterdayLoading, setMetaCreativeYesterdayLoading] = useState(false);
  const [metaCreativeCustomLoading, setMetaCreativeCustomLoading] = useState(false);
  const [metaCreativeStartDate, setMetaCreativeStartDate] = useState('');
  const [metaCreativeEndDate, setMetaCreativeEndDate] = useState('');
  
  // Gap Detection state'leri

  // IKAS dialog states
  const [isIkasDialogOpen, setIsIkasDialogOpen] = useState(false);
  const [ikasLoading, setIkasLoading] = useState(false);
  const [ikasForm, setIkasForm] = useState({ storeName: '', clientId: '', clientSecret: '' });
  // IKAS backfill (data-cleanup tools)
  const [ikasBackfillLoading, setIkasBackfillLoading] = useState(false);
  
  // GA4 E-commerce Monthly Backfill state
  const [ecomMonthlyStart, setEcomMonthlyStart] = useState('');
  const [ecomMonthlyEnd, setEcomMonthlyEnd] = useState('');
  const [ecomMonthlyLoading, setEcomMonthlyLoading] = useState(false);
  const [ecomMonthlyResult, setEcomMonthlyResult] = useState(null);

  // GA4 E-commerce Delete state
  const [ecomDeleteStart, setEcomDeleteStart] = useState('');
  const [ecomDeleteEnd, setEcomDeleteEnd] = useState('');
  const [ecomDeleteConfirm, setEcomDeleteConfirm] = useState('');
  const [ecomDeleteLoading, setEcomDeleteLoading] = useState(false);
  const [ecomDeleteResult, setEcomDeleteResult] = useState(null);

  // GA4 E-commerce Refetch state
  const [ecomRefetchStart, setEcomRefetchStart] = useState('');
  const [ecomRefetchEnd, setEcomRefetchEnd] = useState('');
  const [ecomRefetchLoading, setEcomRefetchLoading] = useState(false);
  const [ecomRefetchResult, setEcomRefetchResult] = useState(null);
  const [ikasBackfillResult, setIkasBackfillResult] = useState(null);
  const [ikasRangeStart, setIkasRangeStart] = useState('');
  const [ikasRangeEnd, setIkasRangeEnd] = useState('');
  const [ikasRangeLoading, setIkasRangeLoading] = useState(false);
  const [ikasClearLoading, setIkasClearLoading] = useState(false);
  // IKAS custom delete (by date/range)
  const [ikasDeleteStart, setIkasDeleteStart] = useState('');
  const [ikasDeleteEnd, setIkasDeleteEnd] = useState('');
  const [ikasDeleteLoading, setIkasDeleteLoading] = useState(false);
  // SHOPIFY dialog
  const [isShopifyDialogOpen, setIsShopifyDialogOpen] = useState(false);
  const [shopifySaving, setShopifySaving] = useState(false);
  const [shopifyForm, setShopifyForm] = useState({ shopDomain: '', accessToken: '' });
  // SHOPIFY backfill (data-cleanup tools)
  const [shopifyBackfillLoading, setShopifyBackfillLoading] = useState(false);
  const [shopifyBackfillResult, setShopifyBackfillResult] = useState(null);
  const [shopifyRangeStart, setShopifyRangeStart] = useState('');
  const [shopifyRangeEnd, setShopifyRangeEnd] = useState('');
  const [shopifyClearLoading, setShopifyClearLoading] = useState(false);
  const [isShopifyClearOpen, setIsShopifyClearOpen] = useState(false);
  const [shopifyClearConfirmText, setShopifyClearConfirmText] = useState('');
  // WOOCOMMERCE dialog
  const [isWooDialogOpen, setIsWooDialogOpen] = useState(false);
  const [wooSaving, setWooSaving] = useState(false);
  const [wooForm, setWooForm] = useState({ storeUrl: '', consumerKey: '', consumerSecret: '' });
  // WOOCOMMERCE backfill (data-cleanup tools)
  const [wooRangeStart, setWooRangeStart] = useState('');
  const [wooRangeEnd, setWooRangeEnd] = useState('');
  const [wooOrdersLoading, setWooOrdersLoading] = useState(false);
  const [wooItemsLoading, setWooItemsLoading] = useState(false);
  
  // NOTE: Test Auto Sync (temp) UI removed from Settings > Data Cleanup

  // Get the initial active section from URL or localStorage or default to 'api-connections'
  const getInitialActiveSection = () => {
    // Önce URL parametresini kontrol et
    const params = new URLSearchParams(window.location.search);
    const sectionFromUrl = params.get('section');
    
    if (sectionFromUrl && settingsSections.some(s => s.id === sectionFromUrl)) {
      return sectionFromUrl;
    }
    
    // Sonra hash'i kontrol et (geriye uyumluluk için)
    const hash = window.location.hash.replace("#", "");
    if (hash && settingsSections.some(s => s.id === hash)) {
      return hash;
    }
    
    // LocalStorage'dan kontrol et
    const savedSection = localStorage.getItem('activeSettingsSection');
    if (savedSection && settingsSections.some(s => s.id === savedSection)) {
      return savedSection;
    }
    
    // Varsayılan olarak
    return "brand-details";
  };

  const [activeSection, setActiveSection] = useState(getInitialActiveSection());

  // Update URL and localStorage when section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    
    // Update URL parameter
    const url = new URL(window.location);
    url.searchParams.set('section', sectionId);
    window.history.pushState({}, '', url);
    
    // Save to localStorage
    localStorage.setItem('activeSettingsSection', sectionId);
  };

  const [isMetaDialogOpen, setIsMetaDialogOpen] = useState(false);
  const [metaForm, setMetaForm] = useState({
    appId: '',
    appSecret: '',
    accessToken: ''
  });
  const [metaLoading, setMetaLoading] = useState(false);
  
  // BigQuery connect modal state
  const [isBigQueryDialogOpen, setIsBigQueryDialogOpen] = useState(false);
  const [bqForm, setBqForm] = useState({ refreshToken: '', accessToken: '', clientId: '', clientSecret: '' });
  const [bqSaving, setBqSaving] = useState(false);
  const [bqShowTokens, setBqShowTokens] = useState(false);
  const [bqStep, setBqStep] = useState(1);
  const [bqProjects, setBqProjects] = useState([]);
  const [bqLoadingProjects, setBqLoadingProjects] = useState(false);
  const [bqSelectedProject, setBqSelectedProject] = useState(null);

  // Adjust connect modal state
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ apiToken: '' });
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustLastToken, setAdjustLastToken] = useState('');
  const [isRtbHouseDialogOpen, setIsRtbHouseDialogOpen] = useState(false);
  const [rtbHouseToken, setRtbHouseToken] = useState('');
  const [rtbHouseStep, setRtbHouseStep] = useState(1);
  const [rtbHouseAccounts, setRtbHouseAccounts] = useState([]);
  const [rtbHouseLoadingAccounts, setRtbHouseLoadingAccounts] = useState(false);
  const [rtbHouseSaving, setRtbHouseSaving] = useState(false);
  const [selectedRtbHouseAccountHash, setSelectedRtbHouseAccountHash] = useState('');
  
  // Pinterest states
  const [isPinterestAccountDialogOpen, setIsPinterestAccountDialogOpen] = useState(false);
  const [pinterestAdAccounts, setPinterestAdAccounts] = useState([]);
  const [selectedPinterestAccount, setSelectedPinterestAccount] = useState('');
  const [pinterestSaving, setPinterestSaving] = useState(false);
  
  // Adjust apps state
  const [isAdjustAppsDialogOpen, setIsAdjustAppsDialogOpen] = useState(false);
  const [adjustAppsLoading, setAdjustAppsLoading] = useState(false);
  const [adjustApps, setAdjustApps] = useState([]);
  const [adjustSavingAppId, setAdjustSavingAppId] = useState(null);
  const [isGoogleAdsDialogOpen, setIsGoogleAdsDialogOpen] = useState(false);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [tempConnectionId, setTempConnectionId] = useState(null);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isGoogleAnalyticsDialogOpen, setIsGoogleAnalyticsDialogOpen] = useState(false);
  const [googleAnalyticsAccounts, setGoogleAnalyticsAccounts] = useState([]);
  const [loadingGAAccounts, setLoadingGAAccounts] = useState(false);
  const [setupType, setSetupType] = useState(null);
  const [isMetaAdsDialogOpen, setIsMetaAdsDialogOpen] = useState(false);
  const [metaAdsAccounts, setMetaAdsAccounts] = useState([]);
  const [loadingMetaAccounts, setLoadingMetaAccounts] = useState(false);
  // TikTok Ads dialog states
  const [isTikTokDialogOpen, setIsTikTokDialogOpen] = useState(false);
  const [tiktokAdvertisers, setTiktokAdvertisers] = useState([]);
  const [selectedTikTokAdvertiser, setSelectedTikTokAdvertiser] = useState(null);
  const [loadingTikTokAdvertisers, setLoadingTikTokAdvertisers] = useState(false);
  const [savingTikTokAccount, setSavingTikTokAccount] = useState(false);
  const [isAnalyticsSetupOpen, setIsAnalyticsSetupOpen] = useState(false);
  const [analyticsProperties, setAnalyticsProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  // Search Console dialog states
  const [isSearchConsoleDialogOpen, setIsSearchConsoleDialogOpen] = useState(false);
  const [searchConsoleSites, setSearchConsoleSites] = useState([]);
  const [loadingSearchConsoleSites, setLoadingSearchConsoleSites] = useState(false);
  const [selectedSearchConsoleSite, setSelectedSearchConsoleSite] = useState(null);
  // Merchant Center dialog states
  const [isMerchantCenterDialogOpen, setIsMerchantCenterDialogOpen] = useState(false);
  const [merchantCenterAccounts, setMerchantCenterAccounts] = useState([]);
  const [loadingMerchantCenterAccounts, setLoadingMerchantCenterAccounts] = useState(false);
  const [selectedMerchantCenterAccount, setSelectedMerchantCenterAccount] = useState(null);
  const [manualMerchantId, setManualMerchantId] = useState('');
  const [manualMerchantName, setManualMerchantName] = useState('');
  // Meta catalogs dialog
  const [isMetaCatalogsDialogOpen, setIsMetaCatalogsDialogOpen] = useState(false);
  const [metaCatalogsLoading, setMetaCatalogsLoading] = useState(false);
  const [metaCatalogs, setMetaCatalogs] = useState([]);
  // Meta wizard (single popup) state
  // NEW FLOW: 'business' -> 'account' -> 'pages' -> 'instagram' -> 'pixels' -> 'catalogs'
  const [metaWizardStep, setMetaWizardStep] = useState('business');
  const [metaWizardLoading, setMetaWizardLoading] = useState(false);
  const [metaWizardCatalogs, setMetaWizardCatalogs] = useState([]); // reserved
  const [metaWizardPages, setMetaWizardPages] = useState([]);
  const [metaWizardPixels, setMetaWizardPixels] = useState([]);
  const [metaPagesLoading, setMetaPagesLoading] = useState(false);
  const [metaPixelsLoading, setMetaPixelsLoading] = useState(false);
  const [selectedMetaPage, setSelectedMetaPage] = useState(null);
  const [selectedMetaPixel, setSelectedMetaPixel] = useState(null);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  // Business Manager state (NEW FLOW)
  const [metaWizardBusinessManagers, setMetaWizardBusinessManagers] = useState([]);
  const [businessManagersLoading, setBusinessManagersLoading] = useState(false);
  const [selectedBusinessManager, setSelectedBusinessManager] = useState(null);
  const [metaWizardBMAdAccounts, setMetaWizardBMAdAccounts] = useState([]);
  const [bmAdAccountsLoading, setBmAdAccountsLoading] = useState(false);
  // Ad Account's authorized Instagram accounts (for ads)
  const [adAccountInstagramAccounts, setAdAccountInstagramAccounts] = useState([]);
  const [adAccountInstagramLoading, setAdAccountInstagramLoading] = useState(false);
  const [selectedAdAccountInstagram, setSelectedAdAccountInstagram] = useState(null);
  
  // Auto sync toggle states
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);

  // Handle Shopify OAuth callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifySuccess = params.get('shopify_success');
    const shopifyError = params.get('shopify_error');
    
    if (shopifySuccess === 'true') {
      toast.success('Shopify connected successfully! Data sync will start shortly.');
      // Clean up URL
      params.delete('shopify_success');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    if (shopifyError) {
      const errorMessages = {
        'missing_params': 'Shopify connection failed: Missing parameters',
        'invalid_state': 'Shopify connection failed: Invalid state',
        'brand_not_found': 'Shopify connection failed: Brand not found',
        'no_token': 'Shopify connection failed: No access token received',
        'callback_failed': 'Shopify connection failed: Please try again'
      };
      toast.error(errorMessages[shopifyError] || 'Shopify connection failed');
      // Clean up URL
      params.delete('shopify_error');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Handle ikas OAuth callback results (legacy - kept for backward compat)
    const ikasSuccess = params.get('ikas_success');
    const ikasError = params.get('ikas_error');
    
    if (ikasSuccess === 'true') {
      toast.success('İkas bağlantısı başarılı! Veri senkronizasyonu kısa süre içinde başlayacak.');
      params.delete('ikas_success');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    if (ikasError) {
      toast.error('İkas bağlantısı başarısız oldu. Lütfen tekrar deneyin.');
      params.delete('ikas_error');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Handle Criteo OAuth callback results
    const criteoStatus = params.get('criteo');
    const criteoError = params.get('error');
    if (criteoStatus === 'connected') {
      const name = params.get('name');
      toast.success(`Criteo connected successfully!${name ? ` (${name})` : ''}`);
      params.delete('criteo');
      params.delete('name');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      fetchConnections();
    }
    if (criteoStatus === 'select') {
      // Consent granted more than one advertiser: ask which account this brand is.
      toast('Criteo connected. Please choose which Criteo account belongs to this brand.', { icon: 'ℹ️' });
      params.delete('criteo');
      params.delete('name');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      fetchConnections();
      openCriteoAccountPicker();
    }
    if (criteoError === 'criteo_auth_failed') {
      const msg = params.get('message') || 'Connection failed';
      toast.error(`Criteo connection failed: ${msg}`);
      params.delete('error');
      params.delete('message');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Handle Pinterest OAuth callback results
    const pinterestStatus = params.get('pinterest');
    if (pinterestStatus === 'connected') {
      const name = params.get('name');
      toast.success(`Pinterest connected successfully!${name ? ` (${name})` : ''}`);
      params.delete('pinterest');
      params.delete('name');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      fetchConnections();
    }
    if (pinterestStatus === 'select_account') {
      toast('Pinterest connected! Please select an ad account.', { icon: '📌' });
      params.delete('pinterest');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      fetchConnections();
      setIsPinterestAccountDialogOpen(true);
      setTimeout(() => loadPinterestAdAccounts(), 500);
    }
    const pinterestError = params.get('error');
    if (pinterestError === 'pinterest_auth_failed') {
      const msg = params.get('message') || 'Connection failed';
      toast.error(`Pinterest connection failed: ${msg}`);
      params.delete('error');
      params.delete('message');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Bağlantıları getiren fonksiyon
  const fetchConnections = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      console.log('Fetching connections for brand:', brandId);

      const response = await fetch('https://api.glowytics.com/api/auth/connections', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const data = await response.json();
      console.log('Connections data:', data);
      
      // Bağlantıları doğru formata dönüştür (platform key normalize)
      // Aynı platform için birden çok kayıt olabilir; öncelik:
      // 1) ACTIVE olanı seç
      // 2) Aksi halde createdAt en yeni olanı seç
      const connectionStatus = {};
      data.forEach((conn) => {
        const key = String(conn.platform || '').toUpperCase();
        const existing = connectionStatus[key];
        const candidate = {
          status: conn.status,
          customerId: conn.customerId,
          expiresAt: conn.expiresAt,
          accountName: conn.accountName || conn.name,
          createdAt: conn.createdAt
        };
        if (!existing) {
          connectionStatus[key] = candidate;
        } else {
          const existingIsActive = String(existing.status || '').toUpperCase() === 'ACTIVE';
          const candidateIsActive = String(candidate.status || '').toUpperCase() === 'ACTIVE';
          if (!existingIsActive && candidateIsActive) {
            connectionStatus[key] = candidate;
          } else if (existingIsActive === candidateIsActive) {
            const existingTs = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
            const candidateTs = candidate.createdAt ? new Date(candidate.createdAt).getTime() : 0;
            if (candidateTs > existingTs) {
              connectionStatus[key] = candidate;
            }
          }
        }
      });

      setConnections(connectionStatus);
    } catch (error) {
      console.error('Fetch connections error:', error);
      toast.error('Failed to fetch connections');
    } finally {
      setLoading(false);
    }
  };

  // Multi-account: fetch connected accounts for a platform
  const fetchMultiAccounts = async (platform) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId || !isMultiAccountFor(brandId, platform)) return;
    setMultiAccountsLoading(prev => ({ ...prev, [platform]: true }));
    try {
      const res = await fetch(`https://api.glowytics.com/api/accounts/${platform}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setMultiAccounts(prev => ({ ...prev, [platform]: data }));
    } catch (e) {
      console.error(`fetchMultiAccounts(${platform}) error:`, e);
    } finally {
      setMultiAccountsLoading(prev => ({ ...prev, [platform]: false }));
    }
  };

  const fetchAllMultiAccounts = () => {
    MULTI_ACCOUNT_PLATFORMS.forEach(p => fetchMultiAccounts(p));
  };

  // Multi-account: remove account
  const [confirmDeleteAccId, setConfirmDeleteAccId] = useState(null);
  const [deletingAccId, setDeletingAccId] = useState(null);

  const handleRemoveAccount = async (connectionId, platform) => {
    const brandId = localStorage.getItem('currentBrandId');
    setDeletingAccId(connectionId);
    try {
      const res = await fetch(`https://api.glowytics.com/api/accounts/${connectionId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Failed to remove account');
      toast.success('Account removed');
      setConfirmDeleteAccId(null);
      fetchMultiAccounts(platform);
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingAccId(null);
    }
  };

  // Multi-account: add new account via OAuth
  const handleAddAccount = (platformKey) => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) {
      toast.error('Please select a brand first');
      return;
    }
    // Trigger OAuth flow — same as handleConnect but won't replace existing
    if (platformKey === 'GOOGLE_ADS') {
      fetch('https://api.glowytics.com/api/auth/google-ads', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      })
        .then(r => r.json())
        .then(data => {
          if (data.url) window.location.href = data.url;
          else toast.error('Failed to get auth URL');
        })
        .catch(e => toast.error(e.message || 'Failed to start connection'));
    } else if (platformKey === 'META_ADS') {
      handleMetaOAuthStart();
    }
  };

  // IKAS handlers
  const handleIkasFormChange = (e) => {
    const { name, value } = e.target;
    setIkasForm(prev => ({ ...prev, [name]: value }));
  };

  const handleIkasSave = async (e) => {
    e.preventDefault();
    try {
      setIkasLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const res = await fetch('https://api.glowytics.com/api/auth/ikas/connect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          storeName: ikasForm.storeName,
          clientId: ikasForm.clientId,
          clientSecret: ikasForm.clientSecret
        })
      });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || 'Failed to save Ikas connection');
      toast.success('İkas bağlantısı kaydedildi');
      setIsIkasDialogOpen(false);
      setIkasForm({ storeName: '', clientId: '', clientSecret: '' });
      await fetchConnections();
      // Schedule a delayed full sync ~3 minutes after successful IKAS connect (similar to GA4/GAds/Meta behavior)
      try {
        const delayMs = 3 * 60 * 1000;
        toast.success('IKAS connected. Full sync will start in ~3 minutes.');
        setTimeout(async () => {
          try {
            await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-Brand-ID': brandId
              },
              body: JSON.stringify({ mode: 'full' })
            });
            toast.success('IKAS full data sync started');
          } catch (e) {
            console.error('IKAS delayed full sync error:', e);
          }
        }, delayMs);
      } catch (_) {}
    } catch (err) {
      console.error('IKAS save error:', err);
      toast.error(err.message || 'Failed to save Ikas connection');
    } finally {
      setIkasLoading(false);
    }
  };

  // useEffect'i güncelleyelim
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await fetchConnections();
      } catch (error) {
        console.error('Initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Fetch brand notification settings when Notifications tab is active
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        setNotifLoading(true);
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const res = await fetch(`https://api.glowytics.com/api/brands/${brandId}/notification-settings`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load notification settings');
        const s = data.settings || {};
        setNotif({
          daily: s.dailyEnabled !== false,
          weekly: s.weeklyEnabled !== false,
          monthly: s.monthlyEnabled !== false,
          anomaly: s.anomalyEnabled !== false,
          revenueAlert: s.revenueAlertEnabled !== false
        });
      } catch (e) {
        console.error('Notification settings load error:', e);
        toast.error(e.message || 'Failed to load notification settings');
      } finally {
        setNotifLoading(false);
      }
    };
    if (activeSection === 'notifications') loadNotificationSettings();
  }, [activeSection]);

  const handleSaveNotificationSettings = async () => {
    try {
      setNotifSaving(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const res = await fetch(`https://api.glowytics.com/api/brands/${brandId}/notification-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          daily_enabled: !!notif.daily,
          weekly_enabled: !!notif.weekly,
          monthly_enabled: !!notif.monthly,
          anomaly_enabled: !!notif.anomaly,
          revenue_alert_enabled: !!notif.revenueAlert
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save settings');
      toast.success('Notification settings saved');
    } catch (e) {
      console.error('Notification settings save error:', e);
      toast.error(e.message || 'Failed to save notification settings');
    } finally {
      setNotifSaving(false);
    }
  };

  // URL parametrelerini kontrol et
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');
    const connectionId = params.get('connection');

    console.log('URL parameters:', { setup, connectionId });
    
    if (setup && connectionId) {
      console.log(`Setting up ${setup} with connection ID: ${connectionId}`);
      setTempConnectionId(connectionId);
      
      if (setup === 'google_ads_account') {
        console.log('Starting Google Ads account setup...');
        setSetupType('google_ads_account');
        setIsLoadingOverlay(true);
        fetchGoogleAdsAccounts(connectionId);
      } else if (setup === 'merchant_center_account') {
        console.log('Starting Merchant Center setup...');
        setSetupType('merchant_center_account');
        setTempConnectionId(connectionId);
        setIsLoadingOverlay(true);
        setTimeout(() => {
          fetchMerchantCenterAccounts();
        }, 800);
      } else if (setup === 'search_console_account') {
        console.log('Starting Search Console setup...');
        setSetupType('search_console_account');
        setTempConnectionId(connectionId);
        setIsLoadingOverlay(true);
        // slight delay similar to GA to ensure connection persisted
        setTimeout(() => {
          fetchSearchConsoleSites();
        }, 800);
      } else if (setup === 'google_analytics_account') {
        console.log('Starting Google Analytics account setup...');
        setSetupType('google_analytics_account');
        setIsLoadingOverlay(true);
        
        // Kısa bir gecikme ekleyelim, bazen bağlantı henüz veritabanına kaydedilmemiş olabilir
        setTimeout(() => {
          fetchGoogleAnalyticsProperties();
        }, 1000);
      } else if (setup === 'meta_ads_account') {
        console.log('Starting Meta Ads account setup...');
        setSetupType('meta_ads_account');
        setIsLoadingOverlay(true);
        // Persist gecikmesi için hemen dene + kısa gecikmeyle tekrar dene
        try { fetchMetaAdsAccounts(connectionId); } catch (_) {}
        setTimeout(() => { fetchMetaAdsAccounts(connectionId); }, 800);
      } else if (setup === 'tiktok_ads_account') {
        console.log('Starting TikTok Ads account setup...');
        setSetupType('tiktok_ads_account');
        setIsLoadingOverlay(true);
        fetchTikTokAdvertisers(connectionId);
      }
    } else {
      console.log('No setup parameters found in URL');
    }
  }, []);

  // Update useEffect to handle URL changes
  useEffect(() => {
    // Listen for popstate events (browser back/forward)
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const sectionFromUrl = params.get('section');
      
      if (sectionFromUrl && settingsSections.some(s => s.id === sectionFromUrl)) {
        setActiveSection(sectionFromUrl);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Add this useEffect to inspect the connections object
  useEffect(() => {
    if (Object.keys(connections).length > 0) {
      console.log('Connections object structure:', JSON.stringify(connections, null, 2));
      
      // Her bir platform için bağlantı detaylarını görelim
      Object.keys(connections).forEach(key => {
        console.log(`Connection details for ${key}:`, connections[key]);
        console.log('Available properties:', Object.keys(connections[key]));
      });
    }
  }, [connections]);

  // Brand name'i çek
  const fetchBrandName = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage');
        return;
      }

      console.log('Fetching brand details for brand ID:', brandId);

      // Yeni endpoint'i kullan
      const response = await fetch(`https://api.glowytics.com/api/brand-info/${brandId}/details`, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch brand details');
      }

      const data = await response.json();
      console.log('🔥 Yeni endpoint\'ten gelen brand verileri:', data);
      
      // Gelen veriyi detaylı olarak console'a bas
      console.log('📊 Brand Detayları:');
      console.log('   - ID:', data.id);
      console.log('   - Name:', data.name);
      console.log('   - Status:', data.status);
      console.log('   - Website URL:', data.websiteUrl);
      console.log('   - Industry:', data.industry);
      console.log('   - Time Zone:', data.timeZone);
      console.log('   - Currency:', data.currency);
      console.log('   - Logo URL:', data.logoUrl);
      console.log('   - User ID:', data.userId);
      console.log('   - Created At:', data.createdAt);
      console.log('   - Updated At:', data.updatedAt);
      
      // Brand name'i state'e yaz
      if (data.name) {
        setBrandName(data.name);
        console.log('✅ Brand name state\'e yazıldı:', data.name);
      }
      
      // Diğer alanları da state'e yaz
      if (data.websiteUrl !== undefined) {
        setWebsiteUrl(data.websiteUrl || '');
      }
      if (data.industry !== undefined) {
        setIndustry(data.industry || '');
      }
      if (data.timeZone !== undefined) {
        setTimeZone(data.timeZone || '');
      }
      if (data.currency !== undefined) {
        setCurrency(data.currency || '');
      }
      if (data.logoUrl !== undefined) {
        setLogoUrl(data.logoUrl || '');
      }
      
      console.log('✅ Tüm brand detayları state\'e yazıldı');
    } catch (error) {
      console.error('❌ Error fetching brand name:', error);
      console.error('Error details:', error.message);
    }
  };
  
  // Sayfa yüklendiğinde bağlantıları ve brand name'i çek
  useEffect(() => {
    fetchConnections();
    fetchAllMultiAccounts();
    fetchBrandName();
    fetchDataSyncLogs();
    fetchAutoSyncStatus(); // NEW: Fetch auto sync status
    
    // Listen for popstate events (browser back/forward)
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const sectionFromUrl = params.get('section');
      
      if (sectionFromUrl && settingsSections.some(s => s.id === sectionFromUrl)) {
        setActiveSection(sectionFromUrl);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Google Ads bağlantı başlatma
  const handleGoogleAdsConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      const response = await fetch('https://api.glowytics.com/api/auth/google-ads', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Error initiating Google Ads connection:', error);
      toast.error('Failed to connect to Google Ads');
    }
  };

  // Google Analytics bağlantı başlatma
  const handleAnalyticsConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      setIsLoadingOverlay(true);

      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to connect to Google Analytics');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to connect to Google Analytics');
    } finally {
      setIsLoadingOverlay(false);
    }
  };

  // Criteo bağlantı başlatma (OAuth Authorization Code)
  const handleCriteoConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const response = await fetch('https://api.glowytics.com/api/criteo/auth/start', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (error) {
      console.error('Error initiating Criteo connection:', error);
      toast.error('Failed to connect to Criteo');
    }
  };

  // Criteo hesap secimi: bagli consent birden fazla advertiser kapsayabilir
  const openCriteoAccountPicker = async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) {
      toast.error('Please select a brand first');
      return;
    }
    setCriteoPickerOpen(true);
    setCriteoPickerLoading(true);
    try {
      const [advRes, statusRes] = await Promise.all([
        fetch('https://api.glowytics.com/api/criteo/advertisers', {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        }),
        fetch('https://api.glowytics.com/api/criteo/status', {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        })
      ]);
      const advData = await advRes.json();
      if (!advRes.ok) throw new Error(advData.error || 'Failed to load Criteo accounts');
      const list = advData.advertisers || [];
      setCriteoAdvertisers(list);

      let current = null;
      if (statusRes.ok) {
        const st = await statusRes.json();
        current = st.advertiserId || null;
      }
      // Preselect what is already bound, or the only option when there is just one.
      setCriteoChosenAdvertiser(current || (list.length === 1 ? String(list[0].id) : null));
    } catch (error) {
      console.error('Criteo advertisers error:', error);
      toast.error(error.message || 'Failed to load Criteo accounts');
      setCriteoAdvertisers([]);
    } finally {
      setCriteoPickerLoading(false);
    }
  };

  const saveCriteoAdvertiser = async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId || !criteoChosenAdvertiser) return;
    try {
      setCriteoSaving(true);
      const response = await fetch('https://api.glowytics.com/api/criteo/select-advertiser', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ advertiserId: criteoChosenAdvertiser })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save Criteo account');

      toast.success(`Criteo account set to ${data.accountName}`);
      if (data.alsoUsedBy) {
        // Same Criteo account on two brands is usually the mistake, not the intent.
        toast(`Heads up: this Criteo account is also connected to "${data.alsoUsedBy.brandName}".`, { icon: '⚠️' });
      }
      setCriteoPickerOpen(false);
      await fetchConnections();
    } catch (error) {
      console.error('Criteo select-advertiser error:', error);
      toast.error(error.message || 'Failed to save Criteo account');
    } finally {
      setCriteoSaving(false);
    }
  };

  const handlePinterestConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const response = await fetch('https://api.glowytics.com/api/pinterest/auth/start', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (error) {
      console.error('Error initiating Pinterest connection:', error);
      toast.error('Failed to connect to Pinterest');
    }
  };

  const handlePinterestSelectAccount = async () => {
    try {
      setPinterestSaving(true);
      const brandId = localStorage.getItem('currentBrandId');
      const resp = await fetch('https://api.glowytics.com/api/pinterest/select-account', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ adAccountId: selectedPinterestAccount }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);
      toast.success(`Pinterest ad account selected: ${data.name || selectedPinterestAccount}`);
      setIsPinterestAccountDialogOpen(false);
      fetchConnections();
    } catch (error) {
      toast.error(error.message || 'Failed to select Pinterest account');
    } finally {
      setPinterestSaving(false);
    }
  };

  const loadPinterestAdAccounts = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const resp = await fetch('https://api.glowytics.com/api/pinterest/ad-accounts', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId },
      });
      const data = await resp.json();
      if (resp.ok) {
        setPinterestAdAccounts(data.accounts || []);
        setSelectedPinterestAccount(data.selectedId || (data.accounts?.[0]?.id || ''));
      }
    } catch (e) {
      console.error('Failed to load Pinterest ad accounts:', e);
    }
  };

  // Merchant Center bağlantı başlatma
  const handleMerchantCenterConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const response = await fetch('https://api.glowytics.com/api/auth/merchant-center', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });
      const ct = response.headers.get('content-type') || '';
      if (!response.ok) {
        let msg = 'Failed to connect to Merchant Center';
        if (ct.includes('application/json')) {
          try {
            const err = await response.json();
            msg = err.error || msg;
          } catch (_) {}
        }
        throw new Error(msg);
      }
      const data = ct.includes('application/json') ? await response.json() : {};
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (error) {
      console.error('Merchant Center connection error:', error);
      toast.error(error.message || 'Failed to connect to Merchant Center');
    }
  };

  // Google Analytics bağlantısını aktifleştir
  const activateAnalyticsConnection = async (connectionId) => {
    try {
      console.log('Activating Google Analytics connection:', connectionId);
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId || !connectionId) {
        console.error('Missing brandId or connectionId');
        return false;
      }
      
      // Bağlantıyı aktifleştir
      const response = await fetch(`https://api.glowytics.com/api/auth/google-analytics/activate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          connectionId: connectionId
        })
      });
      
      if (!response.ok) {
        console.error('Failed to activate connection:', response.status);
        return false;
      }
      
      console.log('Connection activated successfully');
      return true;
    } catch (error) {
      console.error('Error activating connection:', error);
      return false;
    }
  };

  // Google Analytics hesaplarını getir
  const fetchGoogleAnalyticsAccounts = async () => {
    try {
      setLoadingGAAccounts(true);
      const brandId = localStorage.getItem('currentBrandId');

      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics/properties', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      const data = await response.json();
      console.log('GA Properties:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch Google Analytics properties');
      }

      if (data && Array.isArray(data.properties)) {
        setAnalyticsProperties(data.properties);
      } else {
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to fetch Google Analytics properties');
    } finally {
      setLoadingGAAccounts(false);
      setIsLoadingOverlay(false);
    }
  };

  // Google Analytics property'lerini getir
  const fetchGoogleAnalyticsProperties = async () => {
    try {
      setLoadingGAAccounts(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        throw new Error('No brand selected. Please select a brand first.');
      }

      // Önce bağlantı var mı kontrol et
      const connections = await fetch('https://api.glowytics.com/api/auth/connections', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      }).then(res => res.json());
      const gaConnection = connections.find(conn => conn.platform === 'GOOGLE_ANALYTICS' && conn.status === 'ACTIVE');
      if (gaConnection) {
        setIsLoadingOverlay(false);
        setLoadingGAAccounts(false);
        fetchConnections();
        return;
      }

      console.log('Fetching Google Analytics properties for brand:', brandId);
      console.log('Connection ID:', tempConnectionId);
      
      // Bağlantı bilgilerini al
      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics/properties', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId,
          'X-Connection-ID': tempConnectionId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Google Analytics properties');
      }

      const data = await response.json();
      console.log('Google Analytics properties response:', data);
      
      // Veri yapısını kontrol et ve property'leri ayarla
      if (data && data.success && data.data && Array.isArray(data.data)) {
        // Python scriptinden dönen veri yapısı
        setAnalyticsProperties(data.data);
      } else if (data && Array.isArray(data.properties)) {
        // Alternatif veri yapısı
        setAnalyticsProperties(data.properties);
      } else if (data && Array.isArray(data)) {
        // Doğrudan dizi olarak dönen veri yapısı
        setAnalyticsProperties(data);
      } else {
        throw new Error('Invalid response format: properties not found');
      }

      // Dialog'u sadece bir kez aç
      setIsAnalyticsSetupOpen(true);
    } catch (error) {
      console.error('Error fetching Google Analytics properties:', error);
      toast.error(`Failed to fetch Google Analytics properties: ${error.message}`);
    } finally {
      setLoadingGAAccounts(false);
      setIsLoadingOverlay(false);
    }
  };

  // Google Ads hesaplarını getir
  const fetchGoogleAdsAccounts = async (pendingConnectionId) => {
    try {
      console.log('Fetching Google Ads accounts...');
      setLoadingAccounts(true);

      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected. Please select a brand first.');
      }

      // If no pending connection ID (not from OAuth callback), check existing connections
      if (!pendingConnectionId) {
        console.log('Checking existing connections for brand:', brandId);
        const connectionsResponse = await fetch('https://api.glowytics.com/api/auth/connections', {
          credentials: 'include',
          headers: {
            'X-Brand-ID': brandId
          }
        });

        if (!connectionsResponse.ok) {
          throw new Error(`Failed to fetch connections: ${connectionsResponse.status} ${connectionsResponse.statusText}`);
        }

        const connections = await connectionsResponse.json();
        console.log('Current connections:', connections);

        const adsConnection = connections.find(
          conn => conn.platform === 'GOOGLE_ADS' && conn.status === 'ACTIVE'
        );

        // Eğer aktif bağlantı varsa ve OAuth callback'ten gelmiyorsak, popup'ı açma
        if (adsConnection) {
          console.log('Active Google Ads connection found:', adsConnection);
          setIsLoadingOverlay(false);
          setLoadingAccounts(false);
          fetchConnections();
          return;
        }
      }

      // Hesapları getir (pendingConnectionId varsa onu kullan)
      console.log('Fetching Google Ads accounts...', pendingConnectionId ? `(connection: ${pendingConnectionId})` : '');
      const accountsUrl = pendingConnectionId
        ? `https://api.glowytics.com/api/google-ads/accounts/test?connectionId=${pendingConnectionId}`
        : 'https://api.glowytics.com/api/google-ads/accounts/test';
      const response = await fetch(accountsUrl, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
        } catch (e) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      console.log('Google Ads accounts response:', data);
      
      if (data.success && data.accounts) {
        if (!data.accounts.active || !Array.isArray(data.accounts.active) || data.accounts.active.length === 0) {
          console.warn('No active accounts found or invalid format:', data.accounts);
          setGoogleAdsAccounts([]);
          toast.error('No active Google Ads accounts found. Please make sure you have access to at least one active account.');
        } else {
          console.log('Setting Google Ads accounts:', data.accounts.active);
          setGoogleAdsAccounts(data.accounts.active);
          setIsGoogleAdsDialogOpen(true);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch accounts: No accounts data in response');
      }
    } catch (error) {
      console.error('Error fetching Google Ads accounts:', error);
      toast.error(`Failed to fetch Google Ads accounts: ${error.message}`);
    } finally {
      setLoadingAccounts(false);
      setIsLoadingOverlay(false);
    }
  };

  // Google Ads için select change handler
  const handleSelectChange = (value) => {
    try {
      const account = JSON.parse(value);
      console.log('Selected Google Ads account:', account);
      
      setSelectedAccount({
        id: account.id,
        name: account.name,
        currency: account.currency,
        timeZone: account.timeZone,
        managerCustomerId: account.managerCustomerId || null
      });
    } catch (error) {
      console.error('Parse error:', error);
    }
  };

  // Google Analytics için select change handler
  const handleGASelectChange = (value) => {
    try {
      if (!value) {
        setSelectedAccount(null);
        return;
      }
      
      const account = JSON.parse(value);
      console.log('Selected GA account:', account);
      setSelectedAccount(account);
    } catch (error) {
      console.error('Error parsing account:', error);
      toast.error('Failed to select account');
    }
  };

  // Google Analytics hesap kaydetme
  const handleGASave = async () => {
    try {
      if (!selectedAccount) {
        toast.error('Please select an account');
        return;
      }

      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics/complete-setup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': localStorage.getItem('currentBrandId')
        },
        body: JSON.stringify({
          connectionId: tempConnectionId,
          customerId: selectedAccount.id,
          accountName: selectedAccount.displayName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save account');
      }

      toast.success('Google Analytics account connected successfully');
      await fetchConnections();
      setIsGoogleAnalyticsDialogOpen(false);
      setSelectedAccount(null);

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to save account');
    }
  };

  // Meta Ads hesaplarını getir
  const fetchMetaAdsAccounts = async (pendingConnectionId) => {
    try {
      setLoadingMetaAccounts(true);
      // Prefer backend proxy that uses stored access token
      const brandId = localStorage.getItem('currentBrandId');
      const accountsUrl = pendingConnectionId
        ? `https://api.glowytics.com/api/meta-ads/accounts?connectionId=${pendingConnectionId}`
        : 'https://api.glowytics.com/api/meta-ads/accounts';
      const res = await fetch(accountsUrl, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const body = ct.includes('application/json') ? await res.json().catch(() => ({})) : {};
        const msg = body?.error || (await res.text().catch(() => ''));
        if (res.status === 401 && (body?.error === 'TOKEN_EXPIRED' || /token/i.test(String(msg)))) {
          toast.error('Meta access token expired. Reconnecting…');
          try { await handleMetaOAuthStart(); } catch {}
          throw new Error('Meta access token expired');
        }
        throw new Error(msg || 'Failed to fetch Meta Ads accounts');
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data?.accounts) ? data.accounts : (Array.isArray(data) ? data : []));
      setMetaAdsAccounts(list || []);
      setSelectedAccount(null);
      setSelectedCatalog(null);
      setMetaWizardCatalogs([]);
      
      // NEW FLOW: Start with Business Manager selection
      setSelectedBusinessManager(null);
      setMetaWizardBMAdAccounts([]);
      setMetaWizardStep('business');
      setIsMetaAdsDialogOpen(true);
      
      // Fetch Business Managers for the new flow
      fetchBusinessManagers(pendingConnectionId);

    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch Meta Ads accounts');
    } finally {
      setLoadingMetaAccounts(false);
      setIsLoadingOverlay(false);
    }
  };

  // NEW FLOW: Fetch Business Managers
  const fetchBusinessManagers = async (pendingConnectionId) => {
    try {
      setBusinessManagersLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const connParam = pendingConnectionId || tempConnectionId;
      const bmUrl = connParam
        ? `https://api.glowytics.com/api/meta-ads/business-managers?connectionId=${connParam}`
        : 'https://api.glowytics.com/api/meta-ads/business-managers';
      const res = await fetch(bmUrl, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to fetch Business Managers');
      }
      setMetaWizardBusinessManagers(data?.businesses || []);
      console.log('[Settings] Fetched Business Managers:', data?.businesses?.length);
    } catch (error) {
      console.error('Error fetching Business Managers:', error);
      toast.error(error.message || 'Failed to fetch Business Managers');
    } finally {
      setBusinessManagersLoading(false);
    }
  };

  // NEW FLOW: Fetch Ad Accounts from Business Manager
  const fetchBMAdAccounts = async (businessId) => {
    try {
      setBmAdAccountsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const connParam = tempConnectionId ? `?connectionId=${tempConnectionId}` : '';
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/business/${businessId}/ad-accounts${connParam}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to fetch Ad Accounts');
      }
      setMetaWizardBMAdAccounts(data?.adAccounts || []);
      console.log('[Settings] Fetched BM Ad Accounts:', data?.adAccounts?.length);
    } catch (error) {
      console.error('Error fetching BM Ad Accounts:', error);
      toast.error(error.message || 'Failed to fetch Ad Accounts');
    } finally {
      setBmAdAccountsLoading(false);
    }
  };

  // NEW FLOW: Fetch Pages from Business Manager
  const fetchBMPages = async (businessId) => {
    try {
      setMetaPagesLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/business/${businessId}/pages`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to fetch Pages');
      }
      setMetaWizardPages(data?.pages || []);
      console.log('[Settings] Fetched BM Pages:', data?.pages?.length);
    } catch (error) {
      console.error('Error fetching BM Pages:', error);
      toast.error(error.message || 'Failed to fetch Pages');
    } finally {
      setMetaPagesLoading(false);
    }
  };

  // NEW FLOW: Fetch Instagram Accounts from Business Manager
  const fetchBMInstagramAccounts = async (businessId) => {
    try {
      setAdAccountInstagramLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/business/${businessId}/instagram-accounts`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to fetch Instagram Accounts');
      }
      setAdAccountInstagramAccounts(data?.instagramAccounts || []);
      console.log('[Settings] Fetched BM Instagram Accounts:', data?.instagramAccounts?.length);
    } catch (error) {
      console.error('Error fetching BM Instagram Accounts:', error);
      // Don't show error toast - Instagram might not be available
    } finally {
      setAdAccountInstagramLoading(false);
    }
  };

  // Meta Ads için select change handler
  const handleMetaAdsSelectChange = (value) => {
    try {
      if (!value) {
        setSelectedAccount(null);
        return;
      }
      
      const account = JSON.parse(value);
      console.log('Selected Meta account:', account);
      setSelectedAccount(account);
    } catch (error) {
      console.error('Error parsing account:', error);
      toast.error('Failed to select account');
    }
  };

  // Facebook ile giriş başlat (OAuth URL'i backend'den al)
  const handleMetaOAuthStart = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const res = await fetch('https://api.glowytics.com/api/auth/meta-ads', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const msg = ct.includes('application/json') ? (await res.json().catch(() => ({})))?.error : await res.text();
        throw new Error(msg || 'Failed to get Meta auth URL');
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Auth URL not returned');
      }
    } catch (e) {
      console.error('Meta OAuth start error:', e);
      toast.error(e.message || 'Failed to start Meta auth');
    }
  };

  // Backend proxy ile Meta ad hesaplarını listele (stored token kullanır)
  const listMetaAccountsViaBackend = async () => {
    try {
      setLoadingMetaAccounts(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        setLoadingMetaAccounts(false);
        return;
      }
      const res = await fetch('https://api.glowytics.com/api/meta-ads/accounts', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const msg = ct.includes('application/json') ? (await res.json().catch(() => ({})))?.error : await res.text();
        throw new Error(msg || 'Failed to fetch Meta accounts');
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data?.accounts) ? data.accounts : (Array.isArray(data) ? data : []));
      setMetaAdsAccounts(list);
      setIsMetaAdsDialogOpen(true);
    } catch (e) {
      console.error('Meta accounts list error:', e);
      toast.error(e.message || 'Failed to fetch Meta Ads accounts');
    } finally {
      setLoadingMetaAccounts(false);
    }
  };

  // List Meta Product Catalogs via backend proxy
  const fetchMetaCatalogs = async (overrideAdAccountId, openDialog = true) => {
    try {
      setMetaCatalogsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const qs = new URLSearchParams();
      if (overrideAdAccountId) qs.set('adAccountId', overrideAdAccountId);
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/catalogs${qs.toString() ? `?${qs.toString()}` : ''}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      const isJson = ct.includes('application/json');
      if (!res.ok) {
        const body = isJson ? await res.json().catch(() => ({})) : {};
        const msg = body?.error || (await res.text().catch(() => ''));
        if (res.status === 401 && (body?.error === 'TOKEN_EXPIRED' || /token/i.test(String(msg)))) {
          toast.error('Meta access token expired. Reconnecting…');
          try { await handleMetaOAuthStart(); } catch {}
        } else {
          toast.error(msg || 'Failed to fetch Meta catalogs');
        }
        return;
      }
      const data = isJson ? await res.json() : {};
      const list = Array.isArray(data?.catalogs) ? data.catalogs : [];
      setMetaCatalogs(list);
      if (openDialog) {
        setIsMetaCatalogsDialogOpen(true);
      }
    } catch (e) {
      console.error('Meta catalogs error:', e);
      toast.error(e.message || 'Failed to fetch Meta catalogs');
    } finally {
      setMetaCatalogsLoading(false);
    }
  };

  const fetchMetaPages = async () => {
    try {
      setMetaPagesLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const res = await fetch(`https://api.glowytics.com/api/meta-ads/pages`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.pages)) {
        setMetaWizardPages(data.pages);
      } else {
        toast.error(data.error || 'Failed to fetch Meta pages');
      }
    } catch (e) {
      console.error('Meta pages error:', e);
      toast.error('Failed to load Meta pages');
    } finally {
      setMetaPagesLoading(false);
    }
  };

  // Fetch Instagram accounts that the token has access to (from connected Pages)
  const fetchAdAccountInstagramAccounts = async () => {
    try {
      setAdAccountInstagramLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const res = await fetch(`https://api.glowytics.com/api/meta-ads/instagram-accounts`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.instagramAccounts)) {
        setAdAccountInstagramAccounts(data.instagramAccounts);
        // Auto-select first one if available
        if (data.instagramAccounts.length > 0) {
          setSelectedAdAccountInstagram(data.instagramAccounts[0]);
        }
      } else {
        console.log('No Instagram accounts found');
        setAdAccountInstagramAccounts([]);
      }
    } catch (e) {
      console.error('Instagram accounts error:', e);
      setAdAccountInstagramAccounts([]);
    } finally {
      setAdAccountInstagramLoading(false);
    }
  };

  const fetchMetaPixels = async (adAccountId) => {
    try {
      setMetaPixelsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId || !adAccountId) return;

      const res = await fetch(`https://api.glowytics.com/api/meta-ads/ad-accounts/${adAccountId}/pixels`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.pixels)) {
        setMetaWizardPixels(data.pixels);
      } else {
        toast.error(data.error || 'Failed to fetch Meta pixels');
      }
    } catch (e) {
      console.error('Meta pixels error:', e);
      toast.error('Failed to load Meta pixels');
    } finally {
      setMetaPixelsLoading(false);
    }
  };

  // Meta form için input handler ekle
  const handleMetaFormChange = (e) => {
    const { name, value } = e.target;
    setMetaForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Adjust form input handler
  const handleAdjustFormChange = (e) => {
    const { name, value } = e.target;
    setAdjustForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // (Legacy) Update Access Token flow removed

  // Meta form submit handler'ı
  const handleMetaFormSubmit = async (e) => {
    e.preventDefault();
    setMetaLoading(true);

    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) {
      toast.error('No brand selected. Please select a brand first.');
      return;
    }

    try {
      // Önce Meta bağlantısını kur
      const response = await fetch('https://api.glowytics.com/api/auth/meta-ads/connect', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          appId: metaForm.appId,
          appSecret: metaForm.appSecret,
          accessToken: metaForm.accessToken
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to connect Meta Ads');
      }

      const data = await response.json();
      setTempConnectionId(data.connection.id);
      setIsMetaDialogOpen(false);

      // Meta API'den reklam hesaplarını çek
      const API_VERSION = 'v18.0';
      const BASE_URL = `https://graph.facebook.com/${API_VERSION}/me/adaccounts`;
      
      const params = new URLSearchParams({
        access_token: metaForm.accessToken, // Kullanıcının girdiği access token'ı kullan
        fields: 'id,name,account_status,currency',
        limit: '100'
      });

      const adAccountsResponse = await fetch(`${BASE_URL}?${params}`);
      const adAccountsData = await adAccountsResponse.json();

      if (adAccountsData.error) {
        throw new Error(adAccountsData.error.message);
      }

      // Ad accountları state'e kaydet ve dialog'u aç
      setMetaAdsAccounts(adAccountsData.data || []);
      setIsMetaAdsDialogOpen(true);

    } catch (error) {
      console.error('Meta connection error:', error);
      toast.error(error.message || 'Failed to connect Meta Ads');
    } finally {
      setMetaLoading(false);
    }
  };

  // Adjust connect submit handler
  const handleAdjustConnect = async (e) => {
    e.preventDefault();
    setAdjustLoading(true);
    try {
      const token = (adjustForm.apiToken || '').trim();
      if (!token) {
        toast.error('Please enter a valid Adjust API token');
        return;
      }
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }

      // Fetch apps via backend proxy WITHOUT saving the token
      const res = await fetch('https://api.glowytics.com/api/auth/adjust/apps', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiToken: token })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Failed to fetch Adjust apps';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || msg;
        }
        throw new Error(msg);
      }
      const js = await res.json();
      const apps = Array.isArray(js?.apps) ? js.apps : [];
      setAdjustApps(apps);
      setAdjustLastToken(token);
      setIsAdjustDialogOpen(false);
      setAdjustForm({ apiToken: '' });
      setIsAdjustAppsDialogOpen(true);
    } catch (error) {
      console.error('Adjust connect error:', error);
      const msg = error?.message || 'Failed to connect Adjust';
      toast.error(msg);
    } finally {
      setAdjustLoading(false);
    }
  };

  // Fetch Adjust apps via backend proxy (for refresh)
  const fetchAdjustAppsViaProxy = async () => {
    if (!adjustLastToken) throw new Error('Missing token to refresh');
    const res = await fetch('https://api.glowytics.com/api/auth/adjust/apps', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiToken: adjustLastToken })
    });
    const ct = res.headers.get('content-type') || '';
    if (!res.ok) {
      let msg = 'Failed to refresh Adjust apps';
      if (ct.includes('application/json')) {
        const js = await res.json().catch(() => ({}));
        msg = js.error || msg;
      }
      throw new Error(msg);
    }
    const js = await res.json();
    return Array.isArray(js?.apps) ? js.apps : [];
  };

  // Fetch Adjust apps available for the connected token
  const fetchAdjustApps = async () => {
    try {
      setAdjustAppsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) throw new Error('No brand selected');

      const res = await fetch('https://api.glowytics.com/api/auth/adjust/apps', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        let errMsg = 'Failed to fetch Adjust apps';
        if (contentType.includes('application/json')) {
          const err = await res.json().catch(() => ({}));
          errMsg = err.error || errMsg;
        }
        throw new Error(errMsg);
      }

      let data = [];
      if (contentType.includes('application/json')) {
        const js = await res.json();
        // Accept either { apps: [...] } or [...]
        data = Array.isArray(js) ? js : (Array.isArray(js.apps) ? js.apps : []);
      } else {
        throw new Error('Unexpected response format');
      }

      setAdjustApps(data);
    } catch (e) {
      console.error('Fetch Adjust apps error:', e);
      toast.error(e.message || 'Failed to fetch Adjust apps');
      setAdjustApps([]);
    } finally {
      setAdjustAppsLoading(false);
    }
  };

  const handleAdjustAppSave = async (app) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected');
        return;
      }
      const appToken = String(app.id || app.app_token || app.appToken || '');
      const appName = app.name || app.display_name || app.title || 'Adjust App';
      if (!appToken) {
        toast.error('Invalid app token');
        return;
      }
      setAdjustSavingAppId(appToken);
      const res = await fetch('https://api.glowytics.com/api/auth/adjust/complete-setup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({ apiToken: adjustLastToken, appToken, appName })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Failed to save Adjust connection';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || msg;
        }
        throw new Error(msg);
      }
      toast.success('Adjust app connected');
      setIsAdjustAppsDialogOpen(false);
      await fetchConnections();
    } catch (e) {
      console.error('Adjust save app error:', e);
      toast.error(e.message || 'Failed to save Adjust connection');
    } finally {
      setAdjustSavingAppId(null);
    }
  };

  const handleRtbHouseFetchAccounts = async () => {
    try {
      const token = (rtbHouseToken || '').trim();
      if (!token) {
        toast.error('Please enter RTB House access token');
        return;
      }
      setRtbHouseLoadingAccounts(true);
      const res = await fetch('https://api.glowytics.com/api/auth/rtb-house/advertisers', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: token }),
      });
      const js = await res.json();
      if (!res.ok) {
        throw new Error(js?.error || 'Failed to fetch RTB House advertisers');
      }
      const accounts = Array.isArray(js?.advertisers) ? js.advertisers : [];
      setRtbHouseAccounts(accounts);
      if (accounts.length > 0) {
        setSelectedRtbHouseAccountHash(String(accounts[0]?.hash || ''));
        setRtbHouseStep(2);
        toast.success(`Found ${accounts.length} RTB House account(s)`);
      } else {
        setSelectedRtbHouseAccountHash('');
        toast.error('No account found for this token');
      }
    } catch (error) {
      console.error('RTB House advertisers fetch error:', error);
      toast.error(error.message || 'Failed to fetch RTB House advertisers');
      setRtbHouseAccounts([]);
      setSelectedRtbHouseAccountHash('');
      setRtbHouseStep(1);
    } finally {
      setRtbHouseLoadingAccounts(false);
    }
  };

  const handleRtbHouseSave = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const token = (rtbHouseToken || '').trim();
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      if (!token) {
        toast.error('Please enter RTB House access token');
        return;
      }
      if (!selectedRtbHouseAccountHash) {
        toast.error('Please select an RTB House account');
        return;
      }
      const selected = rtbHouseAccounts.find((a) => String(a?.hash || '') === selectedRtbHouseAccountHash);
      if (!selected) {
        toast.error('Selected RTB House account not found');
        return;
      }
      setRtbHouseSaving(true);
      const res = await fetch('https://api.glowytics.com/api/auth/rtb-house/connect', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        body: JSON.stringify({
          accessToken: token,
          advertiserHash: selected.hash,
          advertiserName: selected.name,
        }),
      });
      const js = await res.json();
      if (!res.ok) {
        throw new Error(js?.error || 'Failed to connect RTB House');
      }
      toast.success('RTB House connected successfully');
      setIsRtbHouseDialogOpen(false);
      setRtbHouseToken('');
      setRtbHouseStep(1);
      setRtbHouseAccounts([]);
      setSelectedRtbHouseAccountHash('');
      await fetchConnections();
    } catch (error) {
      console.error('RTB House save connection error:', error);
      toast.error(error.message || 'Failed to connect RTB House');
    } finally {
      setRtbHouseSaving(false);
    }
  };

  // Meta Ads hesap kaydetme
  const handleMetaAdsSave = async () => {
    try {
      if (!selectedAccount) {
        toast.error('Please select an account');
        return;
      }

      // URL'i düzeltelim
      const response = await fetch('https://api.glowytics.com/api/auth/meta-ads/connect', { // endpoint'i değiştirdik
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': localStorage.getItem('currentBrandId')
        },
        body: JSON.stringify({
          customerId: selectedAccount.id.replace('act_', ''), // act_ prefix'ini kaldıralım
          accountName: selectedAccount.name,
          connectionId: tempConnectionId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save account');
      }

      setIsMetaAdsDialogOpen(false);
      toast.success('Meta Ads account connected successfully');
      fetchConnections();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to save account');
    }
  };

  // Property seçildiğinde çalışacak fonksiyon
  const handlePropertySelect = async () => {
    try {
      if (!selectedPropertyId) {
        toast.error('Please select or enter a property ID');
        return;
      }

      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }

      console.log('Setting up Google Analytics with property:', selectedPropertyId);
      console.log('Connection ID:', tempConnectionId);
      console.log('Brand ID:', brandId);

      // Önce bağlantı durumunu kontrol edelim
      const connectionResponse = await fetch(`https://api.glowytics.com/api/auth/connections`, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });
      
      const connections = await connectionResponse.json();
      
      // Google Analytics bağlantısını bulalım
      let analyticsConnection = connections.find(conn => 
        conn.platform === 'GOOGLE_ANALYTICS' && conn.id === tempConnectionId
      );
      
      // Eğer bağlantı bulunamazsa, ilk Google Analytics bağlantısını kullanalım
      if (!analyticsConnection) {
        const allAnalyticsConnections = connections.filter(conn => conn.platform === 'GOOGLE_ANALYTICS');
        if (allAnalyticsConnections.length > 0) {
          analyticsConnection = allAnalyticsConnections[0];
          setTempConnectionId(analyticsConnection.id);
        } else {
          throw new Error('No Google Analytics connection found');
        }
      }
      
      // Eğer bağlantı PENDING durumundaysa, aktifleştirelim
      if (analyticsConnection.status === 'PENDING') {
        await activateAnalyticsConnection(analyticsConnection.id);
      }

      // Property ID'yi manuel olarak girdiyse, property adını da manuel olarak oluşturalım
      const propertyName = analyticsProperties.length > 0 
        ? analyticsProperties.find(p => (p.property_id || p.id) === selectedPropertyId)?.property_name || 'GA4 Property'
        : 'GA4 Property';

      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          connectionId: tempConnectionId,
          propertyId: selectedPropertyId,
          propertyName: propertyName // Property adını da gönderelim
        })
      });

      const responseText = await response.text();
      console.log('Setup response text:', responseText);

      if (!response.ok) {
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
        } catch (e) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.log('Response is not JSON, but setup might have succeeded');
      }
      
      // Bağlantıları yenile
      await fetchConnections();
      
      setIsAnalyticsSetupOpen(false);
      toast.success('Google Analytics connected successfully');
      window.history.replaceState({}, '', '/settings'); // URL parametrelerini temizle

    } catch (error) {
      console.error('Setup error:', error);
      toast.error(`Failed to setup Google Analytics: ${error.message}`);
    }
  };

  // Google Ads hesabını kaydet
  const handleGoogleAdsAccountSave = async () => {
    try {
      if (!selectedAccount) {
        toast.error('Please select an account');
        return;
      }

      const response = await fetch('https://api.glowytics.com/api/auth/google-ads/complete-setup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': localStorage.getItem('currentBrandId')
        },
        body: JSON.stringify({
          connectionId: tempConnectionId,
          customerId: selectedAccount.id,
          accountName: selectedAccount.name,
          managerCustomerId: selectedAccount.managerCustomerId || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save account');
      }

      setIsGoogleAdsDialogOpen(false);
      window.history.replaceState({}, '', '/settings');
      toast.success('Google Ads account connected successfully');
      fetchConnections();
      fetchMultiAccounts('GOOGLE_ADS');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to save account');
    }
  };

  // Google Analytics API'sini test et
  const testGoogleAnalyticsAPI = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      toast.loading('Testing Google Analytics API...');
      
      // Önce bağlantıları kontrol edelim
      console.log('Checking connections...');
      const connectionsResponse = await fetch('https://api.glowytics.com/api/auth/connections', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });
      
      const connections = await connectionsResponse.json();
      console.log('All connections:', connections);
      
      // Google Analytics bağlantılarını filtreleyelim
      const analyticsConnections = connections.filter(conn => conn.platform === 'GOOGLE_ANALYTICS');
      console.log('Analytics connections:', analyticsConnections);
      
      if (analyticsConnections.length === 0) {
        toast.dismiss();
        toast.error('No Google Analytics connections found');
        return;
      }
      
      // İlk bağlantıyı kullanalım
      const connectionId = analyticsConnections[0].id;
      console.log('Using connection ID:', connectionId);
      
      // Property'leri isteyelim
      console.log('Requesting properties...');
      const response = await fetch('https://api.glowytics.com/api/auth/google-analytics/properties', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId,
          'X-Connection-ID': connectionId
        }
      });
      
      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      toast.dismiss();
      
      if (!response.ok) {
        toast.error(`API error: ${response.status} ${response.statusText}`);
        return;
      }
      
      try {
        const data = JSON.parse(responseText);
        console.log('Parsed data:', data);
        
        if (data && Array.isArray(data.properties) && data.properties.length > 0) {
          toast.success(`Found ${data.properties.length} properties`);
        } else if (data && Array.isArray(data) && data.length > 0) {
          toast.success(`Found ${data.length} properties`);
        } else {
          toast.error('No properties found in response');
        }
      } catch (e) {
        console.error('JSON parse error:', e);
        toast.error('Invalid JSON response');
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.dismiss();
      toast.error(`Test failed: ${error.message}`);
    }
  };

  // Merchant Center API test (sonucu sadece consola bas)
  const testMerchantCenterAPI = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      toast.loading('Testing Merchant Center API...');
      // Hesapları listelemek için backend proxy endpoint'i çağır
      const response = await fetch('https://api.glowytics.com/api/merchant-center/accounts', {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });
      const contentType = response.headers.get('content-type') || '';
      const raw = await response.text();
      console.log('Merchant Center raw response:', raw);
      toast.dismiss();
      if (!response.ok) {
        toast.error(`Merchant Center API error: ${response.status} ${response.statusText}`);
        return;
      }
      if (contentType.includes('application/json')) {
        try {
          const data = JSON.parse(raw);
          console.log('Merchant Center parsed data:', data);
          const count = Array.isArray(data?.accounts) ? data.accounts.length
                        : Array.isArray(data) ? data.length
                        : (Array.isArray(data?.resources) ? data.resources.length : 0);
          if (count > 0) {
            toast.success(`Merchant Center OK — ${count} item(s)`);
          } else {
            toast.success('Merchant Center OK — response logged');
          }
        } catch (e) {
          console.log('Merchant Center non-JSON response logged');
          toast.success('Merchant Center OK — response logged');
        }
      } else {
        toast.success('Merchant Center OK — response logged');
      }
    } catch (error) {
      console.error('Merchant Center test error:', error);
      toast.dismiss();
      toast.error(error.message || 'Merchant Center test failed');
    }
  };

  // Search Console bağlantı başlatma
  const handleSearchConsoleConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const response = await fetch('https://api.glowytics.com/api/auth/search-console', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = response.headers.get('content-type') || '';
      if (!response.ok) {
        let msg = 'Failed to connect to Search Console';
        if (ct.includes('application/json')) {
          try {
            const err = await response.json();
            msg = err.error || msg;
          } catch (_) {}
        }
        throw new Error(msg);
      }
      const data = ct.includes('application/json') ? await response.json() : {};
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (error) {
      console.error('Search Console connection error:', error);
      toast.error(error.message || 'Failed to connect to Search Console');
    }
  };

  // Search Console test (sites list)
  const testSearchConsoleAPI = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      toast.loading('Testing Search Console API...');
      const res = await fetch('https://api.glowytics.com/api/auth/search-console/sites', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const raw = await res.text();
      console.log('Search Console raw response:', raw);
      toast.dismiss();
      if (!res.ok) {
        toast.error(`Search Console API error: ${res.status} ${res.statusText}`);
        return;
      }
      try {
        const data = JSON.parse(raw);
        console.log('Search Console parsed data:', data);
        const count = Array.isArray(data?.siteEntry) ? data.siteEntry.length : 0;
        if (count > 0) toast.success(`Search Console OK — ${count} site(s)`);
        else toast.success('Search Console OK — response logged');
      } catch {
        toast.success('Search Console OK — response logged');
      }
    } catch (e) {
      console.error('Search Console test error:', e);
      toast.dismiss();
      toast.error(e.message || 'Search Console test failed');
    }
  };

  // Google Business Profile (GBP) connect
  const handleGBPConnect = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const response = await fetch('https://api.glowytics.com/api/auth/gbp', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!response.ok) {
        throw new Error('Failed to start Google Business Profile auth');
      }
      const data = await response.json().catch(() => ({}));
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (e) {
      console.error('GBP connect error:', e);
      toast.error(e.message || 'Failed to connect Google Business Profile');
    }
  };

  // Merchant Center accounts fetch/save/select
  const fetchMerchantCenterAccounts = async () => {
    try {
      setLoadingMerchantCenterAccounts(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) throw new Error('No brand selected. Please select a brand first.');
      const res = await fetch('https://api.glowytics.com/api/auth/merchant-center/accounts?debug=1', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      const raw = await res.text();
      if (!res.ok) {
        console.error('Merchant Center accounts error:', raw);
        // surface server message if present
        try {
          const err = JSON.parse(raw);
          throw new Error(err.message || err.error || 'Failed to fetch Merchant Center accounts');
        } catch {
          throw new Error('Failed to fetch Merchant Center accounts');
        }
      }
      let accounts = [];
      if (ct.includes('application/json')) {
        try {
          const data = JSON.parse(raw);
          // Normalize possible structures
          if (Array.isArray(data?.accounts)) {
            accounts = data.accounts;
          } else if (Array.isArray(data?.accountIdentifiers)) {
            // Content API authinfo structure
            accounts = data.accountIdentifiers.map(ai => ({
              accountId: ai?.merchantId || ai?.aggregatorId || ai?.accountId || ai?.id,
              displayName: ai?.displayName || ai?.name || `Account ${ai?.merchantId || ai?.id || ''}`
            }));
          } else if (Array.isArray(data?.resources)) {
            accounts = data.resources;
          } else if (Array.isArray(data)) {
            accounts = data;
          }
        } catch (e) {
          console.warn('Merchant Center accounts parse error:', e);
        }
      }
      setMerchantCenterAccounts(accounts);
      setIsMerchantCenterDialogOpen(true);
    } catch (e) {
      console.error('Fetch Merchant Center accounts error:', e);
      toast.error(e.message || 'Failed to fetch Merchant Center accounts');
      // Open dialog to allow manual entry as a fallback
      setIsMerchantCenterDialogOpen(true);
      setIsLoadingOverlay(false);
    } finally {
      setLoadingMerchantCenterAccounts(false);
      setIsLoadingOverlay(false);
    }
  };

  const handleMerchantCenterSelect = (value) => {
    try {
      if (!value) {
        setSelectedMerchantCenterAccount(null);
        return;
      }
      const acc = JSON.parse(value);
      setSelectedMerchantCenterAccount(acc);
    } catch (e) {
      console.error('MC select parse error:', e);
    }
  };

  const handleMerchantCenterSave = async () => {
    try {
      if (!selectedMerchantCenterAccount) {
        toast.error('Please select an account');
        return;
      }
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/auth/merchant-center/complete-setup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          connectionId: tempConnectionId,
          merchantId: (selectedMerchantCenterAccount.accountId || selectedMerchantCenterAccount.id || selectedMerchantCenterAccount.name || ''),
          accountName: (selectedMerchantCenterAccount.displayName || selectedMerchantCenterAccount.name || 'Merchant Center')
        })
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(js.error || 'Failed to save Merchant Center account');
      }
      toast.success('Google Merchant Center connected successfully');
      setIsMerchantCenterDialogOpen(false);
      setSelectedMerchantCenterAccount(null);
      window.history.replaceState({}, '', '/settings');
      await fetchConnections();
    } catch (e) {
      console.error('Merchant Center save error:', e);
      toast.error(e.message || 'Failed to save Merchant Center account');
    }
  };

  const fetchSearchConsoleSites = async () => {
    try {
      setLoadingSearchConsoleSites(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected. Please select a brand first.');
      }
      const res = await fetch('https://api.glowytics.com/api/auth/search-console/sites', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to fetch Search Console sites');
      }
      const data = await res.json();
      const entries = Array.isArray(data?.siteEntry) ? data.siteEntry : [];
      setSearchConsoleSites(entries);
      setIsSearchConsoleDialogOpen(true);
    } catch (e) {
      console.error('Fetch Search Console sites error:', e);
      toast.error(e.message || 'Failed to fetch Search Console sites');
      setIsLoadingOverlay(false);
    } finally {
      setLoadingSearchConsoleSites(false);
      setIsLoadingOverlay(false);
    }
  };

  const handleSearchConsoleSelect = (value) => {
    try {
      if (!value) {
        setSelectedSearchConsoleSite(null);
        return;
      }
      const site = JSON.parse(value);
      setSelectedSearchConsoleSite(site);
    } catch (e) {
      console.error('SC select parse error:', e);
    }
  };

  const handleSearchConsoleSave = async () => {
    try {
      if (!selectedSearchConsoleSite) {
        toast.error('Please select a site');
        return;
      }
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/auth/search-console/complete-setup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          connectionId: tempConnectionId,
          siteUrl: selectedSearchConsoleSite.siteUrl,
          siteName: selectedSearchConsoleSite.siteUrl
        })
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(js.error || 'Failed to save Search Console site');
      }
      toast.success('Search Console connected successfully');
      setIsSearchConsoleDialogOpen(false);
      setSelectedSearchConsoleSite(null);
      window.history.replaceState({}, '', '/settings');
      await fetchConnections();
    } catch (e) {
      console.error('SC save error:', e);
      toast.error(e.message || 'Failed to save Search Console site');
    }
  };

  // Sonra platforms array'ini tanımlayalım
  const platforms = [
    {
      name: 'Google Ads',
      description: 'Connect your Google Ads account to track campaign performance',
      icon: '/google_ads-512.webp',
      connectUrl: '/api/auth/google-ads',
      connectHandler: handleGoogleAdsConnect,
      platform_key: 'GOOGLE_ADS',
      category: 'Ads'
    },
    {
      name: 'Meta Ads',
      description: 'Connect Facebook and Instagram advertising accounts',
      icon: '/6033716.png',
      connectUrl: '/api/auth/meta-ads',
      connectHandler: () => setIsMetaDialogOpen(true),
      platform_key: 'META_ADS',
      category: 'Ads'
    },
    {
      name: 'Google Analytics',
      description: 'Import your website analytics data',
      icon: '/GA4logoicon.svg',
      connectUrl: '/api/auth/google-analytics',
      connectHandler: handleAnalyticsConnect,
      platform_key: 'GOOGLE_ANALYTICS',
      category: 'Analytics'
    },
    {
      name: 'Google Search Console',
      description: 'Connect Search Console to analyze search performance',
      icon: '/search-console-icon-2025-1v33.svg',
      connectUrl: '/api/auth/search-console',
      connectHandler: handleSearchConsoleConnect,
      platform_key: 'SEARCH_CONSOLE',
      category: 'SearchListing'
    },
    {
      name: 'Google Business Profile',
      description: 'Connect to view GBP performance (impressions, actions) and location info',
      icon: '/my-business.svg',
      connectUrl: '/api/auth/gbp',
      connectHandler: handleGBPConnect,
      platform_key: 'GBP',
      category: 'Presence'
    },
    {
      name: 'Google Merchant Center',
      description: 'Sync Google Merchant Center product feeds',
      icon: '/google-merchant-centerpngg.svg',
      connectUrl: '/api/auth/merchant-center',
      connectHandler: handleMerchantCenterConnect,
      platform_key: 'MERCHANT_CENTER',
      category: 'SearchListing'
    },
    {
      name: 'Adjust',
      description: 'Connect Adjust to import mobile attribution data',
      icon: '/adjustlogopng.png',
      connectUrl: '/api/auth/adjust',
      connectHandler: () => setIsAdjustDialogOpen(true),
      platform_key: 'ADJUST',
      category: 'Analytics'
    },
    {
      name: 'BigQuery',
      description: 'Connect Google BigQuery to query your data warehouse',
      icon: null,
      customIcon: 'bigquery',
      connectHandler: () => setIsBigQueryDialogOpen(true),
      platform_key: 'BIGQUERY',
      category: 'Analytics'
    },
    {
      name: 'İkas',
      description: 'İkas mağazanızı bağlayın (Manuel App Token)',
      icon: '/ikaslogoicon.png',
      connectHandler: () => setIsIkasDialogOpen(true),
      platform_key: 'IKAS',
      category: 'Commerce'
    },
    {
      name: 'Shopify',
      description: 'Connect Shopify to ingest orders (Admin API token)',
      icon: '/shopifyicon.png',
      connectUrl: '/api/auth/shopify',
      connectHandler: () => setIsShopifyDialogOpen(true),
      platform_key: 'SHOPIFY',
      category: 'Commerce'
    },
    {
      name: 'WooCommerce',
      description: 'Connect WooCommerce store (URL + Consumer Key/Secret)',
      icon: '/woocommerceicon.png',
      connectUrl: '/api/auth/woocommerce',
      connectHandler: () => setIsWooDialogOpen(true),
      platform_key: 'WOOCOMMERCE',
      category: 'Commerce'
    },
    {
      name: 'TikTok Ads',
      description: 'Track your TikTok advertising campaigns',
      icon: '/download.png',
      connectUrl: '/api/auth/tiktok-ads',
      platform_key: 'TIKTOK_ADS',
      category: 'Ads'
    },
    {
      name: 'Criteo',
      description: 'Connect your Criteo retargeting campaigns',
      icon: '/CRTO-25f99ae9.png',
      connectHandler: handleCriteoConnect,
      platform_key: 'CRITEO_ADS',
      category: 'Ads'
    },
    {
      name: 'RTB House',
      description: 'Integrate RTB House advertising data',
      icon: '/download.jpeg',
      connectHandler: () => {
        setRtbHouseToken('');
        setRtbHouseStep(1);
        setRtbHouseAccounts([]);
        setSelectedRtbHouseAccountHash('');
        setIsRtbHouseDialogOpen(true);
      },
      platform_key: 'RTB_HOUSE',
      category: 'Ads'
    },
    {
      name: 'Pinterest',
      description: 'Connect your Pinterest ad account via OAuth',
      icon: '/pinterestlogopng.png',
      connectHandler: handlePinterestConnect,
      platform_key: 'PINTEREST_ADS',
      category: 'Ads'
    }
  ];

  const handleDisconnect = async (platform) => {
    // Modal'ı aç ve platform bilgisini kaydet
    setPlatformToDisconnect(platform);
    setIsDisconnectModalOpen(true);
  };

  // TikTok Ads: fetch authorized advertisers for a PENDING connection + open picker
  const fetchTikTokAdvertisers = async (pendingConnectionId) => {
    try {
      setLoadingTikTokAdvertisers(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      setTempConnectionId(pendingConnectionId);
      const res = await fetch(
        `https://api.glowytics.com/api/auth/tiktok-ads/pending/${pendingConnectionId}`,
        { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch TikTok advertisers');
      setTiktokAdvertisers(Array.isArray(data?.advertisers) ? data.advertisers : []);
      setSelectedTikTokAdvertiser(null);
      setIsTikTokDialogOpen(true);
    } catch (error) {
      console.error('Error fetching TikTok advertisers:', error);
      toast.error(error.message || 'Failed to fetch TikTok advertisers');
    } finally {
      setLoadingTikTokAdvertisers(false);
      setIsLoadingOverlay(false);
    }
  };

  // TikTok Ads: activate the selected advertiser (single-account)
  const handleTikTokAccountSave = async () => {
    if (!selectedTikTokAdvertiser || !tempConnectionId) return;
    try {
      setSavingTikTokAccount(true);
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(
        'https://api.glowytics.com/api/auth/tiktok-ads/complete-setup',
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          body: JSON.stringify({
            connectionId: tempConnectionId,
            customerId: selectedTikTokAdvertiser.advertiser_id,
            accountName: selectedTikTokAdvertiser.advertiser_name || null
          })
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to connect TikTok advertiser');
      toast.success('TikTok Ads connected');
      setIsTikTokDialogOpen(false);
      setSelectedTikTokAdvertiser(null);
      setTiktokAdvertisers([]);
      setTempConnectionId(null);
      setSetupType(null);
      // Clean URL params
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('setup');
        url.searchParams.delete('connection');
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}
      await fetchConnections();
    } catch (error) {
      console.error('TikTok Ads complete-setup error:', error);
      toast.error(error.message || 'Failed to connect TikTok advertiser');
    } finally {
      setSavingTikTokAccount(false);
    }
  };

  // Yeni fonksiyon: Disconnect işlemini gerçekleştir
  const confirmDisconnect = async () => {
    if (!platformToDisconnect) return;
    
    try {
      setDisconnectLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      
      // Platform key'i URL formatına çevir (GOOGLE_ADS -> google-ads)
      const platformUrlKey = platformToDisconnect.platform_key
        .toLowerCase()
        .replace(/_/g, '-');
      
      const response = await fetch(`https://api.glowytics.com/api/auth/${platformUrlKey}/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      // Önce response'un content-type'ını kontrol et
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        let errorMessage = 'Failed to disconnect';
        
        // Eğer JSON response ise parse et
        if (contentType && contentType.includes('application/json')) {
          try {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
        }
        
        throw new Error(errorMessage);
      }

      // Başarılı response'u da kontrol et
      if (contentType && contentType.includes('application/json')) {
        await response.json(); // Response'u oku ama kullanma (sadece valid JSON olduğunu doğrula)
      }

      // Bağlantıları yeniden yükle ve state'i güncelle
      await fetchConnections();
      toast.success(`${platformToDisconnect.name} disconnected successfully`);
      
      // Modal'ı kapat ve state'leri temizle
      setIsDisconnectModalOpen(false);
      setPlatformToDisconnect(null);
      
    } catch (error) {
      console.error(`Disconnect error for ${platformToDisconnect.name}:`, error);
      toast.error(error.message || `Failed to disconnect ${platformToDisconnect.name}`);
    } finally {
      setDisconnectLoading(false);
    }
  };

  // handleConnect fonksiyonunu geri ekleyelim
  const handleConnect = async (platform) => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      if (platform.connectHandler) {
        platform.connectHandler();
        return;
      }

      const response = await fetch(`https://api.glowytics.com${platform.connectUrl}`, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to start connection process');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Connect error:', error);
      toast.error('Failed to connect platform');
    }
  };

  const handleBqFetchProjects = async () => {
    if (!bqForm.refreshToken && !bqForm.accessToken) {
      toast.error('Refresh Token or Access Token is required');
      return;
    }
    setBqLoadingProjects(true);
    try {
      const res = await fetch('https://api.glowytics.com/api/bigquery/projects', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
        body: JSON.stringify({
          refreshToken: bqForm.refreshToken || undefined,
          accessToken: bqForm.accessToken || undefined,
          clientId: bqForm.clientId || undefined,
          clientSecret: bqForm.clientSecret || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to list projects');
      if (!data.projects?.length) {
        toast.error('No accessible projects found');
        return;
      }
      setBqProjects(data.projects);
      setBqSelectedProject(data.projects[0]?.id || null);
      setBqStep(2);
    } catch (e) {
      toast.error(e.message);
    } finally { setBqLoadingProjects(false); }
  };

  const handleBigQueryConnect = async () => {
    if (!bqSelectedProject) {
      toast.error('Please select a project');
      return;
    }
    setBqSaving(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const selectedProj = bqProjects.find(p => p.id === bqSelectedProject);
      const res = await fetch('https://api.glowytics.com/api/auth/bigquery/connect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          projectId: bqSelectedProject,
          projectName: selectedProj?.name || bqSelectedProject,
          refreshToken: bqForm.refreshToken || undefined,
          accessToken: bqForm.accessToken || undefined,
          clientId: bqForm.clientId || undefined,
          clientSecret: bqForm.clientSecret || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Connection failed');
      toast.success(`BigQuery connected — ${bqSelectedProject}`);
      setIsBigQueryDialogOpen(false);
      setBqForm({ refreshToken: '', accessToken: '', clientId: '', clientSecret: '' });
      setBqStep(1);
      setBqProjects([]);
      setBqSelectedProject(null);
      await fetchConnections();
    } catch (e) {
      toast.error(e.message);
    } finally { setBqSaving(false); }
  };

  // NOTE: Full Mode Refresh (temp) UI removed from Settings > Data Cleanup

  // Test amaçlı yesterday modu veri çekme fonksiyonu
  const handleFetchYesterdayData = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      
      // Start sync status for all platforms
      const { startSync } = useSyncStore.getState();
      startSync('GOOGLE_ADS');
      startSync('META_ADS');
      startSync('GOOGLE_ANALYTICS');
      if (connections?.IKAS?.status === 'ACTIVE') startSync('IKAS');
      
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          mode: 'yesterday'  // Yesterday modu
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json();
          if (errorData.error === 'SYNC_CONFLICT') {
            toast.warning(`⚠️ ${errorData.message}`);
            return;
          }
        }
        throw new Error('Failed to fetch yesterday data');
      }

      toast.success('🚀 Yesterday data sync started for all platforms');

      // Fire-and-forget: also trigger IKAS backfill for yesterday if connected
      if (connections?.IKAS?.status === 'ACTIVE') {
        try {
          const y = new Date();
          y.setDate(y.getDate() - 1);
          const yesterdayStr = y.toISOString().split('T')[0];
          await fetch('https://api.glowytics.com/api/labs/ikas/backfill', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Brand-ID': brandId
            },
            body: JSON.stringify({ startDate: yesterdayStr, endDate: yesterdayStr })
          });
          toast.success('IKAS yesterday sync triggered');
        } catch (e) {
          console.error('IKAS yesterday trigger error:', e);
          toast.error('Failed to trigger IKAS yesterday sync');
        }
      }
    } catch (error) {
      console.error('Error fetching yesterday data:', error);
      toast.error('Failed to fetch yesterday data');
      
      // Clear sync status on error
      const { endSync } = useSyncStore.getState();
      endSync('GOOGLE_ADS');
      endSync('META_ADS');
      endSync('GOOGLE_ANALYTICS');
      if (connections?.IKAS?.status === 'ACTIVE') {
        const { endSync: endSync2 } = useSyncStore.getState();
        endSync2('IKAS');
      }
    }
  };

  // Fetch only Google Ads asset-related tables (yesterday):
  // google_ads_gdn_placement_performance_raw, google_ads_product_group_performance_raw,
  // google_ads_asset_metrics_raw, google_ads_asset_detail_raw
  const handleFetchGadsAssetsYesterday = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      setGadsAssetsYesterdayLoading(true);

      const { startSync } = useSyncStore.getState();
      startSync('GOOGLE_ADS');

      const res = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({ mode: 'yesterday-google-ads-assets' })
      });

      if (!res.ok) {
        throw new Error('Failed to start Google Ads asset tables sync');
      }

      toast.success('🚀 Started yesterday sync for GDN placements, product groups, and asset tables');
    } catch (error) {
      console.error('GAds assets yesterday fetch error:', error);
      toast.error(error.message || 'Failed to start asset tables sync');
    } finally {
      const { endSync } = useSyncStore.getState();
      endSync('GOOGLE_ADS');
      setGadsAssetsYesterdayLoading(false);
    }
  };

  // GA4 E-commerce Item Performance - Monthly Backfill (only this request)
  const handleEcomMonthlyBackfill = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      if (!ecomMonthlyStart || !ecomMonthlyEnd) {
        toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
        return;
      }
      if (ecomMonthlyStart > ecomMonthlyEnd) {
        toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
        return;
      }
      setEcomMonthlyLoading(true);
      setEcomMonthlyResult(null);
      const resp = await fetch('https://api.glowytics.com/api/google-analytics/ecommerce/monthly-backfill', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          startDate: ecomMonthlyStart,
          endDate: ecomMonthlyEnd
        })
      });
      const ct = resp.headers.get('content-type') || '';
      if (resp.status === 202) {
        const data = ct.includes('application/json') ? await resp.json().catch(() => ({})) : {};
        setEcomMonthlyResult({ ok: true, started: true, jobId: data.jobId, acceptedAt: data.acceptedAt });
        toast.success('Monthly backfill started. You can continue using the app.');
      } else if (ct.includes('application/json')) {
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || `Monthly backfill failed (${resp.status})`);
        setEcomMonthlyResult({ ok: true, completed: true, start_date: data.start_date, end_date: data.end_date });
        toast.success('GA4 E-commerce monthly backfill completed successfully.');
      } else {
        const text = await resp.text();
        throw new Error(`${resp.status} ${resp.statusText} – ${text.substring(0, 200)}`);
      }
    } catch (e) {
      console.error('E-commerce monthly backfill error:', e);
      toast.error(e.message);
    } finally {
      setEcomMonthlyLoading(false);
    }
  };

  // GA4 E-commerce Delete handler
  const handleEcomDelete = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { toast.error('Please select a brand first'); return; }
      if (!ecomDeleteStart || !ecomDeleteEnd) { toast.error('Please select start and end dates'); return; }
      if (ecomDeleteStart > ecomDeleteEnd) { toast.error('Start date must be before end date'); return; }
      if (ecomDeleteConfirm !== 'DELETE') { toast.error('Please type DELETE to confirm'); return; }
      setEcomDeleteLoading(true);
      setEcomDeleteResult(null);
      const resp = await fetch(`https://api.glowytics.com/api/brands/${brandId}/ecommerce-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDate: ecomDeleteStart, endDate: ecomDeleteEnd, confirmText: 'DELETE' })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `Delete failed (${resp.status})`);
      setEcomDeleteResult(data);
      setEcomDeleteConfirm('');
      toast.success(data.message || 'E-commerce data deleted');
    } catch (e) {
      console.error('E-commerce delete error:', e);
      toast.error(e.message);
    } finally {
      setEcomDeleteLoading(false);
    }
  };

  // GA4 E-commerce Refetch handler
  const handleEcomRefetch = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { toast.error('Please select a brand first'); return; }
      if (!ecomRefetchStart || !ecomRefetchEnd) { toast.error('Please select start and end dates'); return; }
      if (ecomRefetchStart > ecomRefetchEnd) { toast.error('Start date must be before end date'); return; }
      setEcomRefetchLoading(true);
      setEcomRefetchResult(null);
      const resp = await fetch(`https://api.glowytics.com/api/brands/${brandId}/ecommerce-refetch`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDate: ecomRefetchStart, endDate: ecomRefetchEnd })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `Refetch failed (${resp.status})`);
      setEcomRefetchResult(data);
      toast.success(data.message || 'E-commerce refetch started');
    } catch (e) {
      console.error('E-commerce refetch error:', e);
      toast.error(e.message);
    } finally {
      setEcomRefetchLoading(false);
    }
  };

  // NOTE: Email preview senders (temp) UI removed from Settings > Data Cleanup

  // Custom Date Fetch handler
  const handleFetchCustomDate = async () => {
    try {
      // Determine payload: prefer range if both provided, else single date
      const useRange = Boolean(customFetchStartDate && customFetchEndDate);
      const useSingle = Boolean(customFetchDate) && !useRange;

      if (!useRange && !useSingle) {
        toast.error('Please select a date or a date range');
        return;
      }

      if (useRange) {
        // basic validation
        if (new Date(customFetchStartDate) > new Date(customFetchEndDate)) {
          toast.error('Start date cannot be after end date');
          return;
        }
      }
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }
      setCustomFetchLoading(true);
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify((() => {
          const base = useRange
            ? { mode: 'date', startDate: customFetchStartDate, endDate: customFetchEndDate, tables: customSelectedTables }
            : { mode: 'date', targetDate: customFetchDate, tables: customSelectedTables };
          // Build connectionIds for multi-account platforms
          const cids = {};
          for (const p of MULTI_ACCOUNT_PLATFORMS) {
            const sel = selectedFetchAccounts[p];
            if (sel && !sel.includes('all') && sel.length > 0) {
              cids[p] = sel;
            }
          }
          if (Object.keys(cids).length > 0) base.connectionIds = cids;
          return base;
        })())
      });

      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json();
          if (errorData.error === 'SYNC_CONFLICT') {
            toast.warning(`⚠️ ${errorData.message}`);
            return;
          }
        }
        throw new Error('Failed to fetch custom date data');
      }

      if (useRange) {
        toast.success(`🚀 Custom date range sync started: ${customFetchStartDate} → ${customFetchEndDate}`);
      } else {
        toast.success(`🚀 Custom date sync started for ${customFetchDate}`);
      }
    } catch (error) {
      console.error('Error fetching custom date data:', error);
      toast.error('Failed to fetch custom date data');
    } finally {
      setCustomFetchLoading(false);
    }
  };

  // Meta Creative Mode handler
  const handleMetaCreativeMode = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }
      setMetaCreativeLoading(true);
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({ mode: 'meta-creative' })
      });
      if (!response.ok) {
        throw new Error('Failed to start Meta Creative mode');
      }
      toast.success('🎨 Meta Creative mode started (creative details, performance, mapping)');
    } catch (error) {
      console.error('Meta Creative mode error:', error);
      toast.error(error.message || 'Failed to start Meta Creative mode');
    } finally {
      setMetaCreativeLoading(false);
    }
  };

  // Meta Creative Yesterday Mode handler
  const handleMetaCreativeYesterdayMode = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }
      setMetaCreativeYesterdayLoading(true);
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({ mode: 'meta-creative-yesterday' })
      });
      if (!response.ok) {
        throw new Error('Failed to start Meta Creative (Yesterday)');
      }
      toast.success("🎨 Meta Creative (Yesterday) started for yesterday's data");
    } catch (error) {
      console.error('Meta Creative (Yesterday) error:', error);
      toast.error(error.message || 'Failed to start Meta Creative (Yesterday)');
    } finally {
      setMetaCreativeYesterdayLoading(false);
    }
  };

  // Meta Creative Custom Date/Range handler
  const handleMetaCreativeRunWithDate = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('No brand selected. Please select a brand first.');
        return;
      }
      const s = (metaCreativeStartDate || '').trim();
      const e = (metaCreativeEndDate || '').trim();
      if (!s && !e) {
        toast.error('Lütfen en az bir tarih seçin');
        return;
      }
      setMetaCreativeCustomLoading(true);
      const payload = { mode: 'meta-creative' };
      if (s && e) {
        payload.startDate = s;
        payload.endDate = e;
      } else {
        payload.targetDate = s || e;
      }
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('Failed to start Meta Creative (Date)');
      }
      if (s && e) {
        toast.success(`🎨 Meta Creative (Mapping) ${s} → ${e} için başlatıldı`);
      } else {
        const d = s || e;
        toast.success(`🎨 Meta Creative (Mapping) ${d} günü için başlatıldı`);
      }
    } catch (error) {
      console.error('Meta Creative (Date) error:', error);
      toast.error(error.message || 'Failed to start Meta Creative (Date)');
    } finally {
      setMetaCreativeCustomLoading(false);
    }
  };

  // Brand name state'ini izle
  useEffect(() => {
    console.log('brandName state updated:', brandName);
  }, [brandName]);

  // Brand detaylarını kaydet
  const handleSaveBrandDetails = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      console.log('Saving brand details...');

      const response = await fetch('https://api.glowytics.com/api/brands/' + brandId, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({ 
          name: brandName,
          websiteUrl,
          industry,
          timeZone,
          currency,
          logoUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update brand details');
      }

      toast.success('Brand details updated successfully');
    } catch (error) {
      console.error('Error updating brand details:', error);
      toast.error('Failed to update brand details');
    }
  };

  // Sayfa yüklendiğinde brand name'i doğrudan sol üstteki brand switcher'dan al
  useEffect(() => {
    // Sol üstteki brand switcher'dan brand adını al
    const brandSwitcherElement = document.querySelector('[data-testid="brand-switcher"]');
    if (brandSwitcherElement) {
      const brandSwitcherText = brandSwitcherElement.textContent || '';
      console.log('Brand switcher text:', brandSwitcherText);
      
      // brand05, brand07 gibi formatı al
      const brandNameMatch = brandSwitcherText.match(/brand\d+/);
      if (brandNameMatch) {
        const extractedBrandName = brandNameMatch[0];
        console.log('Extracted brand name from switcher:', extractedBrandName);
        setBrandName(extractedBrandName);
      }
    }
  }, []);

  // Schedule modalını göster
  const showScheduleModal = () => {
    setScheduleModalVisible(true);
    fetchSchedule();
  };

  // Mevcut zamanlamayı getir
  const fetchSchedule = async () => {
    try {
      setLoadingSchedule(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/schedule`, {
        headers: {
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schedule');
      }

      const data = await response.json();
      setCurrentSchedule(data);
      
      if (data && data.active) {
        scheduleForm.setFieldsValue({
          frequency: data.frequency,
          time: data.time ? moment(data.time, 'HH:mm') : null,
          active: data.active
        });
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      toast.error('Failed to fetch schedule');
    } finally {
      setLoadingSchedule(false);
    }
  };

  // Zamanlamayı kaydet
  const saveSchedule = async (values) => {
    try {
      setSavingSchedule(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      
      // Verileri backend'in beklediği formata dönüştür
      const scheduleData = {
        frequency: values.frequency,
        time: values.time.format('HH:mm'),
        active: values.active || false,
        dayOfWeek: values.frequency === 'weekly' ? 1 : null,
        dayOfMonth: values.frequency === 'monthly' ? 1 : null
      };
      
      console.log('Sending schedule data:', scheduleData);
      
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save schedule');
      }

      toast.success('Schedule saved successfully');
      setScheduleModalVisible(false);
      fetchSchedule(); // Refresh the schedule data
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error(error.message || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  // Add useEffect for clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  // Data source normalization function
  const normalizeDataSource = (dataSource) => {
    const sourceMap = {
      'GOOGLE_ADS': 'Google Ads',
      'GOOGLE_ANALYTICS': 'Google Analytics', 
      'META_ADS': 'Meta Ads',
      'ADJUST': 'Adjust',
      'FACEBOOK_ADS': 'Meta Ads', // Legacy support
      'TIKTOK_ADS': 'TikTok Ads',
      'CRITEO': 'Criteo',
      'RTB_HOUSE': 'RTB House',
      'PINTEREST_ADS': 'Pinterest'
    };
    
    return sourceMap[dataSource] || dataSource || 'Unknown';
  };

  // Add fetchDataSyncLogs function
  const fetchDataSyncLogs = async () => {
    try {
      setDataSyncLogsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setDataSyncLogsLoading(false);
        return;
      }

      const response = await fetch('https://api.glowytics.com/api/google-ads/data-sync-logs', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data sync logs');
      }

      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        // Debug: Log all unique data sources
        const uniqueDataSources = [...new Set(data.map(log => log.dataSource))];
        console.log('🔍 Unique data sources found:', uniqueDataSources);
        console.log('🔄 Normalized data sources:', uniqueDataSources.map(ds => `${ds} → ${normalizeDataSource(ds)}`));

        // Group by data source and date
        const groupedBySourceAndDate = data.reduce((acc, log) => {
          const rawSource = log.dataSource;
          const normalizedSource = normalizeDataSource(rawSource);
          const date = new Date(log.syncStartTime).toISOString().split('T')[0];
          
          console.log(`📊 Processing log: ${rawSource} → ${normalizedSource} (${date})`);
          
          if (!acc[normalizedSource]) {
            acc[normalizedSource] = {};
          }
          if (!acc[normalizedSource][date]) {
            acc[normalizedSource][date] = {
              totalSyncs: 0,
              successfulSyncs: 0,
              failedSyncs: 0,
              totalRows: 0
            };
          }
          
          acc[normalizedSource][date].totalSyncs++;
          if (log.syncStatus === 'SUCCESS') {
            acc[normalizedSource][date].successfulSyncs++;
          } else {
            acc[normalizedSource][date].failedSyncs++;
          }
          acc[normalizedSource][date].totalRows += (log.rowsProcessed || 0);
          
          return acc;
        }, {});

        // Log metrics by source and date
        Object.entries(groupedBySourceAndDate).forEach(([source, dateGroups]) => {
          console.log(`\n=== ${source} ===`);
          
          Object.entries(dateGroups).forEach(([date, metrics]) => {
            console.log(`\nDate: ${date}`);
            console.log(`Toplam sync sayısı: ${metrics.totalSyncs}`);
            console.log(`Başarılı sync sayısı: ${metrics.successfulSyncs}`);
            console.log(`Başarısız sync sayısı: ${metrics.failedSyncs}`);
            console.log(`İşlenen toplam satır sayısı: ${metrics.totalRows.toLocaleString()}`);
          });

          // Calculate totals for this source
          const sourceTotals = Object.values(dateGroups).reduce((totals, dayMetrics) => ({
            totalSyncs: totals.totalSyncs + dayMetrics.totalSyncs,
            successfulSyncs: totals.successfulSyncs + dayMetrics.successfulSyncs,
            failedSyncs: totals.failedSyncs + dayMetrics.failedSyncs,
            totalRows: totals.totalRows + dayMetrics.totalRows
          }), { totalSyncs: 0, successfulSyncs: 0, failedSyncs: 0, totalRows: 0 });

          console.log(`\n${source} Toplam İstatistikler:`);
          console.log(`Toplam sync sayısı: ${sourceTotals.totalSyncs}`);
          console.log(`Başarılı sync sayısı: ${sourceTotals.successfulSyncs}`);
          console.log(`Başarısız sync sayısı: ${sourceTotals.failedSyncs}`);
          console.log(`İşlenen toplam satır sayısı: ${sourceTotals.totalRows.toLocaleString()}`);
        });
      } else {
        console.log('No data sync logs found');
      }
      
      setDataSyncLogs(data);
    } catch (error) {
      console.error('Error fetching data sync logs:', error);
    } finally {
      setDataSyncLogsLoading(false);
    }
  };

  // Fetch auto sync schedule status
  const fetchAutoSyncStatus = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const response = await fetch('https://api.glowytics.com/api/data-sync-logs/schedule', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAutoSyncEnabled(data.active || false);
      }
    } catch (error) {
      console.error('Error fetching auto sync status:', error);
    }
  };

  // Update auto sync toggle
  const handleAutoSyncToggle = async (enabled) => {
    try {
      setAutoSyncLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }

      // Determine active platform table patterns for auto sync
      const getActiveTablesForAutoSync = () => {
        const patterns = [];
        if (connections?.GOOGLE_ADS?.status === 'ACTIVE') patterns.push('google_ads_*');
        if (connections?.META_ADS?.status === 'ACTIVE') patterns.push('meta_*');
        if (connections?.GOOGLE_ANALYTICS?.status === 'ACTIVE') patterns.push('ga_*');
        if (connections?.SEARCH_CONSOLE?.status === 'ACTIVE') patterns.push('search_console_*');
        if (connections?.MERCHANT_CENTER?.status === 'ACTIVE') patterns.push('google_mc_*');
        if (connections?.ADJUST?.status === 'ACTIVE') patterns.push('adjust_*');
        if (connections?.SHOPIFY?.status === 'ACTIVE') patterns.push('shopify_*');
        if (connections?.IKAS?.status === 'ACTIVE') patterns.push('ikas_*');
        if (connections?.CRITEO_ADS?.status === 'ACTIVE') patterns.push('criteo_*');
        return patterns;
      };
      const activeTables = getActiveTablesForAutoSync();

      // Define schedules for daily process (night, morning, noon, evening, late-night)
      const schedules = [
        {
          frequency: 'daily',
          time: '00:05', // Night sync - so users see data before morning
          active: enabled,
          mode: 'yesterday',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '07:50', // Pre-morning cleanup before 08:00 fresh fetch
          active: enabled,
          mode: 'cleanup',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '08:00', // Morning sync (yesterday)
          active: enabled,
          mode: 'yesterday',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '12:00', // Cleanup (Turkey time)
          active: enabled,
          mode: 'cleanup',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '12:05', // Fresh sync after cleanup
          active: enabled,
          mode: 'yesterday',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '17:00', // Evening cleanup (Turkey time)
          active: enabled,
          mode: 'cleanup',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '17:05', // Fresh sync after evening cleanup
          active: enabled,
          mode: 'yesterday',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '22:30', // Late-night cleanup (Turkey time)
          active: enabled,
          mode: 'cleanup',
          tables: activeTables
        },
        {
          frequency: 'daily',
          time: '22:35', // Fresh sync after late-night cleanup
          active: enabled,
          mode: 'yesterday',
          tables: activeTables
        }
      ];

      // Send the request to a new bulk endpoint
      const response = await fetch('https://api.glowytics.com/api/data-sync-logs/bulk-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          schedules,
          active: enabled,
          // extras: allow backend to know which optional integrations exist
          extras: {
            ikas: connections?.IKAS?.status === 'ACTIVE',
            shopify: connections?.SHOPIFY?.status === 'ACTIVE',
            adjust: connections?.ADJUST?.status === 'ACTIVE',
            search_console: connections?.SEARCH_CONSOLE?.status === 'ACTIVE',
            merchant_center: connections?.MERCHANT_CENTER?.status === 'ACTIVE',
            criteo: connections?.CRITEO_ADS?.status === 'ACTIVE'
          }
        }) // Send all schedules at once with explicit platform scope
      });

      if (!response.ok) {
        throw new Error('Failed to update auto sync settings');
      }

      setAutoSyncEnabled(enabled);
      
      if (enabled) {
        toast.success(`Auto sync enabled (00:05, 08:00, 12:05, 17:05, 22:35 fetch + cleanups)`);
      } else {
        toast.success('Auto sync disabled');
      }
    } catch (error) {
      console.error('Error updating auto sync:', error);
      toast.error('Failed to update auto sync setting');
    } finally {
      setAutoSyncLoading(false);
    }
  };

  const fetchBrandUsers = async () => {
    try {
      setUsersLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/users`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setBrandUsers(data.users || []);
    } catch (error) {
      console.error('Fetch brand users error:', error);
      toast.error('Could not load users for the brand.');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'user-permissions') {
      fetchBrandUsers();
    }
  }, [activeSection]);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        body: JSON.stringify(inviteUserForm),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to invite user.");
      }
      
      toast.success('User invited successfully!');
      setIsInviteUserModalOpen(false);
      setInviteUserForm({ email: '', role: 'VIEWER' });
      fetchBrandUsers(); // Refresh list

    } catch (error) {
      console.error('Invite user error:', error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = async (newRole, userToUpdate) => {
    const mevcutDeger = rolSecimDegeri(userToUpdate);
    if (mevcutDeger === newRole) return;

    setIsSubmitting(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/users/${userToUpdate.accessId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update role.");
      }

      toast.success(`Role for ${userToUpdate.email} updated to ${newRole}`);
      fetchBrandUsers();

    } catch (error) {
      console.error('Update role error:', error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!userToModify) return;
    setIsSubmitting(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/users/${userToModify.accessId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to remove user.");
      }

      toast.success(`${userToModify.email} has been removed from the brand.`);
      setIsRemoveUserModalOpen(false);
      setUserToModify(null);
      fetchBrandUsers();

    } catch (error)      {
      console.error('Remove user error:', error);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🧹 DATA CLEANUP FUNCTIONS
  const handleDataCleanup = async () => {
    const useRange = Boolean(cleanupStartDate && cleanupEndDate);
    const useSingle = Boolean(cleanupDate) && !useRange;

    if (!useRange && !useSingle) {
      toast.error('Please select a date or a date range');
      return;
    }

    if (useRange) {
      if (new Date(cleanupStartDate) > new Date(cleanupEndDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!cleanupSelectedTables || cleanupSelectedTables.length === 0) {
      toast.error('Please select at least one platform to clean');
      return;
    }

    setCleanupLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');

      // If range not supported by backend, iterate per day and aggregate results
      if (useRange) {
        const start = new Date(`${cleanupStartDate}T00:00:00Z`);
        const end = new Date(`${cleanupEndDate}T00:00:00Z`);
        let cursor = new Date(start);

        let totalDeletedRows = 0;
        let tablesProcessed = 0;
        const aggregatedResults = [];
        const failedDates = [];

        while (cursor <= end) {
          const currentDate = new Date(cursor).toISOString().split('T')[0];
          try {
            const resp = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-Brand-ID': brandId
              },
              body: JSON.stringify({ date: currentDate, confirmText, tables: cleanupSelectedTables })
            });
            if (!resp.ok) {
              const err = await resp.json().catch(() => ({}));
              throw new Error(err.error || `Cleanup failed for ${currentDate}`);
            }
            const dayResult = await resp.json();
            const summary = dayResult.summary || {};
            totalDeletedRows += summary.totalDeletedRows || 0;
            tablesProcessed += summary.tablesProcessed || 0;
            if (Array.isArray(summary.results)) {
              aggregatedResults.push(...summary.results);
            }
          } catch (e) {
            failedDates.push(currentDate);
          }

          // advance one day (UTC safe)
          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }

        const combinedSummary = {
          totalDeletedRows,
          tablesProcessed,
          date: `${cleanupStartDate} → ${cleanupEndDate}`,
          results: aggregatedResults,
          failedDates
        };

        setCleanupResults(combinedSummary);
        if (failedDates.length > 0) {
          toast.success(`Cleaned ${totalDeletedRows} rows for range with partial failures (${failedDates.length} days).`);
        } else {
          toast.success(`Successfully cleaned ${totalDeletedRows} rows for ${cleanupStartDate} → ${cleanupEndDate}`);
        }
      } else {
        const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': brandId
          },
          body: JSON.stringify({ date: cleanupDate, confirmText, tables: cleanupSelectedTables })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to cleanup data');
        }

        const result = await response.json();
        setCleanupResults(result.summary);
        toast.success(`Successfully cleaned ${result.summary.totalDeletedRows} rows for ${cleanupDate}`);
      }

      // Reset form
      setCleanupDate('');
      setCleanupStartDate('');
      setCleanupEndDate('');
      setConfirmText('');

    } catch (error) {
      console.error('Data cleanup error:', error);
      toast.error(error.message);
    } finally {
      setCleanupLoading(false);
    }
  };

  const resetCleanupModal = () => {
    setCleanupDate('');
    setCleanupStartDate('');
    setCleanupEndDate('');
    setConfirmText('');
    setCleanupResults(null);
    setIsCleanupModalOpen(false);
  };

  // 🔥 DELETE ALL DATA FUNCTIONS - EXTREMELY DANGEROUS!

  const handleDeleteAllData = async () => {
    if (deleteAllConfirmText !== 'Deletemyalldata') {
      toast.error('Please type "Deletemyalldata" exactly to confirm this dangerous operation');
      return;
    }

    setDeleteAllLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected');
      }

      // Use unified cleanup endpoint with explicit platform table patterns
      // to ensure ALL platform data is removed (including Adjust tables).
      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          all: true,
          confirmText: 'DELETE',
          // Clear all supported platform tables explicitly
          tables: ['google_ads_*', 'meta_*', 'ga_*', 'search_console_*', 'google_mc_*', 'adjust_*', 'shopify_*', 'ikas_*', 'criteo_*', 'rtb_house_*', 'pinterest_*']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete all platform data');
      }

      const result = await response.json();
      setDeleteAllResults(result);
      
      toast.success(`Successfully deleted all platform data! ${result.totalTablesProcessed} tables processed, ${result.totalRowsDeleted} total rows deleted.`);
      
      // Reset text after successful deletion
      setDeleteAllConfirmText('');
      
    } catch (error) {
      console.error('Delete all data error:', error);
      toast.error(error.message);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const resetDeleteAllModal = () => {
    setDeleteAllConfirmText('');
    setDeleteAllResults(null);
  };

  const handleCleanupYesterday = async () => {
    setYesterdayCleanupLoading(true);
    setYesterdayCleanupResults(null);
    
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected');
      }

      // Calculate yesterday's date
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0]; // YYYY-MM-DD format

      console.log(`Cleaning yesterday's data: ${yesterdayStr}`);

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          date: yesterdayStr,
          confirmText: 'DELETE',
          // Always clean all supported platform tables in "Quick Yesterday Cleanup"
          // (this section is intended to mimic the daily automatic cleanup).
          tables: ['google_ads_*', 'meta_*', 'ga_*', 'search_console_*', 'google_mc_*', 'adjust_*', 'shopify_*', 'ikas_*', 'criteo_*', 'rtb_house_*', 'pinterest_*']
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cleanup yesterday data');
      }

      const result = await response.json();
      setYesterdayCleanupResults(result);
      
      toast.success(`Successfully cleaned yesterday's data! ${result.summary.totalDeletedRows} rows deleted from ${result.summary.tablesProcessed} tables.`);
      
    } catch (error) {
      console.error('Yesterday cleanup error:', error);
      toast.error(error.message);
    } finally {
      setYesterdayCleanupLoading(false);
    }
  };

  // Clean Meta Creative (Yesterday)
  const handleCleanupMetaCreativeYesterday = async () => {
    setMetaCreativeCleanupLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected');
      }

      // Calculate yesterday
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const target = y.toISOString().split('T')[0];

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          date: target,
          confirmText: 'DELETE',
          scope: 'meta-creative'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cleanup Meta Creative data');
      }

      const result = await response.json();
      toast.success(`Cleaned Meta Creative tables for ${target}`);
    } catch (error) {
      console.error('Meta Creative cleanup error:', error);
      toast.error(error.message);
    } finally {
      setMetaCreativeCleanupLoading(false);
    }
  };

  // Clean Meta Creative (ALL)
  const handleCleanupMetaCreativeAll = async () => {
    setMetaCreativeCleanupAllLoading(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('No brand selected');
      }

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          all: true,
          confirmText: 'DELETE',
          scope: 'meta-creative'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cleanup ALL Meta Creative data');
      }

      const result = await response.json();
      toast.success('Cleaned ALL Meta Creative tables');
    } catch (error) {
      console.error('Meta Creative cleanup (ALL) error:', error);
      toast.error(error.message);
    } finally {
      setMetaCreativeCleanupAllLoading(false);
    }
  };

  // NOTE: "Fetch Adjust Only" UI was removed to simplify the Data Cleanup page.



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img 
            src="/logoicon.png" 
            alt="Glowytics Logo" 
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  if (selectedBrand?.currentUserRole === 'MARKETER') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="p-6">
        {/* Saat ve tarih bilgisini buraya ekleyelim */}
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Settings</h2>
          <div className="text-gray-600 text-right">
            <div className="text-xl font-semibold">{formatTime(currentTime)}</div>
            <div className="text-sm">{formatDate(currentTime)}</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'brand-details' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('brand-details')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </span>
            <span>Brand Details</span>
          </button>
          
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'api-connections' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('api-connections')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Link2 className="w-4 h-4" />
            </span>
            <span>Platform Connections</span>
          </button>
          
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'notifications' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('notifications')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Bell className="w-4 h-4" />
            </span>
            <span>Notifications</span>
          </button>
          
          {selectedBrand?.id !== 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4' && (
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'user-permissions' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('user-permissions')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Users2 className="w-4 h-4" />
            </span>
            <span>User Permissions</span>
          </button>
          )}
          
          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'data-health' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('data-health')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </span>
            <span>Data Health</span>
          </button>

          <button 
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'data-sync-logs' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('data-sync-logs')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Database className="w-4 h-4" />
            </span>
            <span>Data Sync Logs</span>
          </button>
          
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'data-cleanup' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('data-cleanup')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <RefreshCw className="w-4 h-4" />
            </span>
            <span>Data Cleanup</span>
          </button>

          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeSection === 'monthly-targets' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'} font-medium`}
            onClick={() => handleSectionChange('monthly-targets')}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <Calendar className="w-4 h-4" />
            </span>
            <span>Monthly Targets</span>
          </button>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl border p-6">
          {activeSection === 'brand-details' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium">Brand Details</h3>
                  <p className="text-sm text-gray-500 mt-1">Configure your brand information and preferences</p>
                </div>
                <Button 
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-4"
                  onClick={handleSaveBrandDetails}
                >
                  Save Changes
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-5">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Brand Identity</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Brand Logo</label>
                        <div className="mt-1 flex items-center">
                          <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center border border-gray-300">
                            {logoUrl ? (
                              <img src={logoUrl} alt="Brand Logo" className="w-full h-full object-contain rounded-xl" />
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-4">
                            <input
                              type="url"
                              value={logoUrl}
                              onChange={(e) => setLogoUrl(e.target.value)}
                              className="px-3 py-1.5 border border-gray-300 rounded-xl text-sm w-64"
                              placeholder="Enter logo URL"
                            />
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">Recommended size: 512x512px. PNG or JPG format.</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                        <input 
                          type="text" 
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" 
                          placeholder="Enter brand name"
                          value={brandName}
                          onChange={(e) => setBrandName(e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                        <input 
                          type="url" 
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" 
                          placeholder="https://example.com"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                        <select 
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={industry}
                          onChange={(e) => setIndustry(e.target.value)}
                        >
                          <option value="">Select industry</option>
                          <optgroup label="E-commerce & Retail">
                            <option value="E-commerce - Fashion & Apparel">E-commerce - Fashion & Apparel</option>
                            <option value="E-commerce - Electronics">E-commerce - Electronics</option>
                            <option value="E-commerce - Home & Garden">E-commerce - Home & Garden</option>
                            <option value="E-commerce - Beauty & Cosmetics">E-commerce - Beauty & Cosmetics</option>
                            <option value="E-commerce - Sports & Outdoors">E-commerce - Sports & Outdoors</option>
                            <option value="E-commerce - Toys & Games">E-commerce - Toys & Games</option>
                            <option value="E-commerce - Books & Media">E-commerce - Books & Media</option>
                            <option value="E-commerce - Pet Supplies">E-commerce - Pet Supplies</option>
                            <option value="E-commerce - Jewelry & Accessories">E-commerce - Jewelry & Accessories</option>
                            <option value="E-commerce - Health & Wellness">E-commerce - Health & Wellness</option>
                            <option value="E-commerce - Marketplace">E-commerce - Marketplace</option>
                            <option value="E-commerce - Subscription Box">E-commerce - Subscription Box</option>
                            <option value="Retail - Physical Stores">Retail - Physical Stores</option>
                            <option value="Retail - Omnichannel">Retail - Omnichannel</option>
                          </optgroup>
                          <optgroup label="Technology">
                            <option value="SaaS - B2B">SaaS - B2B</option>
                            <option value="SaaS - B2C">SaaS - B2C</option>
                            <option value="Software Development">Software Development</option>
                            <option value="Mobile Apps">Mobile Apps</option>
                            <option value="Gaming">Gaming</option>
                            <option value="Cybersecurity">Cybersecurity</option>
                            <option value="AI & Machine Learning">AI & Machine Learning</option>
                            <option value="Cloud Services">Cloud Services</option>
                          </optgroup>
                          <optgroup label="Services">
                            <option value="Finance & Banking">Finance & Banking</option>
                            <option value="Insurance">Insurance</option>
                            <option value="Healthcare & Medical">Healthcare & Medical</option>
                            <option value="Education & E-learning">Education & E-learning</option>
                            <option value="Real Estate">Real Estate</option>
                            <option value="Legal Services">Legal Services</option>
                            <option value="Consulting">Consulting</option>
                            <option value="Marketing Agency">Marketing Agency</option>
                          </optgroup>
                          <optgroup label="Consumer">
                            <option value="Travel & Tourism">Travel & Tourism</option>
                            <option value="Hotels & Hospitality">Hotels & Hospitality</option>
                            <option value="Food & Beverage">Food & Beverage</option>
                            <option value="Restaurants">Restaurants</option>
                            <option value="Entertainment">Entertainment</option>
                            <option value="Media & Publishing">Media & Publishing</option>
                            <option value="Automotive">Automotive</option>
                            <option value="Transportation">Transportation</option>
                          </optgroup>
                          <optgroup label="Other">
                            <option value="Non-Profit">Non-Profit</option>
                            <option value="Government">Government</option>
                            <option value="Manufacturing">Manufacturing</option>
                            <option value="Agriculture">Agriculture</option>
                            <option value="Energy & Utilities">Energy & Utilities</option>
                            <option value="Construction">Construction</option>
                            <option value="Other">Other</option>
                          </optgroup>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-5">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Regional Settings</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
                        <select 
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={timeZone}
                          onChange={(e) => setTimeZone(e.target.value)}
                        >
                          <option value="">Select time zone</option>
                          <option value="UTC">UTC</option>
                          <option value="Europe/Istanbul">Europe/Istanbul</option>
                          <option value="Europe/London">Europe/London</option>
                          <option value="Europe/Paris">Europe/Paris</option>
                          <option value="Europe/Berlin">Europe/Berlin</option>
                          <option value="America/New_York">America/New York</option>
                          <option value="America/Chicago">America/Chicago</option>
                          <option value="America/Los_Angeles">America/Los Angeles</option>
                          <option value="Asia/Dubai">Asia/Dubai</option>
                          <option value="Asia/Singapore">Asia/Singapore</option>
                          <option value="Asia/Tokyo">Asia/Tokyo</option>
                          <option value="Australia/Sydney">Australia/Sydney</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                        <select 
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                        >
                          <option value="">Select currency</option>
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                          <option value="TRY">TRY - Turkish Lira</option>
                          <option value="AED">AED - UAE Dirham</option>
                          <option value="CAD">CAD - Canadian Dollar</option>
                          <option value="AUD">AUD - Australian Dollar</option>
                          <option value="JPY">JPY - Japanese Yen</option>
                          <option value="CNY">CNY - Chinese Yuan</option>
                          <option value="INR">INR - Indian Rupee</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSection === 'api-connections' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium">Platform Connections</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage your connections to external platforms and services</p>
                </div>
          <div className="flex items-center gap-4">
            {/* Auto Sync Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Auto Sync</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={autoSyncEnabled}
                  onChange={(e) => handleAutoSyncToggle(e.target.checked)}
                  disabled={autoSyncLoading}
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${autoSyncLoading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
              </label>
              {autoSyncLoading && (
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          </div>
              </div>
              {/* Grouped small cards without filters */}
              {[
                { id: 'Ads', title: 'Ads Platforms' },
                { id: 'Analytics', title: 'Analytics' },
                { id: 'Commerce', title: 'Commerce' },
                { id: 'SearchListing', title: 'Search & Listing' },
                { id: 'Presence', title: 'Business Presence' }
              ].map(group => {
                const items = platforms.filter(p => p.category === group.id);
                if (items.length === 0) return null;
                return (
                  <div key={group.id} className="space-y-3 mb-6">
                    <h4 className="text-sm font-semibold text-gray-700">{group.title}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
                      {items.map((platform) => {
                        // For META_ADS, check if ANY Meta-related connection exists
                        const META_PLATFORMS = ['META_ADS', 'META_CATALOG', 'FACEBOOK_PAGE', 'INSTAGRAM', 'META_PIXEL'];
                        const isConnected = platform.platform_key === 'META_ADS'
                          ? META_PLATFORMS.some(p => connections[p]?.status === 'ACTIVE')
                          : connections[platform.platform_key] && connections[platform.platform_key].status === 'ACTIVE';
                        const isComingSoon = platform.isComingSoon;
                        const isMultiAccountPlatform = isMultiAccountFor(localStorage.getItem('currentBrandId'), platform.platform_key);
                        const platformAccounts = multiAccounts[platform.platform_key] || [];
                        const isLoadingMulti = multiAccountsLoading[platform.platform_key];
                        return (
                          <div key={platform.name} className="p-4 border rounded-xl bg-white hover:shadow-sm transition">
                            <div className="flex items-start gap-3">
                              {platform.customIcon === 'bigquery' ? (
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                  <Database className="w-4 h-4 text-blue-600" />
                                </div>
                              ) : (
                                <img
                                  src={platform.icon}
                                  alt={`${platform.name} icon`}
                                  className={`w-8 h-8 object-contain ${platform.platform_key === 'IKAS' ? 'rounded-lg' : ''}`}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium text-gray-900 text-sm">{platform.name}</h5>
                                    {isConnected && !isMultiAccountPlatform && (
                                      <>
                                        {platform.platform_key === 'META_ADS' ? (
                                          <div className="text-[11px] text-gray-600 mt-1 space-y-0.5">
                                            {/* Show connected Meta assets */}
                                            {connections['META_ADS']?.status === 'ACTIVE' && (
                                              <div className="truncate w-full">
                                                Ads Account: {connections['META_ADS']?.accountName || 'Connected'}
                                              </div>
                                            )}
                                            {connections['FACEBOOK_PAGE']?.status === 'ACTIVE' && (
                                              <div className="truncate w-full text-gray-500">
                                                Page: {connections['FACEBOOK_PAGE']?.accountName || 'Connected'}
                                              </div>
                                            )}
                                            {connections['INSTAGRAM']?.status === 'ACTIVE' && (
                                              <div className="truncate w-full text-gray-500">
                                                Instagram: {connections['INSTAGRAM']?.accountName || 'Connected'}
                                              </div>
                                            )}
                                            {connections['META_CATALOG']?.status === 'ACTIVE' && (
                                              <div className="truncate w-full text-gray-500">
                                                Catalog: {connections['META_CATALOG']?.accountName || 'Connected'}
                                              </div>
                                            )}
                                            {connections['META_PIXEL']?.status === 'ACTIVE' && (
                                              <div className="truncate w-full text-gray-500">
                                                Pixel: {connections['META_PIXEL']?.accountName || 'Connected'}
                                              </div>
                                            )}
                                          </div>
                                        ) : connections[platform.platform_key] ? (
                                          <div className="text-[11px] text-gray-500 mt-1 truncate w-full">
                                            {connections[platform.platform_key].accountName ||
                                          connections[platform.platform_key].name ||
                                          connections[platform.platform_key].account_name ||
                                             connections[platform.platform_key].displayName}
                                      </div>
                                        ) : null}
                                      </>
                                    )}
                                    {platform.platform_key === 'META_ADS' && isConnected && !isMultiAccountPlatform && (connections['META_ADS']?.expiresAt || connections['FACEBOOK_PAGE']?.expiresAt) && (
                                      <div className="mt-1 text-[11px] text-slate-600">
                                        <span className="bg-slate-100 px-2 py-0.5 rounded-md">
                                          Expires: {new Date(connections['META_ADS']?.expiresAt || connections['FACEBOOK_PAGE']?.expiresAt).toLocaleDateString()}
                                        </span>
                                      </div>
                                    )}

                                  </div>
                                  <div className="flex flex-col items-end gap-1 ml-3 shrink-0">
                                    {isComingSoon ? (
                                      <span className="px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                                        {platform.comingSoonText || 'Coming Soon'}
                                      </span>
                                    ) : isMultiAccountPlatform ? (
                                      /* Multi-account: show "Add Account" button */
                                      <button
                                        onClick={() => handleAddAccount(platform.platform_key)}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center gap-1"
                                      >
                                        <Plus className="w-3 h-3" />
                                        Add Account
                                      </button>
                                    ) : (
                                      <>
                                        {isConnected ? (
                                          <>
                                            <button
                                              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 cursor-default"
                                              disabled
                                            >
                                              Connected
                                            </button>
                                            {platform.platform_key === 'CRITEO_ADS' && (
                                              <button
                                                onClick={openCriteoAccountPicker}
                                                className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100"
                                              >
                                                Change account
                                              </button>
                                            )}
                                            <button
                                              onClick={() => handleDisconnect(platform)}
                                              className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100"
                                            >
                                              Disconnect
                                            </button>
                                            {/* Legacy "Update Access Token" removed */}
                                          </>
                                        ) : (
                                          <button
                                            onClick={() => {
                                              if (platform.platform_key === 'META_ADS') {
                                                // Use OAuth flow (same as Development "Facebook ile giriş yap")
                                                handleMetaOAuthStart();
                                              } else {
                                                handleConnect(platform);
                                              }
                                            }}
                                            className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center gap-1.5"
                                          >
                                            {platform.platform_key === 'META_ADS' && (
                                              <img
                                                src="/facebook.svg"
                                                alt="Facebook"
                                                className="w-3.5 h-3.5"
                                              />
                                            )}
                                            <span>
                                              {platform.platform_key === 'META_ADS'
                                                ? 'Connect with Facebook'
                                                : 'Connect'}
                                            </span>
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Multi-account: connected accounts list */}
                            {isMultiAccountPlatform && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                {isLoadingMulti ? (
                                  <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading accounts...
                                  </div>
                                ) : platformAccounts.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic py-1">
                                    No connected accounts yet.
                                  </p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {platformAccounts.map(acc => (
                                      <div key={acc.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-gray-50 hover:bg-white hover:shadow-sm transition-all text-xs">
                                        {/* Account info */}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            <span className="font-medium text-gray-800 truncate">
                                              {acc.nickname || acc.accountName || acc.customerId || 'Account'}
                                            </span>
                                          </div>
                                          {acc.customerId && (
                                            <p className="text-[10px] text-gray-400">ID: {acc.customerId}</p>
                                          )}
                                        </div>
                                        {/* Remove */}
                                        {confirmDeleteAccId === acc.id ? (
                                          <div className="flex items-center gap-1 flex-shrink-0">
                                            <span className="text-[10px] text-red-600">Remove?</span>
                                            <button
                                              onClick={() => handleRemoveAccount(acc.id, platform.platform_key)}
                                              disabled={deletingAccId === acc.id}
                                              className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 rounded flex items-center gap-0.5"
                                            >
                                              {deletingAccId === acc.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Yes'}
                                            </button>
                                            <button
                                              onClick={() => setConfirmDeleteAccId(null)}
                                              className="px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:text-gray-800 rounded"
                                            >
                                              No
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => setConfirmDeleteAccId(acc.id)}
                                            title="Remove account"
                                            className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* ── Glowytics Pixel Section (dev-only: efe@heyglowy.com) ── */}
              {currentUser?.email === 'efe@heyglowy.com' && <GlowyticsPixelSection />}
            </div>
          )}
          
          {activeSection === 'notifications' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium">Email & App Push Notifications</h3>
                  <p className="text-sm text-gray-500 mt-1">Configure daily/weekly/monthly reports and alerts</p>
                </div>
                <Button className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl" onClick={handleSaveNotificationSettings} disabled={notifSaving || notifLoading}>
                  {notifSaving ? 'Saving…' : 'Save Preferences'}
                </Button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Select Notifications</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                      <div>
                        <p className="font-medium">Performance Report — Daily</p>
                        <p className="text-sm text-gray-500">Daily summary of cost, sessions, revenue</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={notif.daily} onChange={(e) => setNotif(v => ({ ...v, daily: e.target.checked }))} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                      <div>
                        <p className="font-medium">Performance Report — Weekly</p>
                        <p className="text-sm text-gray-500">Last week vs previous week comparison</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={notif.weekly} onChange={(e) => setNotif(v => ({ ...v, weekly: e.target.checked }))} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                      <div>
                        <p className="font-medium">Performance Report — Monthly</p>
                        <p className="text-sm text-gray-500">Last month vs previous month performance</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={notif.monthly} onChange={(e) => setNotif(v => ({ ...v, monthly: e.target.checked }))} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                      <div>
                        <p className="font-medium">Anomaly Alerts</p>
                        <p className="text-sm text-gray-500">Sharp drops or spikes in key metrics</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={notif.anomaly} onChange={(e) => setNotif(v => ({ ...v, anomaly: e.target.checked }))} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                      <div>
                        <p className="font-medium">Revenue Alerts</p>
                        <p className="text-sm text-gray-500">Get notified when no revenue is received for 2+ hours</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={notif.revenueAlert} onChange={(e) => setNotif(v => ({ ...v, revenueAlert: e.target.checked }))} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSection === 'user-permissions' && selectedBrand?.id !== 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium">User Permissions</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage user roles and access permissions for your organization</p>
                </div>
                <Button 
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => setIsInviteUserModalOpen(true)}
                >
                  Invite User
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                {usersLoading ? (
                  <div className="flex justify-center items-center p-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {brandUsers.map(user => {
                        const isCurrentUser = user.id === currentUser?.id;
                        const isOwner = user.role === 'OWNER';
                        
                        // Corrected logic for modification permissions
                        const loggedInUserRole = selectedBrand?.currentUserRole;
                        const canModify = loggedInUserRole === 'OWNER' || 
                                          (loggedInUserRole === 'ADMIN' && user.role !== 'ADMIN' && user.role !== 'OWNER');

                        return (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{user.name || 'Unnamed User'} {isCurrentUser && '(You)'}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isOwner ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Owner
                                </span>
                              ) : (
                                <select 
                                  className="text-sm border-gray-300 rounded-md"
                                  value={rolSecimDegeri(user)}
                                  onChange={(e) => handleRoleChange(e.target.value, user)}
                                  disabled={!canModify || isSubmitting}
                                >
                                  <option value="ADMIN">Admin</option>
                                  <option value="ANALYST">Analyst</option>
                                  {MARKETER_BRAND_IDS.includes(selectedBrand?.id) && (
                                    <option value="MARKETER">Marketer</option>
                                  )}
                                  <option value="BRANDING">Branding</option>
                                  <option value="VIEWER">Viewer</option>
                                  <option value="FINANCE">Finance (yalnızca faturalama)</option>
                                  <option value="GBP">GBP (yalnızca lokasyonlar)</option>
                                </select>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {!isOwner && canModify && (
                                <button 
                                  onClick={() => {
                                    setUserToModify(user);
                                    setIsRemoveUserModalOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                  disabled={isSubmitting}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
          
          {activeSection === 'data-health' && (
            <div>
              <DataHealth embedded={true} />
            </div>
          )}
          
          {activeSection === 'data-sync-logs' && (
            <div>
             
              
              <DataSyncLogs />
            </div>
          )}
          
          {activeSection === 'data-cleanup' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium">Data Cleanup</h3>
                  <p className="text-sm text-gray-500 mt-1">Clean up data for specific dates across all platforms</p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Removed: Test Auto Sync (Yesterday Mode) */}
                  {/* IKAS quick tools under Data Cleanup */}
                  {false && (<>
                  <Button
                    className={`rounded-xl px-4 ${ikasBackfillLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                    onClick={async () => {
                      if (ikasBackfillLoading) return;
                      setIkasBackfillLoading(true);
                      setIkasBackfillResult(null);
                      try {
                        const brandId = localStorage.getItem('currentBrandId');
                        if (!brandId) throw new Error('No brand selected');
                        const res = await fetch('https://api.glowytics.com/api/labs/ikas/backfill', {
                          method: 'POST',
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-Brand-ID': brandId
                          },
                          body: JSON.stringify({ days: 10 })
                        });
                        const ct = res.headers.get('content-type') || '';
                        if (ct.includes('application/json')) {
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || `IKAS backfill failed (${res.status})`);
                          setIkasBackfillResult(data);
                          const inserted = (data.rows_inserted ?? data.rowsInserted ?? data.rows) || 0;
                          toast.success(`IKAS backfill tamam: ${inserted} satır, ${data.startDate} → ${data.endDate}`);
                        } else {
                          const text = await res.text();
                          throw new Error(`${res.status} ${res.statusText} – ${text.substring(0, 200)}`);
                        }
                      } catch (e) {
                        console.error('IKAS backfill error:', e);
                        toast.error(e.message);
                      } finally {
                        setIkasBackfillLoading(false);
                      }
                    }}
                  >
                    {ikasBackfillLoading ? 'IKAS Backfilling…' : 'IKAS: Backfill Last 10 Days'}
                  </Button>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      className="border rounded-md px-2 py-1 text-sm"
                      value={ikasRangeStart}
                      onChange={(e) => setIkasRangeStart(e.target.value)}
                      aria-label="IKAS start date"
                    />
                    <span className="text-sm text-gray-600">→</span>
                    <input
                      type="date"
                      className="border rounded-md px-2 py-1 text-sm"
                      value={ikasRangeEnd}
                      onChange={(e) => setIkasRangeEnd(e.target.value)}
                      aria-label="IKAS end date"
                    />
                    <Button
                      className={`rounded-xl px-3 ${ikasRangeLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-700 text-white hover:bg-emerald-800'}`}
                      onClick={async () => {
                        if (ikasRangeLoading) return;
                        if (!ikasRangeStart || !ikasRangeEnd) {
                          toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                          return;
                        }
                        if (ikasRangeStart > ikasRangeEnd) {
                          toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
                          return;
                        }
                        setIkasRangeLoading(true);
                        try {
                          const brandId = localStorage.getItem('currentBrandId');
                          if (!brandId) throw new Error('No brand selected');
                          const res = await fetch('https://api.glowytics.com/api/labs/ikas/backfill', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json',
                              'X-Brand-ID': brandId
                            },
                            body: JSON.stringify({
                              startDate: ikasRangeStart,
                              endDate: ikasRangeEnd
                            })
                          });
                          const ct = res.headers.get('content-type') || '';
                          if (ct.includes('application/json')) {
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.error || `IKAS backfill failed (${res.status})`);
                            const inserted = (data.rows_inserted ?? data.rowsInserted ?? data.rows) || 0;
                            toast.success(`IKAS backfill tamam: ${inserted} satır, ${data.startDate} → ${data.endDate}`);
                          } else {
                            const text = await res.text();
                            throw new Error(`${res.status} ${res.statusText} – ${text.substring(0, 200)}`);
                          }
                        } catch (e) {
                          console.error('IKAS backfill range error:', e);
                          toast.error(e.message);
                        } finally {
                          setIkasRangeLoading(false);
                        }
                      }}
                    >
                      {ikasRangeLoading ? 'Backfilling…' : 'IKAS: Backfill by Date Range'}
                    </Button>
                  </div>
                  {/* IKAS: Clear All Raw Data button removed */}
                  </>)}
                  {/* Removed: Full Mode Refresh + YouTube Enrich + Preview senders (temp) */}
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Removed: Test Auto Sync Results (temp) */}
                
                {/* Custom Date Fetch */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 w-full">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-blue-800">Custom Date Fetch</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Fetch platform data for a specific date across Google Ads, Meta Ads, and Google Analytics.
                        </p>

                        <div className="mt-4 space-y-4 w-full">
                          {/* Dates */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-white border border-blue-200 rounded-lg p-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Single Date</label>
                              <input
                                type="date"
                                value={customFetchDate}
                                onChange={(e) => setCustomFetchDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-[11px] text-gray-500 mt-1">Use this for a single-day fetch.</p>
                            </div>

                            <div className="bg-white border border-blue-200 rounded-lg p-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                              <input
                                type="date"
                                value={customFetchStartDate}
                                onChange={(e) => setCustomFetchStartDate(e.target.value)}
                                max={customFetchEndDate || new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-[11px] text-gray-500 mt-1">Fill both for a range.</p>
                            </div>

                            <div className="bg-white border border-blue-200 rounded-lg p-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                              <input
                                type="date"
                                value={customFetchEndDate}
                                onChange={(e) => setCustomFetchEndDate(e.target.value)}
                                min={customFetchStartDate || ''}
                                max={new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-[11px] text-gray-500 mt-1">Optional if single date is set.</p>
                            </div>
                          </div>

                          {/* Table selection */}
                          <div className="bg-white border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between gap-3">
                              <label className="block text-xs font-medium text-gray-600">Tables to fetch</label>
                              <div className="flex items-center gap-3 text-xs">
                                <button
                                  type="button"
                                  className="text-blue-700 hover:underline"
                                  onClick={() => setCustomSelectedTables(['google_ads_*', 'ga_*', 'meta_*', 'adjust_*', 'google_mc_*', 'search_console_*', 'criteo_*', 'rtb_house_*', 'pinterest_*', 'tiktok_*'])}
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  className="text-gray-500 hover:underline"
                                  onClick={() => setCustomSelectedTables([])}
                                >
                                  Clear
                                </button>
                              </div>
                            </div>

                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full">
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('google_ads_*')}
                                  onChange={(e) => {
                                    setCustomSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'google_ads_*']))
                                      : prev.filter(v => v !== 'google_ads_*'));
                                  }}
                                />
                                Google Ads
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('ga_*')}
                                  onChange={(e) => {
                                    setCustomSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'ga_*']))
                                      : prev.filter(v => v !== 'ga_*'));
                                  }}
                                />
                                Google Analytics
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('meta_*')}
                                  onChange={(e) => {
                                    setCustomSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'meta_*']))
                                      : prev.filter(v => v !== 'meta_*'));
                                  }}
                                />
                                Meta Ads
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('adjust_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'adjust_*']));
                                      return prev.filter(t => t !== 'adjust_*');
                                    });
                                  }}
                                />
                                Adjust
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('google_mc_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'google_mc_*']));
                                      return prev.filter(t => t !== 'google_mc_*');
                                    });
                                  }}
                                />
                                Merchant Center
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('search_console_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'search_console_*']));
                                      return prev.filter(t => t !== 'search_console_*');
                                    });
                                  }}
                                />
                                Search Console
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('criteo_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'criteo_*']));
                                      return prev.filter(t => t !== 'criteo_*');
                                    });
                                  }}
                                />
                                Criteo Ads
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('rtb_house_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'rtb_house_*']));
                                      return prev.filter(t => t !== 'rtb_house_*');
                                    });
                                  }}
                                />
                                RTB House
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('pinterest_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'pinterest_*']));
                                      return prev.filter(t => t !== 'pinterest_*');
                                    });
                                  }}
                                />
                                Pinterest
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                  checked={customSelectedTables.includes('tiktok_*')}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomSelectedTables(prev => {
                                      if (checked) return Array.from(new Set([...prev, 'tiktok_*']));
                                      return prev.filter(t => t !== 'tiktok_*');
                                    });
                                  }}
                                />
                                TikTok Ads
                              </label>
                            </div>
                          </div>

                          {/* Account Selection for multi-account platforms */}
                          {MULTI_ACCOUNT_BRAND_IDS.includes(localStorage.getItem('currentBrandId')) && (() => {
                            // Build visible platforms list first to avoid empty container
                            const visiblePlatforms = MULTI_ACCOUNT_PLATFORMS.filter(platform => {
                              const tableKey = platform === 'GOOGLE_ADS' ? 'google_ads_*' : 'meta_*';
                              if (!customSelectedTables.includes(tableKey)) return false;
                              const accounts = multiAccounts[platform] || [];
                              const isLoading = multiAccountsLoading[platform];
                              return accounts.length >= 2 || isLoading;
                            });
                            if (visiblePlatforms.length === 0) return null;
                            return (
                            <div className="bg-white border border-blue-200 rounded-lg p-3">
                              <label className="block text-xs font-medium text-gray-600 mb-2">Account Selection</label>
                              <div className="space-y-3">
                                {visiblePlatforms.map(platform => {
                                  const accounts = multiAccounts[platform] || [];
                                  const isLoading = multiAccountsLoading[platform];
                                  const selected = selectedFetchAccounts[platform] || ['all'];
                                  const isAll = selected.includes('all');
                                  const platformLabel = platform === 'GOOGLE_ADS' ? 'Google Ads' : 'Meta Ads';

                                  if (isLoading && accounts.length === 0) {
                                    return (
                                      <div key={platform}>
                                        <div className="text-xs font-medium text-gray-700 mb-1">{platformLabel} Accounts</div>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                          <Loader2 className="w-3 h-3 animate-spin" /> Loading accounts...
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={platform}>
                                      <div className="text-xs font-medium text-gray-700 mb-1">{platformLabel} Accounts</div>
                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setSelectedFetchAccounts(prev => ({ ...prev, [platform]: ['all'] }))}
                                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                                            isAll
                                              ? 'bg-blue-100 border-blue-400 text-blue-800'
                                              : 'bg-white border-gray-300 text-gray-600 hover:border-blue-300'
                                          }`}
                                        >
                                          All Accounts ({accounts.length})
                                        </button>
                                        {accounts.map(acc => {
                                          const isSelected = !isAll && selected.includes(acc.id);
                                          return (
                                            <button
                                              key={acc.id}
                                              type="button"
                                              onClick={() => {
                                                setSelectedFetchAccounts(prev => {
                                                  const current = prev[platform] || ['all'];
                                                  if (current.includes('all')) {
                                                    return { ...prev, [platform]: [acc.id] };
                                                  }
                                                  if (current.includes(acc.id)) {
                                                    const next = current.filter(id => id !== acc.id);
                                                    return { ...prev, [platform]: next.length === 0 ? ['all'] : next };
                                                  }
                                                  return { ...prev, [platform]: [...current, acc.id] };
                                                });
                                              }}
                                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                                                isSelected
                                                  ? 'bg-blue-100 border-blue-400 text-blue-800'
                                                  : 'bg-white border-gray-300 text-gray-600 hover:border-blue-300'
                                              }`}
                                            >
                                              {acc.nickname || acc.accountName || acc.customerId}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="text-[11px] text-gray-500 mt-2">Select specific accounts or fetch all.</p>
                            </div>
                            );
                          })()}

                          {/* Actions */}
                          <div className="grid grid-cols-1 gap-3">
                            <button
                              onClick={handleFetchCustomDate}
                              disabled={(!(customFetchStartDate && customFetchEndDate) && !customFetchDate) || customFetchLoading}
                              className={`w-full h-11 rounded-lg font-medium transition ${
                                ((!(customFetchStartDate && customFetchEndDate) && !customFetchDate) || customFetchLoading)
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                              }`}
                            >
                              {customFetchLoading ? 'Fetching...' : 'Fetch Data'}
                            </button>
                          </div>
                        </div>
                      </div>
                  </div>
                </div>

                {/* Custom Data Cleanup */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 w-full">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-semibold text-red-800">Custom Data Cleanup</h4>
                      <p className="text-sm text-red-700 mt-1">
                        Delete selected platform data for a specific date (or a date range). This action is irreversible.
                      </p>

                      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <div className="bg-white border border-red-200 rounded-lg p-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Single Date</label>
                          <input
                            type="date"
                            value={cleanupDate}
                            onChange={(e) => setCleanupDate(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            Choose one date to delete data for that day.
                          </p>
                        </div>

                        <div className="bg-white border border-red-200 rounded-lg p-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                              type="date"
                              value={cleanupStartDate}
                              onChange={(e) => setCleanupStartDate(e.target.value)}
                              max={cleanupEndDate || new Date().toISOString().split('T')[0]}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                            <input
                              type="date"
                              value={cleanupEndDate}
                              onChange={(e) => setCleanupEndDate(e.target.value)}
                              min={cleanupStartDate || ''}
                              max={new Date().toISOString().split('T')[0]}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">
                            If both start and end dates are set, the entire range will be cleaned.
                          </p>
                        </div>

                        <div className="bg-white border border-red-200 rounded-lg p-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Confirmation</label>
                          <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder='Type "DELETE" to confirm'
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            This confirms that you understand the action is irreversible.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Platforms to clean */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platforms to clean
                    </label>
                  <div className="flex flex-wrap items-center gap-4 bg-white border border-red-200 rounded-lg p-3">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-red-600 rounded border-gray-300"
                          checked={cleanupSelectedTables.includes('google_ads_*')}
                          onChange={(e) => {
                            setCleanupSelectedTables((prev) => e.target.checked
                              ? Array.from(new Set([...prev, 'google_ads_*']))
                              : prev.filter(v => v !== 'google_ads_*'));
                          }}
                        />
                        Google Ads
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-red-600 rounded border-gray-300"
                          checked={cleanupSelectedTables.includes('ga_*')}
                          onChange={(e) => {
                            setCleanupSelectedTables((prev) => e.target.checked
                              ? Array.from(new Set([...prev, 'ga_*']))
                              : prev.filter(v => v !== 'ga_*'));
                          }}
                        />
                        GA4
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-red-600 rounded border-gray-300"
                          checked={cleanupSelectedTables.includes('meta_*')}
                          onChange={(e) => {
                            setCleanupSelectedTables((prev) => e.target.checked
                              ? Array.from(new Set([...prev, 'meta_*']))
                              : prev.filter(v => v !== 'meta_*'));
                          }}
                        />
                        Meta Ads
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-red-600 rounded border-gray-300"
                          checked={cleanupSelectedTables.includes('adjust_*')}
                          onChange={(e) => {
                            setCleanupSelectedTables((prev) => e.target.checked
                              ? Array.from(new Set([...prev, 'adjust_*']))
                              : prev.filter(v => v !== 'adjust_*'));
                          }}
                        />
                        Adjust
                      </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('google_mc_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'google_mc_*']))
                                      : prev.filter(v => v !== 'google_mc_*'));
                                  }}
                                />
                                Merchant Center
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('search_console_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'search_console_*']))
                                      : prev.filter(v => v !== 'search_console_*'));
                                  }}
                                />
                                Search Console
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('shopify_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'shopify_*']))
                                      : prev.filter(v => v !== 'shopify_*'));
                                  }}
                                />
                                Shopify
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('ikas_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'ikas_*']))
                                      : prev.filter(v => v !== 'ikas_*'));
                                  }}
                                />
                                Ikas
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('woo_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'woo_*']))
                                      : prev.filter(v => v !== 'woo_*'));
                                  }}
                                />
                                WooCommerce
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('criteo_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'criteo_*']))
                                      : prev.filter(v => v !== 'criteo_*'));
                                  }}
                                />
                                Criteo Ads
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('rtb_house_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'rtb_house_*']))
                                      : prev.filter(v => v !== 'rtb_house_*'));
                                  }}
                                />
                                RTB House
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('pinterest_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'pinterest_*']))
                                      : prev.filter(v => v !== 'pinterest_*'));
                                  }}
                                />
                                Pinterest
                              </label>
                              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                                  checked={cleanupSelectedTables.includes('tiktok_*')}
                                  onChange={(e) => {
                                    setCleanupSelectedTables((prev) => e.target.checked
                                      ? Array.from(new Set([...prev, 'tiktok_*']))
                                      : prev.filter(v => v !== 'tiktok_*'));
                                  }}
                                />
                                TikTok Ads
                              </label>
                              <div className="ml-auto flex items-center gap-3 text-xs">
                        <button
                          type="button"
                          className="text-blue-600 hover:underline"
                          onClick={() => setCleanupSelectedTables(['google_ads_*', 'ga_*', 'meta_*', 'adjust_*', 'google_mc_*', 'search_console_*', 'shopify_*', 'ikas_*', 'woo_*', 'criteo_*', 'rtb_house_*', 'pinterest_*', 'tiktok_*'])}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="text-gray-500 hover:underline"
                          onClick={() => setCleanupSelectedTables([])}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Tip: Select one or more platforms. Only selected platforms' tables will be cleaned for the chosen date(s).
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={handleDataCleanup}
                      disabled={(!cleanupDate && !(cleanupStartDate && cleanupEndDate)) || confirmText !== 'DELETE' || cleanupLoading || cleanupSelectedTables.length === 0}
                      className={`px-6 py-2 rounded-lg font-medium ${
                        (!cleanupDate && !(cleanupStartDate && cleanupEndDate)) || confirmText !== 'DELETE' || cleanupLoading || cleanupSelectedTables.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {cleanupLoading ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Cleaning...</span>
                        </div>
                      ) : (
                        'Clean Data'
                      )}
                    </Button>
                  </div>
                </div>

                {cleanupResults && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <h4 className="text-lg font-medium text-green-800 mb-4">Cleanup Results</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{cleanupResults.totalDeletedRows}</div>
                        <div className="text-sm text-green-700">Total Rows Deleted</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{cleanupResults.tablesProcessed}</div>
                        <div className="text-sm text-green-700">Tables Processed</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{cleanupResults.date}</div>
                        <div className="text-sm text-green-700">Date Cleaned</div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-green-800 mb-2">Detailed Results:</h5>
                                             <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                         <table className="w-full text-sm">
                           <thead className="bg-green-100">
                             <tr>
                               <th className="text-left p-3 font-medium text-green-800">Table</th>
                               <th className="text-left p-3 font-medium text-green-800">Date Column</th>
                               <th className="text-left p-3 font-medium text-green-800">Existing Rows</th>
                               <th className="text-left p-3 font-medium text-green-800">Deleted</th>
                               <th className="text-left p-3 font-medium text-green-800">Status</th>
                             </tr>
                           </thead>
                           <tbody>
                             {cleanupResults.results?.map((result, index) => (
                               <tr key={index} className={index % 2 === 0 ? 'bg-green-25' : 'bg-white'}>
                                 <td className="p-3 text-green-700 font-mono text-xs">{result.table}</td>
                                 <td className="p-3 text-green-700 font-mono text-xs">{result.dateColumn || 'N/A'}</td>
                                 <td className="p-3 text-green-700 text-center">{result.existingRows || 0}</td>
                                 <td className="p-3 text-green-700 text-center font-semibold">{result.deleted}</td>
                                 <td className="p-3">
                                   <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                     result.status === 'SUCCESS' 
                                       ? 'bg-green-100 text-green-800' 
                                       : result.status === 'NO_DATA_FOR_DATE'
                                       ? 'bg-blue-100 text-blue-800'
                                       : result.status === 'TABLE_NOT_EXISTS'
                                       ? 'bg-gray-100 text-gray-600'
                                       : result.status === 'NO_DATE_COLUMN'
                                       ? 'bg-yellow-100 text-yellow-800'
                                       : 'bg-red-100 text-red-800'
                                   }`}>
                                     {result.status === 'NO_DATA_FOR_DATE' ? 'NO DATA' : 
                                      result.status === 'TABLE_NOT_EXISTS' ? 'NOT EXISTS' :
                                      result.status === 'NO_DATE_COLUMN' ? 'NO DATE COL' :
                                      result.status}
                                   </span>
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                    </div>
                  </div>
                )}

                {/* Yesterday actions: Fetch (left) + Cleanup (right) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Fetch Yesterday Data */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-green-800">Fetch Yesterday Data</h4>
                          <p className="text-sm text-green-700 mt-1">
                            Fetch yesterday's data across active platforms. This mirrors the automatic "yesterday" sync behavior.
                          </p>
                          <p className="text-xs text-green-700 mt-2 font-medium">
                            Target date: {new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleFetchYesterdayData}
                        className="px-6 py-3 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 shadow-lg"
                      >
                        Fetch Yesterday
                      </button>
                    </div>
                  </div>

                  {/* Quick Yesterday Cleanup */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-blue-800">Quick Yesterday Cleanup</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Clean yesterday's data across all platforms. This mimics the daily automatic cleanup system.
                          </p>
                          <p className="text-xs text-blue-600 mt-2 font-medium">
                            Target date: {new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleCleanupYesterday}
                        disabled={yesterdayCleanupLoading}
                        className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                          yesterdayCleanupLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                        }`}
                      >
                        {yesterdayCleanupLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Cleaning Yesterday...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Clean Yesterday</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {yesterdayCleanupResults && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <h4 className="text-lg font-medium text-green-800 mb-4">🧹 Yesterday Cleanup Results</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{yesterdayCleanupResults.summary?.totalDeletedRows || 0}</div>
                        <div className="text-sm text-green-700">Total Rows Deleted</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{yesterdayCleanupResults.summary?.tablesProcessed || 0}</div>
                        <div className="text-sm text-green-700">Tables Processed</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]}</div>
                        <div className="text-sm text-green-700">Date Cleaned</div>
                      </div>
                    </div>
                    
                    {yesterdayCleanupResults.deletionResults && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-green-800 mb-2">Detailed Results:</h5>
                        <div className="bg-white rounded-lg border border-green-200 overflow-hidden max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-green-100 sticky top-0">
                              <tr>
                                <th className="text-left p-3 font-medium text-green-800">Table</th>
                                <th className="text-left p-3 font-medium text-green-800">Rows Deleted</th>
                                <th className="text-left p-3 font-medium text-green-800">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {yesterdayCleanupResults.deletionResults.map((result, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-green-25' : 'bg-white'}>
                                  <td className="p-3 text-green-700 font-mono text-xs">{result.table}</td>
                                  <td className="p-3 text-green-700 text-center font-semibold">{result.deleted}</td>
                                  <td className="p-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      result.status === 'SUCCESS' 
                                        ? 'bg-green-100 text-green-800' 
                                        : result.status === 'NO_DATA_FOR_DATE'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {result.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* GA4: E-commerce Monthly Backfill (Item Performance) */}
                {SHOW_GA4_ECOM_MONTHLY_BACKFILL && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-orange-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-orange-800">GA4: E-commerce Monthly Backfill</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Item Performance verisini aylık kırılımda çekip <code className="px-1 py-0.5 bg-white rounded border">ga_ecommerce_item_monthly</code> tablosuna yazar.
                        Bu buton sadece bu işlemi çalıştırır.
                      </p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={ecomMonthlyStart}
                            onChange={(e) => setEcomMonthlyStart(e.target.value)}
                            className="w-full px-3 py-2 border border-orange-200 rounded-md bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={ecomMonthlyEnd}
                            onChange={(e) => setEcomMonthlyEnd(e.target.value)}
                            min={ecomMonthlyStart || ''}
                            className="w-full px-3 py-2 border border-orange-200 rounded-md bg-white"
                          />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-2 text-[11px] text-orange-700">
                          Geniş aralıklarda (ör. 3+ ay) aylık toplayarak token limitlerini aşmadan backfill yapar.
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={handleEcomMonthlyBackfill}
                            disabled={ecomMonthlyLoading}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${ecomMonthlyLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-orange-600 text-white hover:bg-orange-700'}`}
                          >
                            {ecomMonthlyLoading ? 'Running…' : 'Run GA4 E-commerce Monthly'}
                          </button>
                        </div>
                      </div>

                      {/* Result details intentionally minimal; row counts removed */}
                    </div>
                  </div>
                </div>
                )}

                {/* GA4: Delete E-commerce Data */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-red-800">GA4: Delete E-commerce Data</h4>
                      <p className="text-sm text-red-700 mt-1">
                        Deletes rows from <code className="px-1 py-0.5 bg-white rounded border">ga_ecommerce_raw</code> for the selected date range. Views will update automatically.
                      </p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={ecomDeleteStart}
                            onChange={(e) => setEcomDeleteStart(e.target.value)}
                            className="w-full px-3 py-2 border border-red-200 rounded-md bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={ecomDeleteEnd}
                            onChange={(e) => setEcomDeleteEnd(e.target.value)}
                            min={ecomDeleteStart || ''}
                            className="w-full px-3 py-2 border border-red-200 rounded-md bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-600 font-bold">DELETE</span> to confirm</label>
                          <input
                            type="text"
                            value={ecomDeleteConfirm}
                            onChange={(e) => setEcomDeleteConfirm(e.target.value)}
                            placeholder="DELETE"
                            className="w-full px-3 py-2 border border-red-200 rounded-md bg-white"
                          />
                        </div>
                        <div className="lg:col-span-2 text-[11px] text-red-700">
                          This permanently removes raw e-commerce data for the selected range. Use the Refetch card below to re-pull from GA4.
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={handleEcomDelete}
                            disabled={ecomDeleteLoading || ecomDeleteConfirm !== 'DELETE'}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                              ecomDeleteLoading || ecomDeleteConfirm !== 'DELETE'
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {ecomDeleteLoading ? 'Deleting...' : 'Delete E-commerce Data'}
                          </button>
                        </div>
                      </div>

                      {ecomDeleteResult && (
                        <div className="mt-3 p-3 rounded-md text-sm bg-green-50 text-green-800 border border-green-200">
                          <p><strong>Deleted rows:</strong> {ecomDeleteResult.deletedRows ?? '—'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* GA4: Refetch E-commerce Data */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-blue-800">GA4: Refetch E-commerce Data</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Re-pulls GA4 analytics data (including <code className="px-1 py-0.5 bg-white rounded border">ga_ecommerce_raw</code>) from the API for the selected date range.
                      </p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={ecomRefetchStart}
                            onChange={(e) => setEcomRefetchStart(e.target.value)}
                            className="w-full px-3 py-2 border border-blue-200 rounded-md bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={ecomRefetchEnd}
                            onChange={(e) => setEcomRefetchEnd(e.target.value)}
                            min={ecomRefetchStart || ''}
                            className="w-full px-3 py-2 border border-blue-200 rounded-md bg-white"
                          />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-2 text-[11px] text-blue-700">
                          Fetches all GA4 tables for the selected range. Runs in the background — you can continue using the app.
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={handleEcomRefetch}
                            disabled={ecomRefetchLoading}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                              ecomRefetchLoading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {ecomRefetchLoading ? 'Starting...' : 'Refetch E-commerce Data'}
                          </button>
                        </div>
                      </div>

                      {ecomRefetchResult && (
                        <div className="mt-3 p-3 rounded-md text-sm bg-green-50 text-green-800 border border-green-200">
                          <p>{ecomRefetchResult.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Meta Creative Tools */}
                {SHOW_META_CREATIVE_TOOLS && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-purple-700 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a2 2 0 012-2h2m0 0V9a2 2 0 00-2-2H9m4 6l4 4m0 0l-4 4m4-4H5" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-purple-800">Meta Creative Tools</h4>
                      <p className="text-sm text-purple-700 mt-1">Run or clean creative-only Meta jobs.</p>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          onClick={handleMetaCreativeMode}
                          disabled={metaCreativeLoading}
                          className={`px-4 py-1.5 rounded-md text-sm font-medium ${
                            metaCreativeLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                        >
                          {metaCreativeLoading ? 'Starting Meta Creative…' : 'Run Meta Creative (Full)'}
                        </button>
                        <button
                          onClick={handleMetaCreativeYesterdayMode}
                          disabled={metaCreativeYesterdayLoading}
                          className={`px-4 py-1.5 rounded-md text-sm font-medium ${
                            metaCreativeYesterdayLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          {metaCreativeYesterdayLoading ? 'Starting…' : 'Run Meta Creative (Yesterday)'}
                        </button>
                        <button
                          onClick={handleCleanupMetaCreativeYesterday}
                          disabled={metaCreativeCleanupLoading}
                          className={`px-4 py-1.5 rounded-md text-sm font-medium ${
                            metaCreativeCleanupLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
                          }`}
                        >
                          {metaCreativeCleanupLoading ? 'Cleaning…' : 'Clean Meta Creative (Yesterday)'}
                        </button>
                        <button
                          onClick={handleCleanupMetaCreativeAll}
                          disabled={metaCreativeCleanupAllLoading}
                          className={`px-4 py-1.5 rounded-md text-sm font-medium ${
                            metaCreativeCleanupAllLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-700 text-white hover:bg-red-800'
                          }`}
                        >
                          {metaCreativeCleanupAllLoading ? 'Cleaning…' : 'Clean Meta Creative (ALL)'}
                        </button>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end">
                        <div>
                          <label className="block text-xs text-purple-800 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={metaCreativeStartDate}
                            onChange={(e) => setMetaCreativeStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-purple-200 rounded-md bg-white"
                          />
                    </div>
                        <div>
                          <label className="block text-xs text-purple-800 mb-1">End Date (opsiyonel)</label>
                          <input
                            type="date"
                            value={metaCreativeEndDate}
                            onChange={(e) => setMetaCreativeEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-purple-200 rounded-md bg-white"
                          />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-2 text-[11px] text-purple-700">
                          Tek gün için sadece bir tarih seçin; aralık için iki alanı da doldurun. Varsayılan olarak sadece mapping çalışır.
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={handleMetaCreativeRunWithDate}
                            disabled={metaCreativeCustomLoading}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium ${metaCreativeCustomLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-700 text-white hover:bg-purple-800'}`}
                          >
                            {metaCreativeCustomLoading ? 'Starting…' : 'Run Meta Creative (Date/Range)'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Removed: YouTube Enrichment Result (temp) */}

                {connections?.IKAS?.status === 'ACTIVE' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* IKAS: Custom Date Fetch */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-emerald-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-emerald-800">IKAS: Custom Date Fetch</h4>
                        <p className="text-sm text-emerald-700 mt-1">
                          Fetch IKAS order items for a selected date range.
                        </p>
                        <div className="mt-3 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                              type="date"
                              value={ikasRangeStart}
                              onChange={(e) => setIkasRangeStart(e.target.value)}
                              max={ikasRangeEnd || new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                              type="date"
                              value={ikasRangeEnd}
                              onChange={(e) => setIkasRangeEnd(e.target.value)}
                              min={ikasRangeStart || ''}
                              max={new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              className={`rounded-xl px-3 ${ikasRangeLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-700 text-white hover:bg-emerald-800'}`}
                              onClick={async () => {
                                if (ikasRangeLoading) return;
                                if (!ikasRangeStart || !ikasRangeEnd) {
                                  toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                                  return;
                                }
                                if (ikasRangeStart > ikasRangeEnd) {
                                  toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
                                  return;
                                }
                                setIkasRangeLoading(true);
                                try {
                                  const brandId = localStorage.getItem('currentBrandId');
                                  if (!brandId) throw new Error('No brand selected');
                                  const res = await fetch('https://api.glowytics.com/api/labs/ikas/backfill', {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Brand-ID': brandId
                                    },
                                    body: JSON.stringify({
                                      startDate: ikasRangeStart,
                                      endDate: ikasRangeEnd
                                    })
                                  });
                                  const ct = res.headers.get('content-type') || '';
                                  if (ct.includes('application/json')) {
                                    const data = await res.json();
                                    if (!res.ok) throw new Error(data.error || `IKAS backfill failed (${res.status})`);
                                    const inserted = (data.rows_inserted ?? data.rowsInserted ?? data.rows) || 0;
                                    toast.success(`IKAS backfill tamam: ${inserted} satır, ${data.startDate} → ${data.endDate}`);
                                  } else {
                                    const text = await res.text();
                                    throw new Error(`${res.status} ${res.statusText} – ${text.substring(0, 200)}`);
                                  }
                                } catch (e) {
                                  console.error('IKAS backfill range error:', e);
                                  toast.error(e.message);
                                } finally {
                                  setIkasRangeLoading(false);
                                }
                              }}
                            >
                              {ikasRangeLoading ? 'Backfilling…' : 'Fetch Selected Range'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* IKAS: Custom Date Delete */}
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-rose-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-rose-800">IKAS: Custom Date Delete</h4>
                        <p className="text-sm text-rose-700 mt-1">
                          Delete IKAS raw rows for a selected date range only (table: ikas_order_items_raw).
                        </p>
                        <div className="mt-3 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                              type="date"
                              value={ikasDeleteStart}
                              onChange={(e) => setIkasDeleteStart(e.target.value)}
                              max={ikasDeleteEnd || new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                              type="date"
                              value={ikasDeleteEnd}
                              onChange={(e) => setIkasDeleteEnd(e.target.value)}
                              min={ikasDeleteStart || ''}
                              max={new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              className={`rounded-xl px-3 ${ikasDeleteLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-rose-600 text-white hover:bg-rose-700'}`}
                              onClick={async () => {
                                if (ikasDeleteLoading) return;
                                if (!ikasDeleteStart || !ikasDeleteEnd) {
                                  toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                                  return;
                                }
                                if (ikasDeleteStart > ikasDeleteEnd) {
                                  toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
                                  return;
                                }
                                setIkasDeleteLoading(true);
                                try {
                                  const brandId = localStorage.getItem('currentBrandId');
                                  if (!brandId) throw new Error('No brand selected');
                                  // Iterate per day and call generic cleanup for IKAS tables
                                  const start = new Date(`${ikasDeleteStart}T00:00:00Z`);
                                  const end = new Date(`${ikasDeleteEnd}T00:00:00Z`);
                                  let cursor = new Date(start);
                                  let totalDeleted = 0;
                                  while (cursor <= end) {
                                    const dayStr = cursor.toISOString().split('T')[0];
                                    const resp = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
                                      method: 'POST',
                                      credentials: 'include',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'X-Brand-ID': brandId
                                      },
                                      body: JSON.stringify({
                                        date: dayStr,
                                        confirmText: 'DELETE',
                                        tables: ['ikas_*']
                                      })
                                    });
                                    const ct = resp.headers.get('content-type') || '';
                                    if (!resp.ok) {
                                      const err = ct.includes('application/json') ? await resp.json().catch(() => ({})) : await resp.text();
                                      throw new Error((err && (err.error || err.details)) || `Cleanup failed for ${dayStr}`);
                                    } else {
                                      try {
                                        const js = await resp.json();
                                        totalDeleted += Number(js?.summary?.totalDeletedRows || 0);
                                      } catch (_) {
                                        // ignore parse failure; continue
                                      }
                                    }
                                    // advance one day (UTC)
                                    cursor.setUTCDate(cursor.getUTCDate() + 1);
                                  }
                                  toast.success(`IKAS delete OK: ${ikasDeleteStart} → ${ikasDeleteEnd} (${totalDeleted} rows)`);
                                } catch (e) {
                                  console.error('IKAS custom delete error:', e);
                                  toast.error(e.message || 'Failed to delete IKAS data for range');
                                } finally {
                                  setIkasDeleteLoading(false);
                                }
                              }}
                            >
                              {ikasDeleteLoading ? 'Deleting…' : 'Delete Selected Range'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                )}

                {connections?.WOOCOMMERCE?.status === 'ACTIVE' && (
                <>
                {/* WooCommerce: Custom Date Fetch */}
                <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-fuchsia-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-fuchsia-800">WooCommerce: Custom Date Fetch</h4>
                        <p className="text-sm text-fuchsia-700 mt-1">
                          Select a date range to fetch Orders (summary) and Order Items into DB.
                        </p>
                        <div className="mt-3 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                              type="date"
                              value={wooRangeStart}
                              onChange={(e) => setWooRangeStart(e.target.value)}
                              max={wooRangeEnd || new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                              type="date"
                              value={wooRangeEnd}
                              onChange={(e) => setWooRangeEnd(e.target.value)}
                              min={wooRangeStart || ''}
                              max={new Date().toISOString().split('T')[0]}
                              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              className={`rounded-xl px-3 ${wooOrdersLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-fuchsia-700 text-white hover:bg-fuchsia-800'}`}
                              onClick={async () => {
                                if (wooOrdersLoading) return;
                                if (!wooRangeStart || !wooRangeEnd) {
                                  toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                                  return;
                                }
                                if (wooRangeStart > wooRangeEnd) {
                                  toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
                                  return;
                                }
                                setWooOrdersLoading(true);
                                try {
                                  const brandId = localStorage.getItem('currentBrandId');
                                  if (!brandId) throw new Error('No brand selected');
                                  const res = await fetch('https://api.glowytics.com/api/labs/woocommerce/ingest-orders-min', {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Brand-ID': brandId
                                    },
                                    body: JSON.stringify({
                                      mode: 'custom',
                                      startDate: wooRangeStart,
                                      endDate: wooRangeEnd,
                                      perPage: 100,
                                      maxPages: 300
                                    })
                                  });
                                  const ct = res.headers.get('content-type') || '';
                                  const data = ct.includes('application/json') ? await res.json() : null;
                                  if (!res.ok) throw new Error((data && (data.error || data.details)) || `Woo orders ingest failed (${res.status})`);
                                  toast.success(`Woo Orders OK: upserted ${data?.upserted || 0} (${wooRangeStart} → ${wooRangeEnd})`);
                                } catch (e) {
                                  console.error('Woo orders ingest error:', e);
                                  toast.error(e.message);
                                } finally {
                                  setWooOrdersLoading(false);
                                }
                              }}
                            >
                              {wooOrdersLoading ? 'Fetching…' : 'Fetch Orders (Range)'}
                            </Button>
                            <Button
                              className={`rounded-xl px-3 ${wooItemsLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'}`}
                              onClick={async () => {
                                if (wooItemsLoading) return;
                                if (!wooRangeStart || !wooRangeEnd) {
                                  toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
                                  return;
                                }
                                if (wooRangeStart > wooRangeEnd) {
                                  toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
                                  return;
                                }
                                setWooItemsLoading(true);
                                try {
                                  const brandId = localStorage.getItem('currentBrandId');
                                  if (!brandId) throw new Error('No brand selected');
                                  const res = await fetch('https://api.glowytics.com/api/labs/woocommerce/ingest-orderdetail', {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Brand-ID': brandId
                                    },
                                    body: JSON.stringify({
                                      mode: 'custom',
                                      startDate: wooRangeStart,
                                      endDate: wooRangeEnd,
                                      perPage: 100,
                                      maxPages: 300,
                                      enrichCategories: true
                                    })
                                  });
                                  const ct = res.headers.get('content-type') || '';
                                  const data = ct.includes('application/json') ? await res.json() : null;
                                  if (!res.ok) throw new Error((data && (data.error || data.details)) || `Woo orderdetail ingest failed (${res.status})`);
                                  toast.success(`Woo Order Items OK: inserted ${data?.inserted || 0} (${wooRangeStart} → ${wooRangeEnd})`);
                                } catch (e) {
                                  console.error('Woo orderdetail ingest error:', e);
                                  toast.error(e.message);
                                } finally {
                                  setWooItemsLoading(false);
                                }
                              }}
                            >
                              {wooItemsLoading ? 'Fetching…' : 'Fetch Order Items (Range)'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}

                {/* 🔥 DELETE ALL DATA SECTION - EXTREMELY DANGEROUS! */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-red-800">Platform Data Cleanup - Delete ALL Platform Data</h4>
                        <p className="text-sm text-red-700 mt-1">
                          This will permanently delete <strong>ALL PLATFORM DATA</strong> from Google Ads, Meta Ads, and Google Analytics tables. This action is <strong>IRREVERSIBLE</strong> and will destroy all campaigns, keywords, analytics, and historical performance data. <strong>AI and system tables will NOT be touched.</strong>
                        </p>
                      </div>
                    </div>

                    <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-red-700" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-red-800">EXTREME WARNING</span>
                      </div>
                      <ul className="text-xs text-red-700 space-y-1">
                        <li>• All Google Ads campaign data will be lost</li>
                        <li>• All Meta Ads campaign data will be lost</li>
                        <li>• All Google Analytics data will be lost</li>
                        <li>• All sync logs and historical records will be lost</li>
                        <li>• You will need to re-sync everything from scratch</li>
                        <li className="text-green-700">AI data and system tables will be preserved</li>
                        <li className="text-green-700">User accounts and settings will be preserved</li>
                      </ul>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-red-700 mb-2">
                          Type "Deletemyalldata" to confirm platform data deletion
                        </label>
                        <input
                          type="text"
                          value={deleteAllConfirmText}
                          onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                          placeholder="Type Deletemyalldata to confirm"
                          className="w-full px-3 py-2 border border-red-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                        <p className="text-xs text-red-600 mt-1">
                          Must be typed exactly: <code className="bg-red-100 px-1 rounded">Deletemyalldata</code>
                        </p>
                      </div>

                      <div className="flex justify-between items-center">
                        <button
                          onClick={handleDeleteAllData}
                          disabled={deleteAllConfirmText !== 'Deletemyalldata' || deleteAllLoading}
                          className={`px-6 py-3 rounded-lg font-medium ${
                            deleteAllConfirmText !== 'Deletemyalldata' || deleteAllLoading
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700 shadow-lg'
                          }`}
                        >
                          {deleteAllLoading ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Cleaning Platform Data...</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              <span>DELETE PLATFORM DATA</span>
                            </div>
                          )}
                        </button>
                        
                        {deleteAllConfirmText === 'Deletemyalldata' && (
                          <span className="text-sm text-red-600 font-medium">Ready to delete platform data!</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {deleteAllResults && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <h4 className="text-lg font-medium text-green-800 mb-4">Platform Data Cleanup Results</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{deleteAllResults.totalRowsDeleted}</div>
                        <div className="text-sm text-green-700">Platform Rows Deleted</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{deleteAllResults.totalTablesProcessed}</div>
                        <div className="text-sm text-green-700">Platform Tables Cleaned</div>
                      </div>
                    </div>
                    
                    {deleteAllResults.results && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-green-800 mb-2">Platform Data Cleanup Summary:</h5>
                        <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-green-100">
                              <tr>
                                <th className="text-left p-3 font-medium text-green-800">Table</th>
                                <th className="text-left p-3 font-medium text-green-800">Rows Deleted</th>
                                <th className="text-left p-3 font-medium text-green-800">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deleteAllResults.results.map((result, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-green-25' : 'bg-white'}>
                                  <td className="p-3 text-green-700 font-mono text-xs">{result.table}</td>
                                  <td className="p-3 text-green-700 text-center font-semibold">{result.deletedRows}</td>
                                  <td className="p-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      result.status === 'SUCCESS' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {result.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                                                 </div>
                       </div>
                     )}
                     
                     <div className="mt-6 flex justify-end">
                       <button
                         onClick={resetDeleteAllModal}
                         className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                       >
                         Clear Results
                       </button>
                     </div>
                   </div>
                 )}

                {/* Shopify Backfill (Data Cleanup) */}
                {connections?.SHOPIFY?.status === 'ACTIVE' && (
                  <div className="mb-6 p-4 border rounded-xl bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <img src="/shopify_glyph.svg" alt="Shopify" className="w-5 h-5" />
                        <h4 className="font-medium">Shopify Backfill</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          className="border rounded-md px-2 py-1 text-sm"
                          value={shopifyRangeStart}
                          onChange={(e) => setShopifyRangeStart(e.target.value)}
                          aria-label="Shopify start date"
                        />
                        <span className="text-sm text-gray-600">→</span>
                        <input
                          type="date"
                          className="border rounded-md px-2 py-1 text-sm"
                          value={shopifyRangeEnd}
                          onChange={(e) => setShopifyRangeEnd(e.target.value)}
                          aria-label="Shopify end date"
                        />
                        <Button
                          className={`rounded-xl px-4 ${shopifyBackfillLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                          disabled={shopifyBackfillLoading}
                          onClick={async () => {
                            if (!shopifyRangeStart || !shopifyRangeEnd) {
                              toast.error('Please select start and end dates');
                              return;
                            }
                            // Safety watchdog in case UI misses a state flip
                            let watchdog = null;
                            try {
                              setShopifyBackfillLoading(true);
                              setShopifyBackfillResult(null);
                              watchdog = setTimeout(() => {
                                try { setShopifyBackfillLoading(false); } catch {}
                              }, 60000);
                              const brandId = localStorage.getItem('currentBrandId');
                              if (!brandId) throw new Error('No brand selected');
                            const res = await fetch('https://api.glowytics.com/api/shopify/labs/backfill?async=true', {
                                method: 'POST',
                                credentials: 'include',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'X-Brand-ID': brandId
                                },
                                body: JSON.stringify({ startDate: shopifyRangeStart, endDate: shopifyRangeEnd })
                              });
                              const ct = res.headers.get('content-type') || '';
                              if (ct.includes('application/json')) {
                                const data = await res.json();
                              if (!res.ok) throw new Error(data.error || `Shopify backfill failed (${res.status})`);
                              if (data.async) {
                                toast.success('Shopify backfill started. You can continue using the app.');
                              } else {
                                setShopifyBackfillResult(data);
                                const inserted = data.inserted || 0;
                                toast.success(`Shopify backfill OK: ${inserted} rows, ${shopifyRangeStart} → ${shopifyRangeEnd}`);
                              }
                              } else {
                                const text = await res.text();
                                throw new Error(`${res.status} ${res.statusText} – ${text.substring(0, 200)}`);
                              }
                            } catch (e) {
                              console.error('Shopify backfill error:', e);
                              toast.error(e.message);
                            } finally {
                              try { clearTimeout(watchdog); } catch {}
                              setShopifyBackfillLoading(false);
                            }
                          }}
                        >
                          {shopifyBackfillLoading ? 'Backfilling…' : 'Backfill Range'}
                        </Button>
                        <Button
                          className={`rounded-xl px-4 ${shopifyClearLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-rose-600 text-white hover:bg-rose-700'}`}
                          disabled={shopifyClearLoading}
                          onClick={() => setIsShopifyClearOpen(true)}
                        >
                          {shopifyClearLoading ? 'Clearing…' : 'Clear ALL Shopify Data'}
                        </Button>
                      </div>
                    </div>
                    {shopifyBackfillResult && (
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg border overflow-x-auto">
                        {JSON.stringify(shopifyBackfillResult, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'monthly-targets' && (
            <MonthlyTargets />
          )}
        </div>
      </div>

      {/* Ikas Dialog - Manual App Token */}
      <Dialog
        open={isIkasDialogOpen}
        onClose={() => setIsIkasDialogOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img src="/ikaslogov2.svg" alt="Ikas" className="h-6 w-auto" />
            <span>Connect İkas</span>
          </div>
        }
      >
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            İkas mağazanızı bağlamak için aşağıdaki bilgileri girin. Bu bilgileri ikas panelinizdeki <strong>Ayarlar → API &amp; Webhooks</strong> bölümünden alabilirsiniz.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Store Name</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                name="storeName"
                value={ikasForm.storeName}
                onChange={handleIkasFormChange}
                className="flex-1 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
                placeholder="yourstore"
                required
              />
              <span className="text-gray-500 text-sm">.myikas.com</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Örnek: Mağaza URL'iniz yourstore.myikas.com ise "yourstore" yazın
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
            <input
              type="text"
              name="clientId"
              value={ikasForm.clientId}
              onChange={handleIkasFormChange}
              className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
            <input
              type="password"
              name="clientSecret"
              value={ikasForm.clientSecret}
              onChange={handleIkasFormChange}
              className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="Client Secret"
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              onClick={() => setIsIkasDialogOpen(false)}
              className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
            >
              İptal
            </Button>
            <Button
              disabled={ikasLoading}
              onClick={handleIkasSave}
              className="bg-[#5865F2] text-white hover:bg-[#4752c4] font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {ikasLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Bağlanıyor...
                </>
              ) : (
                <>
                  <img src="/ikaslogoicon.png" alt="" className="w-5 h-5" />
                  Bağlantıyı Kaydet
                </>
              )}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Meta Catalogs Dialog */}
      <Dialog
        open={isMetaCatalogsDialogOpen}
        onClose={() => {
          setIsMetaCatalogsDialogOpen(false);
          setMetaCatalogs([]);
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/6033716.png" 
              alt="Meta Ads" 
              className="h-6 w-auto"
            />
            <span>Meta Product Catalogs</span>
          </div>
        }
      >
        <div className="p-4">
          {metaCatalogsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading catalogs...</span>
            </div>
          ) : (
            <>
              {metaCatalogs.length === 0 ? (
                <div className="text-sm text-gray-600">No catalogs found for this token.</div>
              ) : (
                <div className="max-h-80 overflow-y-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2">ID</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Products</th>
                        <th className="text-left p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metaCatalogs.map((c, idx) => (
                        <tr key={`${c.id}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2 font-mono text-xs">{c.id}</td>
                          <td className="p-2">{c.name || '-'}</td>
                          <td className="p-2">{typeof c.product_count === 'number' ? c.product_count : '-'}</td>
                          <td className="p-2">
                            <Button
                              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-1 text-xs"
                              onClick={async () => {
                                try {
                                  const brandId = localStorage.getItem('currentBrandId');
                                  if (!brandId) {
                                    toast.error('Please select a brand first');
                                    return;
                                  }
                                  const resp = await fetch('https://api.glowytics.com/api/auth/meta-catalog/connect', {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'X-Brand-ID': brandId
                                    },
                                    body: JSON.stringify({ catalogId: c.id, catalogName: c.name })
                                  });
                                  const js = await resp.json().catch(() => ({}));
                                  if (!resp.ok) {
                                    throw new Error(js?.error || 'Failed to save catalog');
                                  }
                                  toast.success(`Catalog connected: ${c.name || c.id}`);
                                  setIsMetaCatalogsDialogOpen(false);
                                  try { await fetchConnections(); } catch {}
                                } catch (e) {
                                  console.error('Meta catalog connect error:', e);
                                  toast.error(e.message || 'Failed to connect catalog');
                                }
                              }}
                            >
                              Select
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={() => setIsMetaCatalogsDialogOpen(false)}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Shopify Clear Modal */}
      <Dialog
        open={isShopifyClearOpen}
        onClose={() => {
          if (!shopifyClearLoading) {
            setIsShopifyClearOpen(false);
            setShopifyClearConfirmText('');
          }
        }}
        title={
          <div className="flex items-center gap-3">
            <img src="/shopify_glyph.svg" alt="Shopify" className="h-6 w-auto" />
            <span>Clear ALL Shopify Data</span>
          </div>
        }
      >
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-700">
            This will permanently delete all rows from <code className="font-mono">shopify_order_items_raw</code> for the current brand schema.
          </p>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Type DELETE to confirm</label>
            <input
              type="text"
              value={shopifyClearConfirmText}
              onChange={(e) => setShopifyClearConfirmText(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="DELETE"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={() => {
                if (!shopifyClearLoading) {
                  setIsShopifyClearOpen(false);
                  setShopifyClearConfirmText('');
                }
              }}
              className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              disabled={shopifyClearLoading || (shopifyClearConfirmText || '').toUpperCase() !== 'DELETE'}
              className={`${
                shopifyClearLoading || (shopifyClearConfirmText || '').toUpperCase() !== 'DELETE'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-rose-600 text-white hover:bg-rose-700'
              } font-semibold px-5 py-2 rounded-lg`}
              onClick={async () => {
                if ((shopifyClearConfirmText || '').toUpperCase() !== 'DELETE') return;
                try {
                  setShopifyClearLoading(true);
                  const brandId = localStorage.getItem('currentBrandId');
                  if (!brandId) throw new Error('No brand selected');
                  const res = await fetch(`https://api.glowytics.com/api/brands/${brandId}/cleanup-data`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-Brand-ID': brandId
                    },
                    body: JSON.stringify({
                      all: true,
                      confirmText: 'DELETE',
                      tables: ['shopify_*']
                    })
                  });
                  const ct = res.headers.get('content-type') || '';
                  if (ct.includes('application/json')) {
                    const data = await res.json();
                    if (!res.ok) {
                      throw new Error(data.error || `Shopify clear failed (${res.status})`);
                    }
                    const deleted = data?.summary?.totalDeletedRows ?? data?.deleted ?? 0;
                    toast.success(`Shopify data cleared. Deleted rows: ${deleted}`);
                    setIsShopifyClearOpen(false);
                    setShopifyClearConfirmText('');
                  } else {
                    const text = await res.text();
                    throw new Error(`${res.status} ${res.statusText} – ${text.substring(0, 200)}`);
                  }
                } catch (e) {
                  console.error('Shopify clear error:', e);
                  toast.error(e.message || 'Failed to clear Shopify data');
                } finally {
                  setShopifyClearLoading(false);
                }
              }}
            >
              {shopifyClearLoading ? 'Clearing…' : 'Clear Shopify Data'}
            </Button>
          </div>
        </div>
      </Dialog>
      {/* Shopify Dialog */}
      <Dialog
        open={isShopifyDialogOpen}
        onClose={() => setIsShopifyDialogOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img src="/shopifyicon.png" alt="Shopify" className="h-6 w-auto" />
            <span>Connect Shopify</span>
          </div>
        }
      >
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Enter your Shopify store domain to connect your store with Glowytics.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Shop Domain</label>
            <input
              type="text"
              value={shopifyForm.shopDomain}
              onChange={(e) => setShopifyForm(prev => ({ ...prev, shopDomain: e.target.value }))}
              placeholder="yourstore.myshopify.com"
              className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2.5"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('shopify-connect-btn')?.click();
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">Example: mystore or mystore.myshopify.com</p>
          </div>
          
          <div className="flex justify-end gap-3 pt-3">
            <Button
              type="button"
              onClick={() => setIsShopifyDialogOpen(false)}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium px-4 py-2.5 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              id="shopify-connect-btn"
              onClick={() => {
                let shop = shopifyForm.shopDomain.trim();
                if (!shop) {
                  toast.error('Please enter your shop domain');
                  return;
                }
                const brandId = localStorage.getItem('currentBrandId');
                if (!brandId) {
                  toast.error('Please select a brand first');
                  return;
                }
                // Normalize shop domain
                shop = shop.replace(/^https?:\/\//, '').replace(/\/$/, '');
                if (!shop.includes('.')) {
                  shop = `${shop}.myshopify.com`;
                }
                // Create state with brandId (base64 encoded JSON)
                const state = btoa(JSON.stringify({ brandId }));
                // Shopify OAuth URL - redirects to Shopify app which syncs token to Glowytics
                const scopes = 'read_orders,read_products,read_customers,read_analytics,read_reports,read_inventory,read_locations';
                const redirectUri = 'https://api.glowytics.com/api/auth/shopify/callback';
                const clientId = 'a4a7031adc7d5b05ae8d2bef5ee0ad8f';
                const oauthUrl = `https://${shop}/admin/oauth/authorize?client_id=${clientId}&scope=${encodeURIComponent(scopes)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
                window.location.href = oauthUrl;
              }}
              className="bg-[#5E8E3E] text-white hover:bg-[#4a7230] font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"
            >
              <img src="/shopify_glyph.svg" alt="" className="w-5 h-5" />
              Connect with Shopify
            </Button>
          </div>
        </div>
      </Dialog>

      {/* WooCommerce Dialog */}
      <Dialog
        open={isWooDialogOpen}
        onClose={() => setIsWooDialogOpen(false)}
      >
        <div className="p-4 flex items-center gap-2">
          <img src="/woocommerceicon.png" alt="WooCommerce" className="h-6 w-auto" />
          <span>Connect WooCommerce</span>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Store URL (https)</label>
            <input
              type="text"
              className="w-full border rounded-xl px-3 py-2"
              placeholder="https://example.com"
              value={wooForm.storeUrl}
              onChange={(e) => setWooForm(prev => ({ ...prev, storeUrl: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Consumer Key</label>
            <input
              type="password"
              className="w-full border rounded-xl px-3 py-2"
              value={wooForm.consumerKey}
              onChange={(e) => setWooForm(prev => ({ ...prev, consumerKey: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Consumer Secret</label>
            <input
              type="password"
              className="w-full border rounded-xl px-3 py-2"
              value={wooForm.consumerSecret}
              onChange={(e) => setWooForm(prev => ({ ...prev, consumerSecret: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              className="px-4 py-2 rounded-xl border text-gray-700"
              onClick={() => setIsWooDialogOpen(false)}
              disabled={wooSaving}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              onClick={async () => {
                try {
                  setWooSaving(true);
                  const brandId = localStorage.getItem('currentBrandId');
                  if (!brandId) {
                    toast.error('Please select a brand first');
                    setWooSaving(false);
                    return;
                  }
                  const { storeUrl, consumerKey, consumerSecret } = wooForm;
                  if (!storeUrl || !consumerKey || !consumerSecret) {
                    toast.error('Store URL, Consumer Key and Secret are required');
                    setWooSaving(false);
                    return;
                  }
                  if (!/^https:\/\//i.test(storeUrl)) {
                    toast.error('Store URL must start with https://');
                    setWooSaving(false);
                    return;
                  }
                  // 1) Test connection via backend proxy (orders 1 item)
                  const testResp = await fetch(`https://api.glowytics.com/api/woocommerce/orders?storeUrl=${encodeURIComponent(storeUrl)}&apiVersion=wc/v3&page=1&per_page=1&idsOnly=true`, {
                    method: 'GET',
                    headers: {
                      'X-Brand-ID': brandId,
                      'x-woo-ck': consumerKey,
                      'x-woo-cs': consumerSecret
                    },
                    credentials: 'include'
                  });
                  if (!testResp.ok) {
                    const err = await testResp.json().catch(() => ({}));
                    throw new Error(err?.error || `WooCommerce test failed (${testResp.status})`);
                  }
                  // 2) Save connection
                  const saveResp = await fetch('https://api.glowytics.com/api/auth/woocommerce/connect', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-Brand-ID': brandId
                    },
                    credentials: 'include',
                    body: JSON.stringify({ storeUrl, consumerKey, consumerSecret })
                  });
                  const data = await saveResp.json().catch(() => ({}));
                  if (!saveResp.ok) throw new Error(data?.error || 'Failed to save WooCommerce connection');
                  toast.success('WooCommerce connected');
                  setIsWooDialogOpen(false);
                  setWooForm({ storeUrl: '', consumerKey: '', consumerSecret: '' });
                  await fetchConnections(); // refresh list
                } catch (err) {
                  console.error('WooCommerce connect error:', err);
                  toast.error(err.message || 'Failed to connect WooCommerce');
                } finally {
                  setWooSaving(false);
                }
              }}
              disabled={wooSaving}
            >
              {wooSaving ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Meta Ads Dialog */}
      <Dialog
        size="3xl"
        contentClassName="p-0"
        open={isMetaAdsDialogOpen}
        onClose={() => {
          setIsMetaAdsDialogOpen(false);
          setSelectedAccount(null);
          setSelectedBusinessManager(null);
          setMetaWizardCatalogs([]);
          setSelectedCatalog(null);
          setMetaWizardStep('business');
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/6033716.png" 
              alt="Meta Ads" 
              className="h-6 w-auto"
            />
            <span className="flex-1">Meta Connect</span>
            <div className="hidden md:flex items-center text-[11px] uppercase tracking-wider font-bold">
              <span className={`${metaWizardStep === 'business' ? 'text-blue-600' : 'text-gray-400'}`}>1. BM</span>
              <span className="mx-2 text-gray-300">›</span>
              <span className={`${metaWizardStep === 'account' ? 'text-blue-600' : 'text-gray-400'}`}>2. Account</span>
              <span className="mx-2 text-gray-300">›</span>
              <span className={`${metaWizardStep === 'pages' ? 'text-blue-600' : 'text-gray-400'}`}>3. Page</span>
              <span className="mx-2 text-gray-300">›</span>
              <span className={`${metaWizardStep === 'instagram' ? 'text-blue-600' : 'text-gray-400'}`}>4. Instagram</span>
              <span className="mx-2 text-gray-300">›</span>
              <span className={`${metaWizardStep === 'pixels' ? 'text-blue-600' : 'text-gray-400'}`}>5. Pixel</span>
              <span className="mx-2 text-gray-300">›</span>
              <span className={`${metaWizardStep === 'catalogs' ? 'text-blue-600' : 'text-gray-400'}`}>6. Catalog</span>
            </div>
          </div>
        }
      >
        <div className="p-6 mx-auto" style={{ width: '100%', overflow: 'hidden' }}>
          {/* STEP 1: BUSINESS MANAGER SELECTION (NEW FLOW) */}
          {metaWizardStep === 'business' && (
            businessManagersLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                <p className="text-sm text-gray-500">Loading Business Managers...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-2">Select the Business Manager that owns your assets:</div>
                
                {metaWizardBusinessManagers.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                    <p className="text-sm text-gray-500">No Business Managers found. Make sure you have the right permissions.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {metaWizardBusinessManagers.map((bm) => {
                      const isSelected = selectedBusinessManager?.id === bm.id;
                      return (
                        <div
                          key={bm.id}
                          onClick={() => setSelectedBusinessManager(bm)}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50/50'
                              : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                            {bm.profile_picture ? (
                              <img src={bm.profile_picture} alt={bm.name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              bm.name?.charAt(0) || 'B'
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{bm.name}</div>
                            <div className="text-xs text-gray-500">ID: {bm.id}</div>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t w-full">
                  <Button
                    onClick={() => {
                      setIsMetaAdsDialogOpen(false);
                      setSelectedBusinessManager(null);
                      setMetaWizardStep('business');
                    }}
                    className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        if (!selectedBusinessManager) {
                          toast.error('Please select a Business Manager');
                          return;
                        }
                        setMetaWizardLoading(true);
                        
                        // Save Business Manager connection to DB
                        const brandId = localStorage.getItem('currentBrandId');
                        const response = await fetch('https://api.glowytics.com/api/auth/business-manager/connect', {
                          method: 'POST',
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json',
                            'X-Brand-ID': brandId
                          },
                          body: JSON.stringify({
                            businessId: selectedBusinessManager.id,
                            businessName: selectedBusinessManager.name
                          })
                        });
                        
                        if (!response.ok) {
                          const errorData = await response.json().catch(() => ({}));
                          throw new Error(errorData.error || 'Failed to save Business Manager');
                        }
                        
                        // Fetch Ad Accounts from this Business Manager
                        await fetchBMAdAccounts(selectedBusinessManager.id);
                        setSelectedAccount(null);
                        setMetaWizardStep('account');
                      } catch (e) {
                        console.error('Business Manager save error:', e);
                        toast.error(e.message || 'Failed to proceed');
                      } finally {
                        setMetaWizardLoading(false);
                      }
                    }}
                    disabled={!selectedBusinessManager || metaWizardLoading}
                    className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 min-w-[120px] flex items-center gap-2"
                  >
                    {metaWizardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Next'}
                  </Button>
                </div>
              </div>
            )
          )}

          {/* STEP 2: AD ACCOUNT SELECTION (from Business Manager) */}
          {metaWizardStep === 'account' && (
            bmAdAccountsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                <p className="text-sm text-gray-500">Loading Ad Accounts...</p>
              </div>
            ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-2">Select the Ad Account you want to track:</div>
              
              {metaWizardBMAdAccounts.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                  <p className="text-sm text-gray-500">No Ad Accounts found in this Business Manager.</p>
                  <Button
                    onClick={() => {
                      setMetaWizardStep('business');
                      setSelectedAccount(null);
                    }}
                    className="mt-4 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Go Back
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {metaWizardBMAdAccounts.map((acc) => {
                    const isSelected = selectedAccount?.id === acc.id;
                    return (
                      <div
                        key={acc.id}
                        onClick={() => setSelectedAccount({ id: acc.id, name: acc.name })}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50/50'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                          {acc.name?.charAt(0) || 'A'}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{acc.name}</div>
                          <div className="text-xs text-gray-500">
                            {acc.account_id} • {acc.ownership === 'owned' ? '🏠 Owned' : '🤝 Shared'}
                          </div>
                        </div>
                        {acc.account_status === 1 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                        )}
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

                  <div className="flex justify-end gap-2 mt-6 pt-4 border-t w-full">
                <Button
                  onClick={() => {
                    setMetaWizardStep('business');
                    setSelectedAccount(null);
                  }}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Back
                </Button>
                <Button
                      onClick={async () => {
                        try {
                          if (!selectedAccount) {
                            toast.error('Please select an account');
                            return;
                          }
                          setMetaWizardLoading(true);
                          // Save/activate selected Meta Ads account first
                          const brandId = localStorage.getItem('currentBrandId');
                          const response = await fetch('https://api.glowytics.com/api/auth/meta-ads/connect', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json',
                              'X-Brand-ID': brandId
                            },
                            body: JSON.stringify({
                              customerId: String(selectedAccount.id).replace('act_', ''),
                              accountName: selectedAccount.name,
                              connectionId: tempConnectionId
                            })
                          });
                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.error || 'Failed to save account');
                          }
                          
                          // Fetch pages AND Instagram from Business Manager
                          setSelectedMetaPage(null);
                          setSelectedAdAccountInstagram(null);
                          await Promise.all([
                            fetchBMPages(selectedBusinessManager.id),
                            fetchBMInstagramAccounts(selectedBusinessManager.id)
                          ]);
                          setMetaWizardStep('pages');
                        } catch (e) {
                          console.error('Meta wizard next error:', e);
                          toast.error(e.message || 'Failed to proceed');
                        } finally {
                          setMetaWizardLoading(false);
                        }
                      }}
                  disabled={!selectedAccount || metaWizardLoading}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-w-[100px]"
                >
                      Next
                </Button>
              </div>
            </div>
            )
          )}

          {metaWizardStep === 'pages' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Select a Facebook Page for your ads:</div>
                    {metaPagesLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                  </div>
                  
                  {metaWizardPages.length === 0 && !metaPagesLoading ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                      <p className="text-sm text-gray-500">No Facebook Pages found. Make sure you have the right permissions.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {metaWizardPages.map((p, idx) => {
                        const isSelected = selectedMetaPage?.id === p.id;
                        return (
                          <button
                            key={`${p.id}-${idx}`}
                            onClick={() => setSelectedMetaPage(p)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'bg-blue-600 text-white border-transparent shadow-lg'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-white/20' : 'bg-blue-100'}`}>
                                <Facebook className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-blue-600'}`} />
                              </div>
                              <div className="text-left">
                                <div className="font-medium">{p.name || 'Unnamed Page'}</div>
                                <div className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-400'}`}>
                                  ID: {p.id}
                                </div>
                              </div>
                            </div>
                            {isSelected && <CheckCircle className="w-5 h-5" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center mt-6 pt-4 border-t">
                    <Button
                      onClick={() => {
                        setMetaWizardStep('account');
                        setSelectedMetaPage(null);
                      }}
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => {
                        if (!selectedMetaPage) {
                          toast.error('Please select a Facebook Page');
                          return;
                        }
                        // Go to Instagram step
                        setMetaWizardStep('instagram');
                      }}
                      disabled={!selectedMetaPage}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-w-[120px] flex items-center gap-2"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Instagram Step - New separate step */}
              {metaWizardStep === 'instagram' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Select an Instagram account for your ads:</div>
                    {adAccountInstagramLoading && <Loader2 className="w-4 h-4 animate-spin text-pink-500" />}
                  </div>
                  
                  {/* Selected Page Info */}
                  {selectedMetaPage && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-3">
                      <Facebook className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="text-sm font-medium text-blue-900">{selectedMetaPage.name}</div>
                        <div className="text-xs text-blue-600">Selected Facebook Page</div>
                      </div>
                    </div>
                  )}
                  
                  {adAccountInstagramAccounts.length > 0 ? (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {adAccountInstagramAccounts.map((ig) => {
                        const isSelected = selectedAdAccountInstagram?.id === ig.id;
                        return (
                          <button
                            key={ig.id}
                            onClick={() => setSelectedAdAccountInstagram(ig)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white border-transparent shadow-lg'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-pink-300 hover:bg-pink-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-white/20' : 'bg-gradient-to-br from-pink-100 to-purple-100'}`}>
                                <Instagram className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-pink-600'}`} />
                              </div>
                              <div className="text-left">
                                <div className="font-medium">@{ig.username || ig.id}</div>
                                <div className={`text-xs ${isSelected ? 'text-pink-100' : 'text-gray-400'}`}>
                                  Connected to: {ig.connected_page_name || 'Unknown Page'}
                                </div>
                              </div>
                            </div>
                            {isSelected && <CheckCircle className="w-5 h-5" />}
                          </button>
                        );
                      })}
                    </div>
                  ) : !adAccountInstagramLoading ? (
                    <div className="text-center py-10 bg-amber-50 rounded-xl border border-amber-200">
                      <Instagram className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                      <p className="text-sm text-amber-700 font-medium">No Instagram accounts found</p>
                      <p className="text-xs text-amber-600 mt-1">Campaigns will only run on Facebook.</p>
                    </div>
                  ) : null}
                  
                  <p className="text-xs text-gray-400">
                    Select an Instagram account to enable Instagram ad placements. You can skip this step if you only want Facebook ads.
                  </p>
                  
                  <div className="flex justify-between items-center mt-6 pt-4 border-t">
                    <Button
                      onClick={() => {
                        setMetaWizardStep('pages');
                        setSelectedAdAccountInstagram(null);
                      }}
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          setMetaWizardLoading(true);
                          
                          // Save page and instagram connection to DB
                          const brandId = localStorage.getItem('currentBrandId');
                          const igAccount = selectedAdAccountInstagram;
                          
                          const response = await fetch('https://api.glowytics.com/api/auth/facebook-page/connect', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json',
                              'X-Brand-ID': brandId
                            },
                            body: JSON.stringify({
                              pageId: selectedMetaPage.id,
                              pageName: selectedMetaPage.name,
                              pageAccessToken: selectedMetaPage.access_token,
                              instagramId: igAccount ? igAccount.id : null,
                              instagramName: igAccount ? (igAccount.username || 'Instagram Account') : null,
                              instagramIgId: igAccount?.ig_id || null,
                              instagramSource: igAccount?.source || null,
                              instagramBusinessId: igAccount?.business_id || null
                            })
                          });

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.error || 'Failed to save page connection');
                          }
                          
                          // Move to pixels step
                          setSelectedMetaPixel(null);
                          await fetchMetaPixels(selectedAccount.id);
                          setMetaWizardStep('pixels');
                        } catch (e) {
                          console.error('Meta wizard instagram next error:', e);
                          toast.error(e.message || 'Failed to proceed');
                        } finally {
                          setMetaWizardLoading(false);
                        }
                      }}
                      disabled={metaWizardLoading}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-w-[120px] flex items-center gap-2"
                    >
                      {metaWizardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedAdAccountInstagram ? 'Next' : 'Skip')}
                    </Button>
                  </div>
                </div>
              )}
              {metaWizardStep === 'pixels' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Select the Meta Pixel used for conversion tracking:</div>
                    {metaPixelsLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                  </div>

                  {metaWizardPixels.length === 0 && !metaPixelsLoading ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                      <p className="text-sm text-gray-500">No Pixels found for this ad account.</p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-xl w-full shadow-inner">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="text-left p-3 text-gray-500 font-semibold border-b">Pixel Name</th>
                            <th className="text-left p-3 text-gray-500 font-semibold border-b">Last Fired</th>
                            <th className="text-right p-3 text-gray-500 font-semibold border-b">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {metaWizardPixels.map((p, idx) => {
                            const isSelected = selectedMetaPixel?.id === p.id;
                            
                            // Robust date parsing for last_fired_time
                            let lastFired = 'Never';
                            if (p.last_fired_time) {
                              const date = !isNaN(p.last_fired_time) 
                                ? new Date(Number(p.last_fired_time) * 1000) 
                                : new Date(p.last_fired_time);
                              
                              if (!isNaN(date.getTime())) {
                                lastFired = date.toLocaleDateString();
                              }
                            }

                            return (
                              <tr
                                key={`${p.id}-${idx}`}
                                onClick={() => setSelectedMetaPixel(p)}
                                className={`group cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-gray-50/80'}`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full transition-all ${isSelected ? 'bg-blue-500 scale-110' : 'bg-transparent group-hover:bg-gray-200'}`} />
                                    <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>{p.name || '-'}</span>
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-0.5 ml-4 font-mono">{p.id}</div>
                                </td>
                                <td className="p-3">
                                  <span className="text-xs text-gray-500">{lastFired}</span>
                                </td>
                                <td className="p-3 text-right">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setSelectedMetaPixel(p); }}
                                    className={`rounded-full px-4 py-1 text-xs font-semibold transition-all ${
                                      isSelected
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-400 group-hover:bg-gray-100'
                                    }`}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-6 pt-4 border-t">
                    <Button
                      onClick={() => {
                        setMetaWizardStep('pages');
                        setSelectedMetaPixel(null);
                      }}
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          if (!selectedMetaPixel) {
                            toast.error('Please select a pixel');
                            return;
                          }
                          setMetaWizardLoading(true);
                          
                          // Save pixel connection to DB
                          const brandId = localStorage.getItem('currentBrandId');
                          const response = await fetch('https://api.glowytics.com/api/auth/meta-pixel/connect', {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json',
                              'X-Brand-ID': brandId
                            },
                            body: JSON.stringify({
                              pixelId: selectedMetaPixel.id,
                              pixelName: selectedMetaPixel.name
                            })
                          });

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.error || 'Failed to save pixel connection');
                          }
                          
                          // Move to catalogs step
                          setSelectedCatalog(null);
                          await fetchMetaCatalogs(selectedAccount.id, false);
                          setMetaWizardStep('catalogs');
                        } catch (e) {
                          console.error('Meta wizard pixel next error:', e);
                          toast.error(e.message || 'Failed to proceed');
                        } finally {
                          setMetaWizardLoading(false);
                        }
                      }}
                      disabled={!selectedMetaPixel || metaWizardLoading}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-w-[120px] flex items-center gap-2"
                    >
                      {metaWizardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Next'}
                    </Button>
                  </div>
                </div>
              )}
              {metaWizardStep === 'catalogs' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Select your product catalog for Dynamic Ads:</div>
                    {metaCatalogsLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                  </div>

                  {metaCatalogs.length === 0 && !metaCatalogsLoading ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
                      <p className="text-sm text-gray-500">No catalogs found. You can finish without a catalog.</p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-xl w-full shadow-inner">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="text-left p-3 text-gray-500 font-semibold border-b">Catalog Name</th>
                            <th className="text-left p-3 text-gray-500 font-semibold border-b">Products</th>
                            <th className="text-right p-3 text-gray-500 font-semibold border-b">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {metaCatalogs.map((c, idx) => {
                            const isSelected = selectedCatalog?.id === c.id;
                            return (
                              <tr
                                key={`${c.id}-${idx}`}
                                onClick={() => setSelectedCatalog(c)}
                                className={`group cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-gray-50/80'}`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full transition-all ${isSelected ? 'bg-blue-500 scale-110' : 'bg-transparent group-hover:bg-gray-200'}`} />
                                    <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>{c.name || '-'}</span>
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-0.5 ml-4 font-mono">{c.id}</div>
                                </td>
                                <td className="p-3">
                                  <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs text-gray-600 border border-gray-200">
                                    {typeof c.product_count === 'number' ? `${c.product_count.toLocaleString()} items` : '-'}
                                  </span>
                                </td>
                                <td className="p-3 text-right">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setSelectedCatalog(c); }}
                                    className={`rounded-full px-4 py-1 text-xs font-semibold transition-all ${
                                      isSelected
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-400 group-hover:bg-gray-100'
                                    }`}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-6 pt-4 border-t">
                    <Button
                      onClick={() => {
                        setMetaWizardStep('pixels');
                        setSelectedCatalog(null);
                      }}
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-6"
                    >
                      Back
                    </Button>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => {
                          setIsMetaAdsDialogOpen(false);
                          toast.success('Connection completed successfully');
                        }}
                        className="bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 px-4"
                      >
                        Skip and Finish
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            if (!selectedCatalog) {
                              toast.error('Please select a catalog');
                              return;
                            }
                            setMetaWizardLoading(true);
                            const brandId = localStorage.getItem('currentBrandId');
                            const resp = await fetch('https://api.glowytics.com/api/auth/meta-catalog/connect', {
                              method: 'POST',
                              credentials: 'include',
                              headers: {
                                'Content-Type': 'application/json',
                                'X-Brand-ID': brandId
                              },
                              body: JSON.stringify({ catalogId: selectedCatalog.id, catalogName: selectedCatalog.name })
                            });
                            const js = await resp.json().catch(() => ({}));
                            if (!resp.ok) throw new Error(js?.error || 'Failed to connect catalog');
                            toast.success(`Catalog connected: ${selectedCatalog.name || selectedCatalog.id}`);
                            setIsMetaAdsDialogOpen(false);
                            try { await fetchConnections(); } catch {}
                          } catch (e) {
                            console.error('Meta catalog connect error:', e);
                            toast.error(e.message || 'Failed to connect catalog');
                          } finally {
                            setMetaWizardLoading(false);
                          }
                        }}
                        disabled={!selectedCatalog || metaWizardLoading}
                        className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20 px-8 flex items-center gap-2"
                      >
                        {metaWizardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect Catalog'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
        </div>
      </Dialog>

      {/* Search Console Dialog */}
      <Dialog
        open={isSearchConsoleDialogOpen}
        onClose={() => {
          setIsSearchConsoleDialogOpen(false);
          setSelectedSearchConsoleSite(null);
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/search-console-icon-2025-1v33.svg" 
              alt="Search Console" 
              className="h-6 w-auto"
            />
            <span>Select Search Console Site</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingSearchConsoleSites ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading sites...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => handleSearchConsoleSelect(e.target.value)}
                value={selectedSearchConsoleSite ? JSON.stringify(selectedSearchConsoleSite) : ""}
              >
                <option value="">Select a site</option>
                {searchConsoleSites.map(site => (
                  <option
                    key={site.siteUrl}
                    value={JSON.stringify({ siteUrl: site.siteUrl })}
                  >
                    {site.siteUrl}
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsSearchConsoleDialogOpen(false);
                    setSelectedSearchConsoleSite(null);
                  }}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSearchConsoleSave}
                  disabled={!selectedSearchConsoleSite}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Connect Site
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Google Ads Account Seçim Dialog'u */}
      <Dialog
        open={isGoogleAdsDialogOpen}
        onClose={() => {
          setIsGoogleAdsDialogOpen(false);
          setSelectedAccount(null);
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/google_ads-512.webp" 
              alt="Google Ads" 
              className="h-6 w-auto"
            />
            <span>Select Google Ads Account</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingAccounts ? (
            <div className="min-h-[200px] bg-gray-50">
              <div className="loading-logo-container" style={{ minHeight: '200px' }}>
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => handleSelectChange(e.target.value)}
                value={selectedAccount ? JSON.stringify(selectedAccount) : ""}
              >
                <option value="">Select an account</option>
                {googleAdsAccounts.map(account => (
                  <option 
                    key={account.id} 
                    value={JSON.stringify({
                      id: account.id,
                      name: account.name,
                      currency: account.currency,
                      timeZone: account.timeZone,
                      managerCustomerId: account.managerCustomerId || null
                    })}
                  >
                    {account.name} ({account.id})
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsGoogleAdsDialogOpen(false);
                    setSelectedAccount(null);
                  }}
                  className="bg-gray-200 text-gray-800 hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGoogleAdsAccountSave}
                  disabled={!selectedAccount}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Connect Account
                </Button>
              </div>
            </div>
          )}

          {googleAdsAccounts.length === 0 && !loadingAccounts && (
            <p className="text-sm text-gray-500 text-center">
              No active Google Ads accounts found.
            </p>
          )}
        </div>
      </Dialog>

      {/* TikTok Ads Advertiser Picker Dialog */}
      <Dialog
        open={isTikTokDialogOpen}
        onClose={() => {
          setIsTikTokDialogOpen(false);
          setSelectedTikTokAdvertiser(null);
        }}
        title={
          <div className="flex items-center gap-3">
            <img src="/download.png" alt="TikTok Ads" className="h-6 w-auto" />
            <span>Select TikTok Advertiser Account</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingTikTokAdvertisers ? (
            <div className="min-h-[200px] bg-gray-50">
              <div className="loading-logo-container" style={{ minHeight: '200px' }}>
                <img
                  src="/logoicon.png"
                  alt="Glowytics Logo"
                  className="loading-logo"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                You authorized {tiktokAdvertisers.length} advertiser account
                {tiktokAdvertisers.length === 1 ? '' : 's'}. Choose one to connect — only a single
                account can be active per brand.
              </p>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    setSelectedTikTokAdvertiser(null);
                  } else {
                    try {
                      setSelectedTikTokAdvertiser(JSON.parse(val));
                    } catch (_) {
                      setSelectedTikTokAdvertiser(null);
                    }
                  }
                }}
                value={selectedTikTokAdvertiser ? JSON.stringify(selectedTikTokAdvertiser) : ''}
              >
                <option value="">Select an advertiser</option>
                {tiktokAdvertisers.map((adv) => (
                  <option
                    key={adv.advertiser_id}
                    value={JSON.stringify({
                      advertiser_id: adv.advertiser_id,
                      advertiser_name: adv.advertiser_name
                    })}
                  >
                    {adv.advertiser_name
                      ? `${adv.advertiser_name} (${adv.advertiser_id})`
                      : adv.advertiser_id}
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsTikTokDialogOpen(false);
                    setSelectedTikTokAdvertiser(null);
                  }}
                  className="bg-gray-200 text-gray-800 hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTikTokAccountSave}
                  disabled={!selectedTikTokAdvertiser || savingTikTokAccount}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingTikTokAccount ? 'Connecting…' : 'Connect Advertiser'}
                </Button>
              </div>
            </div>
          )}

          {tiktokAdvertisers.length === 0 && !loadingTikTokAdvertisers && (
            <p className="text-sm text-gray-500 text-center">
              No authorized TikTok advertiser accounts found.
            </p>
          )}
        </div>
      </Dialog>

      {/* Google Analytics Dialog */}
      <Dialog
        open={isGoogleAnalyticsDialogOpen}
        onClose={() => {
          setIsGoogleAnalyticsDialogOpen(false);
          setSelectedAccount(null);
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/GA4logoicon.svg" 
              alt="Google Analytics 4" 
              className="h-6 w-auto"
            />
            <span>Select Google Analytics Account</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingGAAccounts ? (
            <div className="min-h-[200px] bg-gray-50">
              <div className="loading-logo-container" style={{ minHeight: '200px' }}>
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleGASelectChange}
                value={selectedAccount ? JSON.stringify(selectedAccount) : ""}
              >
                <option value="">Select an account</option>
                {googleAnalyticsAccounts.map(account => (
                  <option 
                    key={account.id} 
                    value={JSON.stringify({
                      id: account.id,
                      displayName: account.displayName,
                      regionCode: account.regionCode
                    })}
                  >
                    {account.displayName} ({account.id})
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsGoogleAnalyticsDialogOpen(false);
                    setSelectedAccount(null);
                  }}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGASave}
                  disabled={!selectedAccount}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Connect Account
                </Button>
              </div>
            </div>
          )}

          {googleAnalyticsAccounts.length === 0 && !loadingGAAccounts && (
            <p className="text-sm text-gray-500 text-center">
              No Google Analytics accounts found.
            </p>
          )}
        </div>
      </Dialog>

      {/* Merchant Center Dialog */}
      <Dialog
        open={isMerchantCenterDialogOpen}
        onClose={() => {
          setIsMerchantCenterDialogOpen(false);
          setSelectedMerchantCenterAccount(null);
        }}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/google-merchant-centerpngg.svg" 
              alt="Google Merchant Center" 
              className="h-6 w-auto"
            />
            <span>Select Google Merchant Center Account</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingMerchantCenterAccounts ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading Merchant Center accounts...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => handleMerchantCenterSelect(e.target.value)}
                value={selectedMerchantCenterAccount ? JSON.stringify(selectedMerchantCenterAccount) : ""}
              >
                <option value="">{merchantCenterAccounts.length > 0 ? 'Select an account' : 'No accounts found'}</option>
                {merchantCenterAccounts.map(acc => (
                  <option 
                    key={String(acc.accountId || acc.id || acc.name)}
                    value={JSON.stringify({
                      accountId: acc.accountId || acc.id || acc.name,
                      displayName: acc.displayName || acc.name || `Account ${acc.accountId || acc.id || ''}`
                    })}
                  >
                    {(acc.displayName || acc.name || 'Account')} ({acc.accountId || acc.id || acc.name})
                  </option>
                ))}
              </select>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsMerchantCenterDialogOpen(false);
                    setSelectedMerchantCenterAccount(null);
                    setManualMerchantId('');
                    setManualMerchantName('');
                  }}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMerchantCenterSave}
                  disabled={!selectedMerchantCenterAccount && !manualMerchantId.trim()}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Connect Account
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Adjust Connect Dialog */}
      <Dialog
        open={isAdjustDialogOpen}
        onClose={() => setIsAdjustDialogOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img
              src="/adjustlogo.png"
              alt="Adjust"
              className="h-6 w-auto"
            />
            <span>Connect Adjust</span>
          </div>
        }
      >
        <form onSubmit={handleAdjustConnect} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Token</label>
            <input
              type="text"
              name="apiToken"
              value={adjustForm.apiToken}
              onChange={handleAdjustFormChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="Enter your Adjust API token"
              required
            />
            <p className="mt-2 text-xs text-gray-500">
              Paste a valid Adjust API token with read permissions.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              onClick={() => setIsAdjustDialogOpen(false)}
              className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={adjustLoading}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
            >
              {adjustLoading ? 'Connecting…' : 'Connect'}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Adjust Apps Dialog */}
      <Dialog
        open={isAdjustAppsDialogOpen}
        onClose={() => setIsAdjustAppsDialogOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/adjustlogo.png" 
              alt="Adjust" 
              className="h-6 w-auto"
            />
            <span>Adjust Apps</span>
          </div>
        }
      >
        <div className="p-4">
          {adjustAppsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading apps…</span>
            </div>
          ) : adjustApps.length === 0 ? (
            <p className="text-sm text-gray-600">No apps found for this token.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {adjustApps.map((app, idx) => {
                const token = String(app.id || app.app_token || app.appToken || '');
                const name = app.name || app.display_name || app.title || 'Unnamed App';
                const platform = app.platform || app.os || '';
                const storeId = app.store_id || app.bundle_id || app.package_name || '';
                return (
                  <div key={`${token}-${idx}`} className="p-3 border rounded-lg flex items-center justify-between">
                    <div>
                      <div className="font-medium">{name}</div>
                      <div className="text-xs text-gray-500">token: {token}</div>
                      {(platform || storeId) && (
                        <div className="text-xs text-gray-500">{platform && `platform: ${platform}`}{platform && storeId ? ' · ' : ''}{storeId && `store_id: ${storeId}`}</div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleAdjustAppSave(app)}
                      disabled={adjustSavingAppId === token}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${adjustSavingAppId === token ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      {adjustSavingAppId === token ? 'Connecting…' : 'Connect'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-between items-center gap-2 mt-4 pt-2 border-t">
            <button
              type="button"
              onClick={async () => {
                try {
                  setAdjustAppsLoading(true);
                  const apps = await fetchAdjustAppsViaProxy();
                  setAdjustApps(apps);
                } catch (e) {
                  console.error('Adjust refresh apps error:', e);
                  toast.error(e.message || 'Failed to refresh apps');
                } finally {
                  setAdjustAppsLoading(false);
                }
              }}
              className="text-sm text-blue-600 hover:underline disabled:opacity-50"
              disabled={adjustAppsLoading}
            >
              {adjustAppsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <Button
              onClick={() => setIsAdjustAppsDialogOpen(false)}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      {/* RTB House Connect Dialog */}
      <Dialog
        open={isRtbHouseDialogOpen}
        onClose={() => {
          if (rtbHouseSaving) return;
          setRtbHouseToken('');
          setRtbHouseStep(1);
          setRtbHouseAccounts([]);
          setSelectedRtbHouseAccountHash('');
          setIsRtbHouseDialogOpen(false);
        }}
        title={
          <div className="flex items-center gap-3">
            <img
              src="/download.jpeg"
              alt="RTB House"
              className="h-6 w-auto rounded"
            />
            <span>Connect RTB House</span>
          </div>
        }
      >
        <div className="p-4 space-y-4">
          {rtbHouseStep === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                <input
                  type="password"
                  value={rtbHouseToken}
                  onChange={(e) => setRtbHouseToken(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
                  placeholder="Paste RTB House access token"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setIsRtbHouseDialogOpen(false)}
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleRtbHouseFetchAccounts}
                  disabled={rtbHouseLoadingAccounts || !rtbHouseToken.trim()}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
                >
                  {rtbHouseLoadingAccounts ? 'Loading…' : 'Continue'}
                </Button>
              </div>
            </>
          )}

          {rtbHouseStep === 2 && (
            <>
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <div className="divide-y">
                  {rtbHouseAccounts.map((acc, idx) => {
                    const hash = String(acc?.hash || '');
                    const name = acc?.name || 'Unnamed Advertiser';
                    const status = acc?.status || '-';
                    const currency = acc?.currency || '-';
                    return (
                      <label key={`${hash}-${idx}`} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                        <input
                          type="radio"
                          name="rtb-house-account"
                          value={hash}
                          checked={selectedRtbHouseAccountHash === hash}
                          onChange={() => setSelectedRtbHouseAccountHash(hash)}
                          className="mt-1"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{name}</div>
                          <div className="text-xs text-gray-500 break-all">hash: {hash}</div>
                          <div className="text-xs text-gray-500">status: {status} · currency: {currency}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    setRtbHouseStep(1);
                    setRtbHouseAccounts([]);
                    setSelectedRtbHouseAccountHash('');
                  }}
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
                  disabled={rtbHouseSaving}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleRtbHouseSave}
                  disabled={rtbHouseSaving || !selectedRtbHouseAccountHash || !rtbHouseToken.trim()}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
                >
                  {rtbHouseSaving ? 'Connecting…' : 'Connect'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Pinterest Ad Account Selection Dialog */}
      <Dialog
        open={isPinterestAccountDialogOpen}
        onClose={() => setIsPinterestAccountDialogOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img src="/pinterestlogopng.png" alt="Pinterest" className="h-6 w-auto" />
            <span>Select Pinterest Ad Account</span>
          </div>
        }
      >
        <div className="p-4 space-y-4">
          {pinterestAdAccounts.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500 mb-3">Loading ad accounts...</p>
              <Button onClick={loadPinterestAdAccounts} className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg">
                Load Accounts
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">Select the ad account you want to connect:</p>
              <div className="space-y-2">
                {pinterestAdAccounts.map((acc) => (
                  <label key={acc.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedPinterestAccount === acc.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="pinterest_account"
                      value={acc.id}
                      checked={selectedPinterestAccount === acc.id}
                      onChange={() => setSelectedPinterestAccount(acc.id)}
                      className="text-blue-600"
                    />
                    <div>
                      <div className="font-medium text-gray-800">{acc.name}</div>
                      <div className="text-xs text-gray-500">ID: {acc.id} &middot; {acc.currency} &middot; {acc.country}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={() => setIsPinterestAccountDialogOpen(false)} className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg">Cancel</Button>
                <Button
                  onClick={handlePinterestSelectAccount}
                  disabled={pinterestSaving || !selectedPinterestAccount}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-5 py-2 rounded-lg"
                >
                  {pinterestSaving ? 'Saving...' : 'Connect'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* BigQuery Dialog — 2-step: tokens → pick project → save */}
      <Dialog
        open={isBigQueryDialogOpen}
        onClose={() => { setIsBigQueryDialogOpen(false); setBqStep(1); setBqProjects([]); setBqSelectedProject(null); }}
        title={
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-blue-600" />
            <span>Connect BigQuery</span>
            {bqStep === 2 && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Select Project</span>}
          </div>
        }
      >
        <div className="p-4 space-y-4">
          {bqStep === 1 && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Refresh Token</label>
                  <button type="button" onClick={() => setBqShowTokens(!bqShowTokens)} className="text-xs text-gray-500 hover:text-gray-700">
                    {bqShowTokens ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input
                  type={bqShowTokens ? 'text' : 'password'}
                  value={bqForm.refreshToken}
                  onChange={e => setBqForm(p => ({ ...p, refreshToken: e.target.value }))}
                  placeholder="1//0abc..."
                  className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-sm font-mono px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type={bqShowTokens ? 'text' : 'password'}
                  value={bqForm.accessToken}
                  onChange={e => setBqForm(p => ({ ...p, accessToken: e.target.value }))}
                  placeholder="ya29.a0..."
                  className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-sm font-mono px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type={bqShowTokens ? 'text' : 'password'}
                    value={bqForm.clientId}
                    onChange={e => setBqForm(p => ({ ...p, clientId: e.target.value }))}
                    placeholder="123...apps.googleusercontent.com"
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-sm font-mono px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type={bqShowTokens ? 'text' : 'password'}
                    value={bqForm.clientSecret}
                    onChange={e => setBqForm(p => ({ ...p, clientSecret: e.target.value }))}
                    placeholder="GOCSPX-..."
                    className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-sm font-mono px-3 py-2"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">Playground'dan token aldıysanız, Playground'un veya kendi GCP projenizin Client bilgilerini girin.</p>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
                <p className="font-medium mb-1">How to get a token?</p>
                <ol className="list-decimal ml-4 space-y-0.5 text-blue-600">
                  <li>Go to <a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noopener noreferrer" className="underline">OAuth Playground</a></li>
                  <li>Select "BigQuery API v2" → choose bigquery scope</li>
                  <li>"Authorize APIs" → sign in with your Google account</li>
                  <li>"Exchange authorization code for tokens" → copy the Refresh Token</li>
                  <li>Enter the Client ID / Secret used when generating the token</li>
                </ol>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => { setIsBigQueryDialogOpen(false); setBqStep(1); }}
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleBqFetchProjects}
                  disabled={bqLoadingProjects || (!bqForm.refreshToken && !bqForm.accessToken)}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
                >
                  {bqLoadingProjects ? 'Loading projects…' : 'List Projects →'}
                </Button>
              </div>
            </>
          )}

          {bqStep === 2 && (
            <>
              <p className="text-sm text-gray-600">
                {bqProjects.length} project{bqProjects.length !== 1 ? 's' : ''} found. Select the project you want to connect:
              </p>

              <div className="max-h-64 overflow-y-auto space-y-1.5 border border-gray-200 rounded-lg p-2">
                {bqProjects.map(p => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      bqSelectedProject === p.id
                        ? 'bg-blue-50 border border-blue-300 ring-1 ring-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bqProject"
                      value={p.id}
                      checked={bqSelectedProject === p.id}
                      onChange={() => setBqSelectedProject(p.id)}
                      className="accent-blue-600"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 block truncate">{p.id}</span>
                      {p.name && p.name !== p.id && (
                        <span className="text-xs text-gray-500 block truncate">{p.name}</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex justify-between pt-2">
                <Button
                  type="button"
                  onClick={() => { setBqStep(1); setBqProjects([]); setBqSelectedProject(null); }}
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold px-4 py-2 rounded-lg text-sm"
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  onClick={handleBigQueryConnect}
                  disabled={bqSaving || !bqSelectedProject}
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
                >
                  {bqSaving ? 'Saving…' : `Save — ${bqSelectedProject || ''}`}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Meta Ads Dialog */}
      <Dialog
        open={isMetaDialogOpen}
        onClose={() => setIsMetaDialogOpen(false)}
        title={
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <img 
                src="/6033716.png" 
                alt="Meta Ads" 
                className="h-6 w-auto"
              />
              <span className="text-lg font-semibold">Connect Meta Ads</span>
              <a href="https://www.glowytics.com" target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-500 hover:text-blue-700" title="How to get Meta credentials?">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </a>
            </div>
            <span className="text-xs text-gray-500 mt-1 ml-8">Click the info icon to learn how to get your Meta App ID, App Secret, and Access Token.</span>
          </div>
        }
      >
        <form onSubmit={handleMetaFormSubmit} className="p-4 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
            <input
              type="text"
              name="appId"
              value={metaForm.appId}
              onChange={handleMetaFormChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="Enter your Meta App ID"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">App Secret</label>
            <input
              type="password"
              name="appSecret"
              value={metaForm.appSecret}
              onChange={handleMetaFormChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="Enter your Meta App Secret"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
            <input
              type="text"
              name="accessToken"
              value={metaForm.accessToken}
              onChange={handleMetaFormChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white placeholder-gray-400 text-base px-3 py-2"
              placeholder="Enter your Meta Access Token"
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              onClick={() => setIsMetaDialogOpen(false)}
              className="bg-gray-300 text-gray-800 hover:bg-gray-400 font-semibold px-5 py-2 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={metaLoading}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold px-5 py-2 rounded-lg"
            >
              {metaLoading ? (
                <div className="flex items-center gap-2">
                  <span>Connecting...</span>
                </div>
              ) : (
                'Connect'
              )}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Google Analytics Property Seçim Dialog'u */}
      <Dialog
        open={isAnalyticsSetupOpen}
        onClose={() => setIsAnalyticsSetupOpen(false)}
        title={
          <div className="flex items-center gap-3">
            <img 
              src="/GA4logoicon.svg" 
              alt="Google Analytics 4" 
              className="h-6 w-auto"
            />
            <span>Select Google Analytics Property</span>
          </div>
        }
      >
        <div className="p-4">
          {loadingGAAccounts ? (
            <div className="min-h-[200px] bg-gray-50">
              <div className="loading-logo-container" style={{ minHeight: '200px' }}>
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
            </div>
          ) : analyticsProperties.length > 0 ? (
            <div className="space-y-4">
              <Select
                placeholder="Select a property"
                value={selectedPropertyId}
                onChange={(value) => setSelectedPropertyId(value)}
              >
                {analyticsProperties.map(property => (
                  <Select.Option 
                    key={property.property_id} 
                    value={property.property_id}
                  >
                    {property.property_name} ({property.website_url || 'No URL'})
                  </Select.Option>
                ))}
              </Select>

              <div className="flex justify-end gap-2 mt-4">
                <Button onClick={() => setIsAnalyticsSetupOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePropertySelect}
                  disabled={!selectedPropertyId}
                  className="bg-blue-600 text-white"
                >
                  Connect
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No properties found</p>
          )}
        </div>
      </Dialog>

      {/* Loading Overlay */}
      {isLoadingOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <div className="loading-logo-container" style={{ minHeight: 'auto' }}>
              <img 
                src="/logoicon.png" 
                alt="Glowytics Logo" 
                className="loading-logo"
                style={{ width: '40px', height: '40px' }}
              />
            </div>
            <p className="text-gray-700 text-center mt-3">
              {setupType === 'search_console_account'
                ? 'Loading Search Console sites...'
                : setupType === 'merchant_center_account'
                ? 'Loading Merchant Center accounts...'
                : setupType === 'google_analytics_account'
                ? 'Loading Google Analytics accounts...'
                : setupType === 'meta_ads_account'
                ? 'Loading Meta Ads accounts...'
                : 'Loading Google Ads accounts...'}
            </p>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      {isDisconnectModalOpen && platformToDisconnect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Disconnect {platformToDisconnect.name}</h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to disconnect {platformToDisconnect.name}? This action will:
              </p>
              <ul className="space-y-2 ml-5 list-disc text-gray-700">
                <li>Remove your API connection</li>
                <li>Stop automatic data syncing</li>
                <li>Prevent access to {platformToDisconnect.name} analytics</li>
              </ul>
              <p className="mt-4 text-gray-600">
                You can reconnect at any time, but you'll need to re-authenticate.
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDisconnectModalOpen(false);
                  setPlatformToDisconnect(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={disconnectLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDisconnect}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={disconnectLoading}
              >
                {disconnectLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Disconnecting...</span>
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Criteo Account Picker */}
      {criteoPickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">Choose Criteo account</h2>
            <p className="text-sm text-gray-500 mb-4">
              These are the Criteo accounts your login granted access to. Pick the one that belongs to this brand.
            </p>

            {criteoPickerLoading ? (
              <div className="py-8 text-center text-gray-500">Loading accounts...</div>
            ) : criteoAdvertisers.length === 0 ? (
              <div className="py-6 text-center text-gray-500 text-sm">
                No Criteo account was granted to this connection. Reconnect and approve the correct account on Criteo's consent screen.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto mb-5">
                {criteoAdvertisers.map((adv) => (
                  <label
                    key={adv.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                      String(criteoChosenAdvertiser) === String(adv.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="criteo-advertiser"
                      value={adv.id}
                      checked={String(criteoChosenAdvertiser) === String(adv.id)}
                      onChange={() => setCriteoChosenAdvertiser(String(adv.id))}
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">{adv.name}</span>
                      <span className="block text-xs text-gray-500">ID: {adv.id}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCriteoPickerOpen(false)}
                disabled={criteoSaving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveCriteoAdvertiser}
                disabled={criteoSaving || criteoPickerLoading || !criteoChosenAdvertiser}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {criteoSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      <Modal
        title="Schedule Data Fetch"
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setScheduleModalVisible(false)}
            className="mr-2"
          >
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => scheduleForm.submit()}
            loading={savingSchedule}
            style={{ backgroundColor: '#1677ff' }}
          >
            Save Schedule
          </Button>
        ]}
      >
        <Spin spinning={loadingSchedule}>
          <Form form={scheduleForm} onFinish={saveSchedule} layout="vertical">
            <Form.Item
              name="frequency"
              label="Frequency"
              rules={[{ required: true, message: 'Please select frequency' }]}
            >
              <AntSelect>
                <AntSelect.Option value="daily">Daily</AntSelect.Option>
                <AntSelect.Option value="weekly">Weekly</AntSelect.Option>
                <AntSelect.Option value="monthly">Monthly</AntSelect.Option>
              </AntSelect>
            </Form.Item>
            
            <Form.Item
              name="time"
              label="Time"
              rules={[{ required: true, message: 'Please select time' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            
            <Form.Item
              name="active"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Invite User Modal */}
      <Dialog
        open={isInviteUserModalOpen}
        onClose={() => setIsInviteUserModalOpen(false)}
        title="Invite a New User"
      >
        <form onSubmit={handleInviteUser} className="p-4 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              User's Email
            </label>
            <input
              type="email"
              id="email"
              value={inviteUserForm.email}
              onChange={(e) => setInviteUserForm({...inviteUserForm, email: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="name@company.com"
              required
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="role"
              value={inviteUserForm.role}
              onChange={(e) => setInviteUserForm({...inviteUserForm, role: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="VIEWER">Viewer</option>
              <option value="ANALYST">Analyst</option>
              {MARKETER_BRAND_IDS.includes(selectedBrand?.id) && (
                <option value="MARKETER">Marketer</option>
              )}
              <option value="BRANDING">Branding</option>
              <option value="ADMIN">Admin</option>
              <option value="FINANCE">Finance (yalnızca faturalama)</option>
              <option value="GBP">GBP (yalnızca lokasyonlar)</option>
            </select>
            {inviteUserForm.role === 'FINANCE' && (
              <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800">
                <strong>Finance</strong> kısıtlı bir roldür: bu kullanıcı <strong>bu markada</strong>
                {' '}yalnızca <strong>Faturalama</strong> sayfasını görür. Kullanıcının diğer
                markalarındaki yetkisi değişmez.
              </p>
            )}
            {inviteUserForm.role === 'GBP' && (
              <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800">
                <strong>GBP</strong> kısıtlı bir roldür: bu kullanıcı <strong>bu markada</strong>
                {' '}yalnızca <strong>Google Business Profile → Lokasyonlar</strong> sayfasını görür
                (lokasyon listesi, puan/yorumlar, fotoğraflar ve <strong>yorum yanıtlama</strong>).
                Kullanıcının diğer markalarındaki yetkisi değişmez.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" onClick={() => setIsInviteUserModalOpen(false)} className="bg-gray-200 text-gray-800">
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 text-white" disabled={isSubmitting}>
              {isSubmitting ? 'Inviting...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Remove User Confirmation Modal */}
      {isRemoveUserModalOpen && userToModify && (
        <Dialog
          open={isRemoveUserModalOpen}
          onClose={() => setIsRemoveUserModalOpen(false)}
          title="Remove User"
        >
          <div className="p-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to remove <strong>{userToModify.email}</strong> from this brand? They will immediately lose all access.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" onClick={() => setIsRemoveUserModalOpen(false)} className="bg-gray-200 text-gray-800">
                Cancel
              </Button>
              <Button onClick={handleRemoveUser} className="bg-red-600 text-white" disabled={isSubmitting}>
                {isSubmitting ? 'Removing...' : 'Remove User'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────
// Glowytics Pixel — Embed Code Generator
// ─────────────────────────────────────────────

function GlowyticsPixelSection() {
  const { user: pixelUser } = useAuth();
  const brandId = localStorage.getItem('currentBrandId') || '';
  const brandName = localStorage.getItem('currentBrandName') || 'Your Brand';
  const [copied, setCopied] = React.useState(null);
  const [pixelStatus, setPixelStatus] = React.useState(null);
  const [pixelStats, setPixelStats] = React.useState(null);
  const [realtime, setRealtime] = React.useState(null);
  const [realtimeEnabled, setRealtimeEnabled] = React.useState(false);
  const [realtimeModalOpen, setRealtimeModalOpen] = React.useState(false);
  const [chatEnabled, setChatEnabled] = React.useState(false);
  const [chatLoading, setChatLoading] = React.useState(true);

  // Load chat widget config
  React.useEffect(() => {
    if (!brandId) return;
    fetch(`https://api.glowytics.com/api/pixel/config?brandId=${brandId}`)
      .then(r => r.json())
      .then(data => { setChatEnabled(data.chat === true); setChatLoading(false); })
      .catch(() => setChatLoading(false));
  }, [brandId]);

  const toggleChatWidget = async () => {
    const newValue = !chatEnabled;
    setChatEnabled(newValue);
    try {
      const res = await fetch('https://api.glowytics.com/api/pixel/config', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId, chat: newValue })
      });
      if (!res.ok) throw new Error('API error');
      toast.success(newValue ? 'Chat widget enabled' : 'Chat widget disabled');
    } catch (e) {
      setChatEnabled(!newValue); // rollback
      toast.error('Failed to update');
    }
  };

  // Realtime polling — every 30 seconds
  React.useEffect(() => {
    if (!realtimeEnabled || !brandId) return;
    let cancelled = false;

    const fetchRealtime = async () => {
      try {
        const res = await fetch(`https://api.glowytics.com/api/pixel/realtime?brandId=${brandId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('API error');
        const data = await res.json();
        if (!cancelled) setRealtime(data);
      } catch (e) {
        if (!cancelled) setRealtime(null);
      }
    };

    fetchRealtime(); // immediate
    const interval = setInterval(fetchRealtime, 30000); // every 30s

    return () => { cancelled = true; clearInterval(interval); };
  }, [realtimeEnabled, brandId]);

  const checkPixelConnection = async () => {
    setPixelStatus('checking');
    setPixelStats(null);
    try {
      const res = await fetch(`https://api.glowytics.com/api/pixel/stats?brandId=${brandId}&days=1`, { credentials: 'include' });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      const totalEvents = parseInt(data.total_events || 0);
      if (totalEvents > 0) {
        setPixelStatus('connected');
        setPixelStats({
          totalEvents,
          uniqueVisitors: parseInt(data.unique_visitors || 0),
          sessions: parseInt(data.total_sessions || 0),
          pageViews: parseInt(data.page_views || 0),
          purchases: parseInt(data.purchases || 0),
          lastEvent: data.last_event ? new Date(data.last_event).toLocaleString() : null,
          firstEvent: data.first_event ? new Date(data.first_event).toLocaleString() : null,
        });
      } else {
        setPixelStatus('no_data');
      }
    } catch (err) {
      setPixelStatus('error');
    }
  };

  const scriptCode = `<!-- Glowytics Pixel — ${brandName} -->\n<script>\n  window.glowyticsConfig = { brandId: '${brandId}' };\n</script>\n<script src="https://api.glowytics.com/pixel/glowytics-pixel.js" async></script>`;

  const gtmCode = `<script>\n  window.glowyticsConfig = { brandId: '${brandId}' };\n  (function() {\n    var s = document.createElement('script');\n    s.src = 'https://api.glowytics.com/pixel/glowytics-pixel.js';\n    s.async = true;\n    document.head.appendChild(s);\n  })();\n</script>`;

  const handleCopy = (type) => {
    const text = type === 'gtm' ? gtmCode : scriptCode;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      toast.success('Pixel code copied!');
      setTimeout(() => setCopied(null), 3000);
    }).catch(() => toast.error('Failed to copy'));
  };

  if (!brandId) return null;

  return (
    <div className="mt-10 pt-8 border-t border-gray-200">
      {/* Realtime Modal */}
      <RealtimeModal isOpen={realtimeModalOpen} onClose={() => setRealtimeModalOpen(false)} />

      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50">
          <Code className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Glowytics Pixel</h2>
          <p className="text-sm text-gray-500">Track visitors, events, and conversions on your website</p>
        </div>
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">Page Views & Sessions</p>
            <p className="text-[10px] text-gray-500">UTM parameters, referrer, session tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">GA4 Ecommerce Events</p>
            <p className="text-[10px] text-gray-500">Auto-captures dataLayer (purchase, add_to_cart...)</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Code className="w-4 h-4 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">Custom Events</p>
            <p className="text-[10px] text-gray-500">glowytics.track('event_name', data)</p>
          </div>
        </div>
      </div>

      {/* Option 1: Script Tag */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Option 1: Direct Script Tag</h3>
            <p className="text-xs text-gray-500 mt-0.5">Add before the closing <code className="bg-gray-200 px-1 rounded text-[10px]">&lt;/head&gt;</code> tag</p>
          </div>
          <button
            onClick={() => handleCopy('script')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              copied === 'script'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {copied === 'script' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'script' ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <pre className="p-4 bg-gray-900 text-green-400 text-xs leading-relaxed overflow-x-auto font-mono">
          <code>{scriptCode}</code>
        </pre>
      </div>

      {/* Option 2: GTM */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Option 2: Google Tag Manager (GTM)</h3>
            <p className="text-xs text-gray-500 mt-0.5">Custom HTML tag → Trigger: <strong>All Pages</strong></p>
          </div>
          <button
            onClick={() => handleCopy('gtm')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              copied === 'gtm'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {copied === 'gtm' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'gtm' ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <pre className="p-4 bg-gray-900 text-green-400 text-xs leading-relaxed overflow-x-auto font-mono">
          <code>{gtmCode}</code>
        </pre>
      </div>

      {/* ── Realtime Widget ── */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${realtimeEnabled && realtime && realtime.activeVisitors > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Realtime Visitors</h3>
              <p className="text-[10px] text-gray-500">Active users in the last 5 minutes · Updates every 30s</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRealtimeModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
              </span>
              Open Realtime Dashboard
            </button>
            <button
              onClick={() => setRealtimeEnabled(prev => !prev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                realtimeEnabled
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {realtimeEnabled ? (
                <><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div> Live</>
              ) : (
                'Quick View'
              )}
            </button>
          </div>
        </div>

        {realtimeEnabled && (
          <div className="p-4">
            {realtime ? (
              <>
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-4xl font-bold text-gray-900">{realtime.activeVisitors}</span>
                  <span className="text-sm text-gray-500 mb-1">
                    {realtime.activeVisitors === 1 ? 'active visitor' : 'active visitors'} right now
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 text-center">
                    <p className="text-[10px] text-blue-600">Sessions</p>
                    <p className="text-sm font-bold text-blue-800">{realtime.activeSessions}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100 text-center">
                    <p className="text-[10px] text-indigo-600">Page Views</p>
                    <p className="text-sm font-bold text-indigo-800">{realtime.pageViews5min}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-100 text-center">
                    <p className="text-[10px] text-amber-600">Product Views</p>
                    <p className="text-sm font-bold text-amber-800">{realtime.productViews5min}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-orange-50 border border-orange-100 text-center">
                    <p className="text-[10px] text-orange-600">Add to Carts</p>
                    <p className="text-sm font-bold text-orange-800">{realtime.addToCarts5min}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-green-50 border border-green-100 text-center">
                    <p className="text-[10px] text-green-600">Purchases</p>
                    <p className="text-sm font-bold text-green-800">{realtime.purchases5min}</p>
                  </div>
                </div>
                {/* Top Pages */}
                {realtime.topPages && realtime.topPages.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1.5">Active Pages</p>
                    <div className="space-y-1">
                      {realtime.topPages.slice(0, 5).map((page, i) => (
                        <div key={i} className="flex items-center justify-between py-1 px-2 rounded bg-gray-50 text-xs">
                          <span className="text-gray-700 truncate max-w-[70%]">{page.page_path}</span>
                          <span className="text-gray-500 flex-shrink-0">{page.visitors} {parseInt(page.visitors) === 1 ? 'visitor' : 'visitors'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-3">
                  Last updated: {new Date(realtime.timestamp).toLocaleTimeString()}
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Connecting to realtime data...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Chat Widget Toggle ── */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50">
              <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Chat Widget</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Show a chat assistant on your website for visitors
              </p>
              {['efe@heyglowy.com', 'efe@glowytics.com'].includes(pixelUser?.email) && (
                <a href="/cro/asistan" className="text-xs text-indigo-600 hover:underline mt-1 inline-block">Yeni AI asistan: /cro/asistan</a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-medium ${chatEnabled ? 'text-green-600' : 'text-gray-400'}`}>
              {chatLoading ? '...' : chatEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={toggleChatWidget}
              disabled={chatLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                chatEnabled ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                chatEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Check Connection ── */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Pixel Status</h3>
            <p className="text-xs text-gray-500 mt-0.5">Verify that event data is flowing from your website</p>
          </div>
          <button
            onClick={checkPixelConnection}
            disabled={pixelStatus === 'checking'}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              pixelStatus === 'checking'
                ? 'bg-gray-100 text-gray-400 border border-gray-200'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
            }`}
          >
            {pixelStatus === 'checking' ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <RefreshCw className="w-3.5 h-3.5" />
                Check Connection
              </>
            )}
          </button>
        </div>

        {/* Status Result */}
        {pixelStatus === 'connected' && pixelStats && (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm font-semibold text-green-700">Connected — Receiving Data</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-2.5 rounded-lg bg-green-50 border border-green-100">
                <p className="text-[10px] text-green-600 font-medium">Events (24h)</p>
                <p className="text-lg font-bold text-green-800">{pixelStats.totalEvents.toLocaleString()}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-[10px] text-blue-600 font-medium">Visitors</p>
                <p className="text-lg font-bold text-blue-800">{pixelStats.uniqueVisitors.toLocaleString()}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-purple-50 border border-purple-100">
                <p className="text-[10px] text-purple-600 font-medium">Sessions</p>
                <p className="text-lg font-bold text-purple-800">{pixelStats.sessions.toLocaleString()}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-[10px] text-amber-600 font-medium">Page Views</p>
                <p className="text-lg font-bold text-amber-800">{pixelStats.pageViews.toLocaleString()}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                <p className="text-[10px] text-emerald-600 font-medium">Purchases</p>
                <p className="text-lg font-bold text-emerald-800">{pixelStats.purchases.toLocaleString()}</p>
              </div>
            </div>
            {pixelStats.lastEvent && (
              <p className="text-[10px] text-gray-500 mt-2">Last event: {pixelStats.lastEvent}</p>
            )}
          </div>
        )}

        {pixelStatus === 'no_data' && (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
              <span className="text-sm font-semibold text-amber-700">Waiting for Data</span>
            </div>
            <p className="text-xs text-gray-600">
              No events received in the last 24 hours. Make sure the pixel code is installed on your website and visit a page to generate data.
            </p>
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-xs text-amber-800 font-medium mb-1">Troubleshooting:</p>
              <ul className="text-xs text-amber-700 space-y-1 list-disc pl-4">
                <li>Check that the pixel code is pasted in the <code className="bg-amber-100 px-1 rounded">&lt;head&gt;</code> section or GTM</li>
                <li>Make sure the <code className="bg-amber-100 px-1 rounded">brandId</code> matches: <code className="bg-amber-100 px-1 rounded text-[10px]">{brandId}</code></li>
                <li>Visit your website in a new tab and come back to check again</li>
                <li>Check browser console for errors (F12 → Console)</li>
              </ul>
            </div>
          </div>
        )}

        {pixelStatus === 'error' && (
          <div className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <span className="text-sm font-semibold text-red-700">Connection Error</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Could not check pixel status. Please try again.</p>
          </div>
        )}

        {pixelStatus === null && (
          <div className="p-4 text-center">
            <p className="text-xs text-gray-400">Click "Check Connection" to verify your pixel is working</p>
          </div>
        )}
      </div>

      {/* Brand ID info */}
      <div className="flex items-center gap-2.5 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
        <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <p className="text-xs text-indigo-800">
          <strong>Brand ID:</strong>{' '}
          <code className="bg-indigo-100 px-1.5 py-0.5 rounded text-[10px] select-all font-mono">{brandId}</code>
          <span className="ml-2 text-indigo-600">
            This pixel is unique to <strong>{brandName}</strong>. Each brand has its own isolated data.
          </span>
        </p>
      </div>

      {/* Details */}
      <details className="mt-4">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">What does the pixel capture?</summary>
        <div className="mt-2 p-4 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1.5">
          <p><strong>Auto:</strong> Page views, sessions (30 min timeout), UTM params, visitor ID (first-party cookie), referrer, screen size</p>
          <p><strong>GA4 dataLayer:</strong> view_item, add_to_cart, remove_from_cart, begin_checkout, purchase, view_item_list, search</p>
          <p><strong>Manual:</strong> <code className="bg-gray-200 px-1 rounded">glowytics.track('event', {'{'} data {'}'})</code></p>
          <p><strong>Sessions:</strong> 30 min timeout, midnight reset, UTM change = new session, max 4h</p>
        </div>
      </details>
    </div>
  );
} 